"use client";

import {
  type ChangeEvent,
  type CSSProperties,
  type DragEvent as ReactDragEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  memo,
  useMemo,
  useRef,
  useState,
} from "react";
import type { Path as FabricPath, StaticCanvas as FabricStaticCanvas } from "fabric";
import { ConfigProvider, Slider, theme } from "antd";
import { AlignCenter, AlignLeft, AlignRight, ChevronLeft, ChevronRight, Eraser, Hand, Loader2, Maximize, Minus, Package, Plus, Shirt, UserRound, X, ZoomIn, ZoomOut } from "lucide-react";
import { getSafeErrorMessage } from "../../lib/error-codes";
import {
  getEcomHelpTipDeviceId,
  getEcomHelpTipRedDotStorageKey,
} from "../../lib/ecom-help-tip-dismissal";
import { useSceneTutorialApi, type SceneTutorial } from "../../lib/scene-tutorial-api";
import { useShellState } from "@flowx/module-runtime/client";
import { loadImage } from "../../lib/image-edit-image-cache";
import type { Guide } from "../../lib/image-edit-guides";
import { GUIDE_MOVE_CURSOR, canvasToScreenCoordinate, guidePointerDeltaToCanvasDelta, screenToCanvasCoordinate, findNearbyGuide, drawGuides, shouldShowGuideMoveCursor } from "../../lib/image-edit-guides";
import { calculateRulerTicks, drawRuler, RULER_THICKNESS } from "../../lib/image-edit-rulers";
import { imageEditApi, type ImageEditSticker, type SegmentSelectionRegion } from "../../lib/image-edit-api";
import {
  deletePersonalFont,
  listPersonalFonts,
  listPlatformFonts,
  uploadPersonalFont,
  type PersonalFont,
  type PlatformFont,
} from "../../lib/image-layer-api";
import { getFontPickerSelectedLabel, sortFontsForPicker } from "../../lib/font-list-order";
import {
  getPersonalFontCssFamily,
  getPersonalFontFilesForFontFace,
  filterPersonalFontGroupsByRegisteredUuids,
  groupPersonalFonts,
  mergePersonalFontGroups,
  removeDeletedPersonalFontFiles,
} from "../../lib/image-edit-personal-font-groups";
import {
  createFontUploadRunSession,
  executeFontUploadBatches,
  MAX_PERSONAL_FONT_FILE_BYTES,
  planFontUploadBatches,
} from "../../lib/image-edit-font-upload-batch";
import {
  getFontFaceWeightDescriptor,
  parseFontMetadata,
  type FontMetadata,
} from "../../lib/image-edit-font-metadata";
import {
  COLOR_BALANCE_TONES,
  HUE_TRACK_GRADIENTS,
  LIGHTNESS_TRACK_GRADIENT,
  MAX_IMAGE_ADJUSTMENT,
  MIN_IMAGE_ADJUSTMENT,
  SATURATION_TRACK_GRADIENTS,
  areColorAdjustmentsDefault,
  cloneColorAdjustments,
  createDefaultColorAdjustments,
  normalizeColorAdjustments,
  type ColorAdjustments,
  type ColorBalanceAxis,
  type ColorBalanceTone,
  type HueAdjustmentKey,
  type HueChannel,
} from "../../lib/image-edit-adjustments";
import { paintExactSelectionBrushDab } from "../../lib/image-edit-selection-brush";
import {
  applyImageFrameStyleToCanvas,
  clampImageFrameControlValue,
  scaleImageFrameStyle,
  type ImageFramePreviewBackend,
  type ImageFrameStyle,
} from "../../lib/image-edit-stroke";
import {
  createStrokeStackPreviewLayout,
  getStrokeStackSourceScale,
  hitTestStackPreviewFrame,
  mapStackPointToFrame,
  resolveImageEditPreviewLayoutForTab,
  type ImageEditPreviewLayout,
  type StackPreviewLayout,
  type StrokeStackPreviewLayout,
} from "../../lib/image-edit-stack-preview";
import { drawStrokeStackPreviewDirect } from "../../lib/image-edit-stroke-compositor";
import { createPortal } from "react-dom";
import {
  DEFAULT_TEXT_LAYER_STYLE,
  TEXT_LAYER_CONTENT_PADDING,
  drawTextLayersToCanvas,
  getTextLayerCssStyle,
  measureTextLayerFittedHeight,
  resolveTextLetterSpacing,
  resolveTextLineHeight,
  type ImageEditTextLayer,
  type LayerEraseStroke,
  type TextLayerAlign,
  type TextLayerStyle,
} from "../../lib/image-edit-text-layer";
import {
  isCornerResizeHandle,
  resizeLayerFromHandle,
  rotationFromPointer,
  type LayerResizeHandle,
} from "../../lib/image-edit-layer-transform";
import { clampLayerPositionToCanvasViewport } from "../../lib/image-edit-layer-workspace";
import {
  HelpTipTrigger,
  normalizeHelpTipConfig,
} from "../ecom-image/help-tip/HelpTip";
import {
  getImageEditLayerZIndex,
  moveImageEditLayer,
  type ImageEditLayerOrderAction,
  type ImageEditLayerOrderEntry,
} from "../../lib/image-edit-layer-order";
import {
  ECOM_EYEDROPPER_ICON_PATH,
  EcomButton,
  EcomColorInput,
  EcomInputNumber,
  EcomSegmented,
  EcomSelect,
  EcomTextStyleToggle,
} from "../../ui";
import { getEcomOverlayClassName } from "../../ui/facade";
import { EcomPortalProvider, EcomPortalRoot, useEcomPortalController } from "../../ui/portal";
import { ECOM_LAYERS } from "../../ui/theme/layers";

type EditMode = "single" | "batch";
type EditScope = "full" | "selection";
type SelectionCombineMode = "replace" | "add" | "subtract";
type SelectionTool = "wand" | "brush";
type SelectionOutlineMode = "cleaned" | "exact";
type FabricModule = typeof import("fabric");
type SelectionShortcutMode = Extract<SelectionCombineMode, "add" | "subtract">;
type LocalImageImportSource = "files" | "folder" | "drop";

const IMAGE_EDITOR_TUTORIAL_SCENE_KEY = "image_editor_header";

export interface ImageEditItem {
  id: string;
  url: string;
  index: number;
  isLocal?: boolean;
  sourceFile?: File;
  remoteUrl?: string;
}

export interface ImageEditSaveResult {
  sourceIndex: number;
  sourceUrl: string;
  editedUrl: string;
}

interface ImageEditModalProps {
  open: boolean;
  mode: EditMode;
  images: ImageEditItem[];
  initialImageId?: string;
  taskId?: string;
  saveAsCopy?: boolean;
  /** AI 详情页复刻请求 stack；仅描边/圆角 tab 实际拼接全部屏。 */
  previewLayout?: ImageEditPreviewLayout;
  onClose: () => void;
  onSave: (results: ImageEditSaveResult[]) => void | Promise<void>;
}

interface ImageEditStickerLayer {
  id: string;
  imageId: string;
  stickerUuid: string;
  url: string;
  alt: string;
  x: number;
  y: number;
  width: number;
  height: number;
  coordinateWidth: number;
  coordinateHeight: number;
  rotation: number;
  eraseStrokes?: LayerEraseStroke[];
}

interface LayerEraserStrokeOperation {
  pointerId: number;
  kind: "text" | "sticker";
  layerId: string;
}

interface StickerLayerPointerOperation {
  pointerId: number;
  layerId: string;
  mode: "move" | "resize" | "rotate";
  handle?: LayerResizeHandle;
  startClientX: number;
  startClientY: number;
  startX: number;
  startY: number;
  startWidth: number;
  startHeight: number;
  startRotation: number;
  centerClientX: number;
  centerClientY: number;
  startPointerAngle: number;
}

type TextLayerPointerOperation = StickerLayerPointerOperation & {
  // 字体随框等比缩放需要以拖拽起点的样式与图层坐标系为基准，避免在每帧回调里依赖闭包中的陈旧 layer。
  startStyle: TextLayerStyle;
  startCoordinateWidth: number;
  startCoordinateHeight: number;
};

interface StrokePreviewFrameSource {
  id: string;
  canvas: HTMLCanvasElement;
  naturalWidth: number;
  naturalHeight: number;
  sourceScale: number;
}

const MAX_BATCH_COUNT = 20;
const STICKER_DRAG_MIME = "application/x-flowx-image-edit-sticker";
const MIN_STICKER_LAYER_SIZE = 24;
const MIN_TEXT_LAYER_SIZE = 24;
const LAYER_SELECTION_BORDER_COLOR = "#FFFFFF";
const LAYER_RESIZE_HANDLES: Array<{ handle: LayerResizeHandle; className: string; cursor: string }> = [
  { handle: "top-left", className: "-left-1.5 -top-1.5", cursor: "cursor-nwse-resize" },
  { handle: "top", className: "left-1/2 -top-1.5 -translate-x-1/2", cursor: "cursor-ns-resize" },
  { handle: "top-right", className: "-right-1.5 -top-1.5", cursor: "cursor-nesw-resize" },
  { handle: "right", className: "-right-1.5 top-1/2 -translate-y-1/2", cursor: "cursor-ew-resize" },
  { handle: "bottom-right", className: "-bottom-1.5 -right-1.5", cursor: "cursor-nwse-resize" },
  { handle: "bottom", className: "-bottom-1.5 left-1/2 -translate-x-1/2", cursor: "cursor-ns-resize" },
  { handle: "bottom-left", className: "-bottom-1.5 -left-1.5", cursor: "cursor-nesw-resize" },
  { handle: "left", className: "-left-1.5 top-1/2 -translate-y-1/2", cursor: "cursor-ew-resize" },
];
const MIN_ADJUSTMENT = MIN_IMAGE_ADJUSTMENT;
const MAX_ADJUSTMENT = MAX_IMAGE_ADJUSTMENT;
const MIN_CANVAS_ZOOM = 0.25;
const MAX_CANVAS_ZOOM = 4;
const CANVAS_ZOOM_STEP = 1.2;
const CANVAS_DRAG_THRESHOLD = 4;
const MIN_BRUSH_SIZE = 1;
const MAX_BRUSH_SIZE = 50;
const DEFAULT_BRUSH_SIZE = 10;
const DEFAULT_LAYER_ERASER_SIZE = 20;
const MIN_SELECTION_FEATHER_RADIUS = 0;
const MAX_SELECTION_FEATHER_RADIUS = 100;
const DEFAULT_SELECTION_FEATHER_RADIUS = 3;
const MAX_PREVIEW_EDGE = 1600;
const MAX_PREVIEW_PIXELS = 1_200_000;
const MAX_CANVAS_PIXEL_RATIO = 2;
const MAX_EDIT_HISTORY = 60;
const SELECTION_DASH_SEGMENT = 10;
const SELECTION_DASH_GAP = 4;
const SELECTION_DASH_PATTERN = SELECTION_DASH_SEGMENT + SELECTION_DASH_GAP;
const SELECTION_DASH_SPEED_PX_PER_MS = 0.008;
const SELECTION_MIN_OUTLINE_CONTOUR_PX = 18;
const SELECTION_DISPLAY_CLOSE_RADIUS = 10;
const SELECTION_OUTLINE_STROKE_WIDTH = 1;
const SELECTION_MASK_ALPHA_THRESHOLD = 12;
const SELECTION_OUTLINE_BASE_COLOR = "rgba(255,255,255,0.72)";
const SELECTION_OUTLINE_DASH_COLOR = "rgba(20,20,20,0.58)";
const SELECTION_OUTLINE_ALGORITHM_VERSION =
  `trace:${SELECTION_DISPLAY_CLOSE_RADIUS}:min:${SELECTION_MIN_OUTLINE_CONTOUR_PX}:threshold:${SELECTION_MASK_ALPHA_THRESHOLD}`;
const DEFAULT_ADJUSTMENTS = createDefaultColorAdjustments();
const FALLBACK_FONT_WEIGHTS = [100, 200, 300, 400, 500, 600, 700, 800, 900];

const FONT_WEIGHT_LABELS: Record<number, string> = {
  100: "Thin",
  200: "Extra Light",
  300: "Light",
  400: "Regular",
  500: "Medium",
  600: "Semi Bold",
  700: "Bold",
  800: "Extra Bold",
  900: "Black",
};

function getFontFaceSource(url: string): string {
  if (/\.woff2(\?|#|$)/i.test(url)) return `url("${url}") format("woff2")`;
  if (/\.woff(\?|#|$)/i.test(url)) return `url("${url}") format("woff")`;
  if (/\.otf(\?|#|$)/i.test(url)) return `url("${url}") format("opentype")`;
  if (/\.ttf(\?|#|$)/i.test(url)) return `url("${url}") format("truetype")`;
  return `url("${url}")`;
}

function getPlatformFontVariableRange(font: PlatformFont): [number, number] | null {
  const range = font.variableWeightRange ?? font.variable_weight_range;
  if (range && range.length === 2 && range[0] <= range[1]) return range;
  return null;
}

// 平台字体 → FontFace 注册清单：静态分字重字体逐文件注册，
// 可变字体按 variable_weight_range 注册单 face 让浏览器插值全部字重。
function getPlatformFontFaceEntries(font: PlatformFont): Array<{ url: string; weight?: string }> {
  if (font.files) {
    return Object.entries(font.files)
      .filter((entry): entry is [string, string] => Boolean(entry[1]))
      .map(([weight, url]) => ({ url, weight }));
  }
  if (!font.file_url) return [];
  const range = getPlatformFontVariableRange(font);
  return [{ url: font.file_url, weight: range ? `${range[0]} ${range[1]}` : undefined }];
}

function TextInsertionIcon() {
  return (
    <svg viewBox="0 0 1024 1024" className="h-6 w-6" fill="currentColor" aria-hidden="true">
      <path d="M438.954667 543.288889v-53.475556h54.461629V211.361185c-6.46637-19.304296-35.157333-59.107556-49.208889-70.049185-13.956741-10.808889-30.473481-18.962963-48.886518-24.803556-4.683852-1.479111-12.913778-2.711704-23.22963-3.413333a371.863704 371.863704 0 0 0-34.247111-0.568889l-4.152889 0.151704-2.503111-54.290963 5.176889-0.18963a423.61363 423.61363 0 0 1 39.442963 0.682667c14.298074 0.967111 26.320593 2.768593 35.991704 5.878519 24.082963 7.604148 46.288593 18.583704 65.744592 33.640296 12.496593 9.728 28.823704 28.937481 42.496 49.436444 14.089481-20.423111 31.061333-39.367111 45.24563-49.114074 23.191704-16.118519 44.316444-27.553185 64.474074-33.943704 9.765926-3.128889 21.807407-4.93037 36.124444-5.878518a423.442963 423.442963 0 0 1 39.367112-0.682667l5.195851 0.18963-2.522074 54.272-4.133926-0.132741a371.674074 371.674074 0 0 0-34.209185 0.549926c-10.315852 0.70163-18.545778 1.934222-23.286518 3.451259-14.241185 4.513185-30.90963 13.520593-50.100148 26.851556-15.113481 10.410667-41.206519 45.833481-49.170963 67.26163v279.191703h54.253037v53.475556h-54.253037v226.853926c7.964444 21.428148 34.057481 56.775111 49.303703 67.242666 19.057778 13.274074 35.65037 22.262519 50.100148 26.756741 4.664889 1.517037 12.875852 2.730667 23.172741 3.451259 11.131259 0.758519 23.22963 0.853333 34.228148 0.568889l4.096-0.151703 2.522074 54.290963-5.157926 0.189629c-12.572444 0.32237-26.263704 0.208593-39.424-0.701629-14.336-0.967111-26.377481-2.787556-35.858962-5.840593-20.347259-6.33363-41.528889-17.787259-64.606815-33.867852-14.260148-9.784889-31.288889-28.747852-45.378371-49.189926-13.653333 20.442074-29.961481 39.632593-42.496 49.436445-19.437037 15.094519-41.699556 26.093037-65.592889 33.583407-9.746963 3.147852-21.807407 4.949333-36.124444 5.935407a420.788148 420.788148 0 0 1-39.480889 0.682667c-4.058074-0.113778-4.058074-0.113778-5.138963-0.170667l2.503111-54.272 4.096 0.132741c10.998519 0.284444 23.134815 0.18963 34.285037-0.568889 10.296889-0.720593 18.526815-1.953185 23.381334-3.508148 18.261333-5.745778 34.778074-13.899852 48.64-24.651852 14.203259-11.131259 42.856296-50.858667 48.545185-70.390518l0.777481-225.829926h-54.461629z" />
    </svg>
  );
}

function LayerFrontIcon() {
  return (
    <svg viewBox="0 0 1024 1024" className="h-4 w-4" fill="currentColor" aria-hidden="true">
      <path d="M950.857143 950.857143v73.142857H658.285714v-73.142857h292.571429z m0-292.571429H658.285714v365.714286a73.142857 73.142857 0 0 1-72.777143-65.682286L585.142857 950.857143V658.285714a73.142857 73.142857 0 0 1 73.142857-73.142857h292.571429l7.460571 0.365714A73.142857 73.142857 0 0 1 1024 658.285714v292.571429l-0.365714 7.460571a73.142857 73.142857 0 0 1-65.316572 65.316572L950.857143 1024V658.285714zM365.714286 365.714286v73.142857H73.142857V365.714286h292.571429z m0-292.571429H73.142857v365.714286a73.142857 73.142857 0 0 1-72.777143-65.609143L0 365.714286V73.142857a73.142857 73.142857 0 0 1 73.142857-73.142857h292.571429l7.533714 0.365714A73.142857 73.142857 0 0 1 438.857143 73.142857v292.571429l-0.365714 7.533714a73.142857 73.142857 0 0 1-65.243429 65.243429L365.714286 438.857143V73.142857z" opacity=".6" />
      <path d="M731.428571 731.428571v73.142858H292.571429v-73.142858h438.857142z m0-438.857142H292.571429v512a73.142857 73.142857 0 0 1-73.142858-73.142858V292.571429a73.142857 73.142857 0 0 1 73.142858-73.142858h438.857142a73.142857 73.142857 0 0 1 73.142858 73.142858v438.857142a73.142857 73.142857 0 0 1-73.142858 73.142858V292.571429z" />
    </svg>
  );
}

function LayerBackIcon() {
  return (
    <svg viewBox="0 0 1024 1024" className="h-4 w-4" fill="currentColor" aria-hidden="true">
      <path d="M731.428571 731.428571v73.142858H292.571429v-73.142858h438.857142z m0-438.857142H292.571429v512a73.142857 73.142857 0 0 1-73.142858-73.142858V292.571429a73.142857 73.142857 0 0 1 73.142858-73.142858h438.857142a73.142857 73.142857 0 0 1 73.142858 73.142858v438.857142a73.142857 73.142857 0 0 1-73.142858 73.142858V292.571429z" opacity=".6" />
      <path d="M950.857143 950.857143v73.142857H658.285714v-73.142857h292.571429z m0-292.571429H658.285714v365.714286a73.142857 73.142857 0 0 1-72.777143-65.682286L585.142857 950.857143V658.285714a73.142857 73.142857 0 0 1 73.142857-73.142857h292.571429l7.460571 0.365714A73.142857 73.142857 0 0 1 1024 658.285714v292.571429l-0.365714 7.460571a73.142857 73.142857 0 0 1-65.316572 65.316572L950.857143 1024V658.285714zM365.714286 365.714286v73.142857H73.142857V365.714286h292.571429z m0-292.571429H73.142857v365.714286a73.142857 73.142857 0 0 1-72.777143-65.609143L0 365.714286V73.142857a73.142857 73.142857 0 0 1 73.142857-73.142857h292.571429l7.533714 0.365714A73.142857 73.142857 0 0 1 438.857143 73.142857v292.571429l-0.365714 7.533714a73.142857 73.142857 0 0 1-65.243429 65.243429L365.714286 438.857143V73.142857z" />
    </svg>
  );
}

function LayerForwardIcon() {
  return (
    <svg viewBox="0 0 1024 1024" className="h-4 w-4" fill="currentColor" aria-hidden="true">
      <path d="M950.857143 950.857143v73.142857H73.142857v-73.142857h877.714286z m0-146.285714H73.142857v219.428571a73.142857 73.142857 0 0 1-73.142857-73.142857v-146.285714a73.142857 73.142857 0 0 1 73.142857-73.142858h877.714286l7.460571 0.365715A73.142857 73.142857 0 0 1 1024 804.571429v146.285714l-0.365714 7.460571a73.142857 73.142857 0 0 1-65.316572 65.316572L950.857143 1024v-219.428571zM802.669714 392.923429L531.675429 80.749714a26.112 26.112 0 0 0-39.424 0L221.403429 392.923429a6.509714 6.509714 0 0 0 4.900571 10.825142h66.121143a13.165714 13.165714 0 0 0 9.874286-4.534857l178.688-205.897143v459.629715c0 3.657143 2.925714 6.582857 6.582857 6.582857h49.005714c3.510857 0 6.509714-2.925714 6.509714-6.582857V193.316571l178.688 205.897143c2.413714 2.925714 5.997714 4.534857 9.874286 4.534857h66.121143c5.558857 0 8.557714-6.582857 4.900571-10.752z" />
    </svg>
  );
}

function LayerBackwardIcon() {
  return (
    <svg viewBox="0 0 1024 1024" className="h-4 w-4" fill="currentColor" aria-hidden="true">
      <path d="M73.142857 73.142857V0h877.714286v73.142857H73.142857z m0 146.285714h877.714286V0a73.142857 73.142857 0 0 1 73.142857 73.142857v146.285714a73.142857 73.142857 0 0 1-73.142857 73.142858H73.142857l-7.533714-0.365715A73.142857 73.142857 0 0 1 0 219.428571V73.142857L0.365714 65.609143A73.142857 73.142857 0 0 1 65.609143 0.365714L73.142857 0v219.428571z" />
      <path d="M221.330286 631.003429l270.994285 312.32a26.112 26.112 0 0 0 39.424 0l270.848-312.32a6.582857 6.582857 0 0 0-4.900571-10.752h-66.121143a13.165714 13.165714 0 0 0-9.874286 4.534857l-178.688 205.897143V371.053714a6.582857 6.582857 0 0 0-6.582857-6.582857h-49.005714a6.582857 6.582857 0 0 0-6.509714 6.582857v459.629715L302.226286 624.786286a12.946286 12.946286 0 0 0-9.874286-4.534857h-66.121143a6.509714 6.509714 0 0 0-4.900571 10.752z" />
    </svg>
  );
}

function LayerOrderToolbar({
  rotation,
  onAction,
}: {
  rotation: number;
  onAction: (action: ImageEditLayerOrderAction) => void;
}) {
  const actions = [
    { action: "front" as const, label: "置于顶层", Icon: LayerFrontIcon },
    { action: "forward" as const, label: "上移一层", Icon: LayerForwardIcon },
    { action: "backward" as const, label: "下移一层", Icon: LayerBackwardIcon },
    { action: "back" as const, label: "置于底层", Icon: LayerBackIcon },
  ];
  return (
    <div
      data-image-edit-layer-order-toolbar="true"
      className="group/layer-order absolute left-[calc(100%+8px)] top-1/2 z-[80] w-9 -translate-y-1/2 overflow-hidden rounded bg-[#2B2B2D] py-1 text-white shadow-[0_6px_18px_rgba(0,0,0,0.42)] transition-[width] duration-150 hover:w-[132px] focus-within:w-[132px]"
      style={{ transform: `translateY(-50%) rotate(${-rotation}deg)` }}
      onPointerDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onPointerUp={(event) => event.stopPropagation()}
    >
      {actions.map(({ action, label, Icon }) => (
        <button
          key={action}
          type="button"
          aria-label={label}
          title={label}
          onClick={(event) => {
            event.stopPropagation();
            onAction(action);
          }}
          className="flex h-8 w-[132px] items-center text-left text-xs text-white/90 hover:bg-white/10 focus-visible:bg-white/10 focus-visible:outline-none"
        >
          <span className="flex h-8 w-9 shrink-0 items-center justify-center"><Icon /></span>
          <span className="whitespace-nowrap pr-3">{label}</span>
        </button>
      ))}
    </div>
  );
}

type AdjustmentKey = Exclude<keyof ColorAdjustments, "hueSaturation" | "colorBalance">;

interface AdjustmentControlConfig {
  key: AdjustmentKey;
  label: string;
  gradient?: string;
  displayScale?: number;
  displayPrecision?: number;
}

const NEUTRAL_ADJUSTMENT_GRADIENT = "linear-gradient(90deg,#5a5a5a 0%,#a5a5a5 50%,#dedede 100%)";
const OTHER_ADJUSTMENT_CONTROLS: AdjustmentControlConfig[] = [
  { key: "exposure", label: "曝光", gradient: NEUTRAL_ADJUSTMENT_GRADIENT, displayScale: 20, displayPrecision: 2 },
  { key: "contrast", label: "对比度", gradient: NEUTRAL_ADJUSTMENT_GRADIENT },
  { key: "highlights", label: "亮光", gradient: NEUTRAL_ADJUSTMENT_GRADIENT },
  { key: "shadows", label: "阴影", gradient: NEUTRAL_ADJUSTMENT_GRADIENT },
];

const HUE_CHANNEL_UI: Array<{ channel: HueChannel; label: string; color?: string }> = [
  { channel: "master", label: "全部" },
  { channel: "red", label: "红色", color: "#FF0000" },
  { channel: "yellow", label: "黄色", color: "#FFFF05" },
  { channel: "green", label: "绿色", color: "#03FF00" },
  { channel: "cyan", label: "青色", color: "#03FFFF" },
  { channel: "blue", label: "蓝色", color: "#0000FF" },
  { channel: "magenta", label: "洋红", color: "#FF00FF" },
];

const COLOR_BALANCE_TONE_LABELS: Record<ColorBalanceTone, string> = {
  shadows: "阴影",
  midtones: "中间调",
  highlights: "高光",
};

const COLOR_BALANCE_AXIS_UI: Array<{
  axis: ColorBalanceAxis;
  leftLabel: string;
  rightLabel: string;
  gradient: string;
}> = [
  { axis: "cyanRed", leftLabel: "青色", rightLabel: "红色", gradient: "linear-gradient(90deg,#00FFFF 0%,#FF0000 100%)" },
  { axis: "magentaGreen", leftLabel: "洋红", rightLabel: "绿色", gradient: "linear-gradient(90deg,#FF00FF 0%,#00FF00 100%)" },
  { axis: "yellowBlue", leftLabel: "黄色", rightLabel: "蓝色", gradient: "linear-gradient(90deg,#FFFF00 0%,#0000FF 100%)" },
];

/**
 * 色彩平衡「色调」选择器：自绘深色下拉面板，替代原生 <select>。
 * 选中态与悬停态统一使用 #4C4949（与其它生图下拉一致），避免原生 select 在
 * 不同系统下选中/悬停颜色错乱（如出现亮蓝选中 + 深蓝悬停的层级颠倒问题）。
 */
function ColorBalanceToneSelect({
  value,
  onChange,
}: {
  value: ColorBalanceTone;
  onChange: (value: ColorBalanceTone) => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelPosition, setPanelPosition] = useState<{ left: number; top: number; width: number } | null>(null);

  const updatePanelPosition = useCallback(() => {
    const button = buttonRef.current;
    if (!button) return;
    const rect = button.getBoundingClientRect();
    // 面板宽度与触发按钮一致，避免下拉选项比选择框窄。
    const panelWidth = rect.width;
    const panelMaxHeight = 132;
    const viewportPadding = 8;
    const gap = 4;
    const belowTop = rect.bottom + gap;
    const canOpenBelow = belowTop + panelMaxHeight <= window.innerHeight - viewportPadding;
    const aboveTop = rect.top - panelMaxHeight - gap;
    setPanelPosition({
      left: Math.max(viewportPadding, Math.min(rect.left, window.innerWidth - panelWidth - viewportPadding)),
      top: canOpenBelow || aboveTop < viewportPadding ? belowTop : aboveTop,
      width: panelWidth,
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!containerRef.current?.contains(target) && !panelRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    updatePanelPosition();
    window.addEventListener("resize", updatePanelPosition);
    window.addEventListener("scroll", updatePanelPosition, true);
    return () => {
      window.removeEventListener("resize", updatePanelPosition);
      window.removeEventListener("scroll", updatePanelPosition, true);
    };
  }, [open, updatePanelPosition]);

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      updatePanelPosition();
      setOpen((current) => !current);
    }
  };

  const panel = open ? (
    <div
      ref={panelRef}
      className="fixed z-[1200] overflow-y-auto rounded-[8px] bg-[#323234] p-2 shadow-[0_12px_30px_rgba(0,0,0,0.38)]"
      style={panelPosition ? { left: panelPosition.left, top: panelPosition.top, width: panelPosition.width } : undefined}
      role="listbox"
    >
      <div className="space-y-1">
        {COLOR_BALANCE_TONES.map((toneKey) => (
          <button
            key={toneKey}
            type="button"
            onClick={() => {
              onChange(toneKey);
              setOpen(false);
            }}
            className={`flex h-8 w-full items-center rounded-[6px] px-3 text-left text-[13px] text-[#F6F6F6] transition-colors ${
              toneKey === value ? "bg-[#4C4949]" : "hover:bg-[#4C4949]"
            }`}
            role="option"
            aria-selected={toneKey === value}
          >
            {COLOR_BALANCE_TONE_LABELS[toneKey]}
          </button>
        ))}
      </div>
    </div>
  ) : null;

  return (
    <div ref={containerRef} className="relative min-w-0 flex-1">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          updatePanelPosition();
          setOpen((current) => !current);
        }}
        onKeyDown={handleKeyDown}
        className="flex h-7 w-full items-center justify-between rounded-[4px] border border-[#656565] bg-[#121212] px-2 text-[13px] text-[#F6F6F6] outline-none transition-colors hover:border-[#3A3A3C] focus:border-white"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate">{COLOR_BALANCE_TONE_LABELS[value]}</span>
        <svg
          className={`h-2 w-3 shrink-0 text-[#F6F6F6] transition-transform ${open ? "rotate-180" : ""}`}
          viewBox="0 0 8 4"
          fill="none"
          aria-hidden="true"
        >
          <path d="M1 0.5L4 3.5L7 0.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {panel && typeof document !== "undefined" ? createPortal(panel, document.body) : null}
    </div>
  );
}

interface SmartSelectionIconProps {
  className?: string;
  size?: number;
  strokeWidth?: number;
}

type SmartSelectionIconComponent = (props: SmartSelectionIconProps) => ReactElement;

const SMART_SELECTION_BUTTONS: Array<{ region: SegmentSelectionRegion; label: string; Icon: SmartSelectionIconComponent }> = [
  { region: "skin", label: "扣皮肤", Icon: SmartSelectionSkinIcon },
  { region: "person", label: "扣人物", Icon: SmartSelectionPersonIcon },
  { region: "cloth", label: "扣衣服", Icon: SmartSelectionClothIcon },
  { region: "face", label: "扣人脸", Icon: SmartSelectionFaceIcon },
  { region: "commodity", label: "扣商品", Icon: SmartSelectionCommodityIcon },
];

const MAGIC_WAND_ICON_PATH =
  "M753.37 759.387c-8.916 0-17.3-3.47-23.605-9.771L544.078 563.929c-13.016-13.015-13.016-34.19 0-47.205 6.309-6.309 14.691-9.783 23.605-9.783s17.298 3.475 23.605 9.783l185.684 185.684c13.017 13.017 13.017 34.195 0 47.211C770.67 755.917 762.286 759.387 753.37 759.387zM329.522 499.909l-190.142-24.002 121.773-148.003-35.942-188.239 178.396 70.066 167.934-92.335-11.519 191.302 139.71 131.176-185.497 48.148-81.573 173.424L329.522 499.909zM425.838 552.021l50.498-107.391 114.873-29.822-86.511-81.233 7.133-118.449-103.99 57.197-110.456-43.415 22.246 116.571-75.382 91.637 117.726 14.879L425.838 552.021z";
const MAGIC_WAND_TOOL_ICON_PATHS = [
  "M819.2 401.1L652.8 298.7l17.1-192c4.2-76.8-81.1-119.5-140.8-72.6l-149.3 128-179.3-76.8C128 59.7 59.7 128 85.3 196.3l72.5 179.2-128 149.3c-51.2 59.7-4.3 145.1 72.5 140.8l192-17.1 102.4 166.4c38.4 64 136.5 51.2 153.6-25.6l46.9-187.7 187.7-46.9C866 541.9 883 443.8 819 401.1zM584.5 520.6c-29.9 8.5-55.5 29.9-64 64l-46.9 187.7-102.4-166.4c-17.1-25.6-46.9-42.7-81.1-38.4l-192 17.1 128-149.3c21.3-25.6 25.6-59.7 12.8-89.6l-72.5-179.3 179.2 72.5c29.9 12.8 64 8.5 89.6-12.8l149.3-128-17.1 192c-4.3 29.9 12.8 64 38.4 81.1l166.4 102.4-187.7 46.9z",
  "M682.7 686.9c17.1-17.1 42.7-17.1 59.7-4.3l170.7 157.9c17.1 17.1 17.1 42.7 4.3 59.7-17.1 17.1-42.7 17.1-59.7 4.3L687 746.6c-21.3-17.1-21.3-42.7-4.3-59.7z",
];
const AI_SMART_SELECTION_ICON_PATH =
  "M135.12704 72.33536h627.75296v62.79168H135.12704zM40.96 40.96m40.96 0l43.6224 0q40.96 0 40.96 40.96l0 43.6224q0 40.96-40.96 40.96l-43.6224 0q-40.96 0-40.96-40.96l0-43.6224q0-40.96 40.96-40.96ZM40.96 731.50464m40.96 0l43.6224 0q40.96 0 40.96 40.96l0 43.6224q0 40.96-40.96 40.96l-43.6224 0q-40.96 0-40.96-40.96l0-43.6224q0-40.96 40.96-40.96ZM731.50464 40.96m40.96 0l43.6224 0q40.96 0 40.96 40.96l0 43.6224q0 40.96-40.96 40.96l-43.6224 0q-40.96 0-40.96-40.96l0-43.6224q0-40.96 40.96-40.96ZM135.168 135.12704v627.75296H72.2944V135.168zM794.29632 480.41984a31.37536 31.37536 0 0 1-31.41632-31.41632V166.5024h62.79168v282.50112a31.37536 31.37536 0 0 1-31.37536 31.41632zM480.41984 794.29632a31.37536 31.37536 0 0 1-31.41632 31.37536H166.5024v-62.79168h282.50112c17.36704 0 31.41632 14.09024 31.41632 31.41632zM884.24448 835.66592l-17.2032 17.2032a12.0832 12.0832 0 0 1-17.2032 0l-208.40448-208.4864 34.4064-34.32448 208.40448 208.44544a12.12416 12.12416 0 0 1 0 17.2032zM811.29472 654.1312h-125.78816v125.78816a12.20608 12.20608 0 0 1-12.16512 12.12416h-24.24832a12.16512 12.16512 0 0 1-12.12416-12.12416l-0.12288-174.44864h174.4896a12.16512 12.16512 0 0 1 12.12416 12.12416v24.24832a12.24704 12.24704 0 0 1-12.16512 12.288z";
const PREVIOUS_STEP_ICON_PATH =
  "M500.6336 30.1056c5.3248-2.56 7.7824-2.56 13.1072-2.56 5.2736 0 10.3936 0 13.056 2.56 5.3248 2.6112 7.7824 5.3248 10.3936 7.7824l443.6992 417.6384a40.192 40.192 0 0 1 2.6112 54.784 40.192 40.192 0 0 1-54.7328 2.56l-378.4704-354.816V953.856a40.1408 40.1408 0 0 1-39.168 39.168 40.1408 40.1408 0 0 1-39.1168-39.168V158.0544l-375.9104 354.816a37.6832 37.6832 0 0 1-54.7328-2.56 37.4272 37.4272 0 0 1-10.3936-26.112c0-10.3936 5.3248-20.8896 13.1072-28.672L487.68 37.9904c2.56-2.6624 7.7824-5.12 12.9536-7.8848z";
const NEXT_STEP_ICON_PATH =
  "M523.3664 993.8944c-5.3248 2.56-7.7824 2.56-13.1072 2.56-5.2736 0-10.3936 0-13.056-2.56-5.3248-2.6112-7.7824-5.3248-10.3936-7.7824L43.1104 568.4736a40.192 40.192 0 0 1-2.6112-54.784 40.192 40.192 0 0 1 54.7328-2.56l378.4704 354.816V70.144c0-20.8896 18.2784-39.168 39.168-39.168 20.8896 0 39.1168 18.2784 39.1168 39.168v795.8016l375.9104-354.816a37.6832 37.6832 0 0 1 54.7328 2.56 37.4272 37.4272 0 0 1 10.3936 26.112 42.0864 42.0864 0 0 1-13.1072 28.672l-443.5968 417.536c-2.56 2.6624-7.7824 5.12-12.9536 7.8848z";
const FEATHER_SELECTION_ICON_PATH =
  "M1018.311 594.489c-2.844 11.378-11.378 17.067-19.911 14.222l-56.889-11.378c-8.533-2.844-17.067-11.377-14.222-22.755 2.844-19.911 5.689-42.667 5.689-65.422s-2.845-42.667-5.69-65.423c-2.844-11.377 5.69-19.91 14.223-22.755l54.045-11.378c8.533-2.844 19.91 5.689 19.91 14.222 5.69 34.134 8.534 59.734 8.534 88.178s-2.844 54.044-5.689 82.489zM927.29 213.333c5.689 8.534 2.844 19.911-5.689 25.6l-45.511 31.29c-8.533 5.688-19.911 2.844-25.6-5.69-25.6-36.977-54.045-65.422-91.022-91.022-8.534-5.689-11.378-17.067-5.69-25.6l31.29-45.511c5.689-8.533 17.066-11.378 25.6-5.689C856.177 128 896 167.822 927.289 213.333zM594.489 5.69c11.378 2.844 17.067 11.378 14.222 19.911l-11.378 56.889c-2.844 8.533-11.377 17.067-22.755 14.222-19.911-2.844-42.667-5.689-65.422-5.689s-42.667 2.845-65.423 5.69c-11.377 2.844-19.91-5.69-22.755-14.223l-5.69-54.045c-2.844-8.533 5.69-19.91 14.223-19.91C457.956 2.843 483.556 0 512 0s54.044 2.844 82.489 5.689zM213.333 96.71c8.534-5.689 19.911-2.844 25.6 5.689l31.29 45.511c5.688 8.533 2.844 19.911-5.69 25.6-36.977 25.6-65.422 54.045-91.022 91.022-5.689 5.69-17.067 8.534-25.6 2.845L102.4 238.933c-8.533-5.689-11.378-17.066-5.689-25.6C128 167.823 167.822 128 213.333 96.711zM28.444 415.29c-8.533-2.845-19.91 5.689-19.91 14.222C2.843 457.956 0 483.556 0 512s2.844 54.044 5.689 82.489c2.844 11.378 11.378 17.067 19.911 14.222l56.889-11.378c8.533-2.844 17.067-11.377 14.222-22.755-2.844-19.911-5.689-42.667-5.689-65.422s2.845-42.667 5.69-65.423c2.844-11.377-5.69-19.91-14.223-22.755l-54.045-5.69z m68.267 395.378c-5.689-8.534-2.844-19.911 5.689-25.6l45.511-31.29c8.533-5.688 19.911-2.844 25.6 5.69 25.6 36.977 54.045 65.422 91.022 91.022 8.534 5.689 11.378 17.067 5.69 25.6l-31.29 45.511c-5.689 5.689-17.066 11.378-25.6 5.689C167.823 896 128 856.178 96.711 810.667z m332.8 207.644c-11.378-2.844-17.067-11.378-14.222-19.911l11.378-54.044c2.844-8.534 11.377-17.067 22.755-14.223 19.911 2.845 42.667 5.69 65.422 5.69s42.667-2.845 65.423-5.69c11.377-2.844 19.91 5.69 22.755 14.223L614.4 998.4c2.844 8.533-5.689 19.911-14.222 19.911-34.134 2.845-59.734 5.689-88.178 5.689s-54.044-2.844-82.489-5.689z m381.156-91.022c-8.534 5.689-19.911 2.844-25.6-5.689l-31.29-45.511c-5.688-8.533-2.844-19.911 5.69-25.6 34.133-25.6 65.422-56.889 91.022-91.022 5.689-8.534 17.067-11.378 25.6-5.69l45.511 31.29c8.533 5.689 11.378 17.066 5.689 25.6C896 856.177 856.178 896 810.667 927.289zM512 250.31c-145.067 0-261.689 116.622-261.689 261.689S366.933 773.689 512 773.689 773.689 657.067 773.689 512 657.067 250.311 512 250.311z m0 432.356c-93.867 0-170.667-76.8-170.667-170.667S418.133 341.333 512 341.333 682.667 418.133 682.667 512 605.867 682.667 512 682.667z";
const SMART_SELECTION_FACE_ICON_PATHS = [
  "M508.69 830.28c-76.49 0-144.73-33.17-197.33-95.93-46.73-55.75-78.53-133.43-89.55-218.73-11.94-92.46 9.91-175.58 61.52-234.06 51.28-58.11 128.64-88.79 223.79-88.79h1.69c95.66 0.36 173.15 30.87 224.08 88.22 26.36 29.68 45.07 65.73 55.61 107.14 9.94 39.05 12.33 81.8 7.12 127.06-9.81 85.18-41.36 163.08-88.86 219.35-25.31 29.99-54.57 53.43-86.97 69.67-34.5 17.3-71.89 26.07-111.1 26.07z m-1.56-573.51c-76.26 0-137.03 23.2-175.81 67.14-39.23 44.45-55.57 109.62-46.03 183.52 16.64 128.85 92.17 258.85 223.41 258.85 133.02 0 209.02-133.96 223.35-258.42 8.64-75.04-8.05-140.52-47-184.38-39.05-43.97-98.42-66.42-176.47-66.72-0.49 0.01-0.97 0.01-1.45 0.01z",
  "M373.98 959.93h-97.55c-116.9 0-212-95.1-212-212v-43.26c0-17.67 14.33-32 32-32s32 14.33 32 32v43.26c0 81.61 66.39 148 148 148h97.55c17.67 0 32 14.33 32 32s-14.33 32-32 32zM746.72 959.93h-82.39c-17.67 0-32-14.33-32-32s14.33-32 32-32h82.39c81.61 0 148-66.39 148-148v-43.26c0-17.67 14.33-32 32-32s32 14.33 32 32v43.26c0 116.9-95.1 212-212 212zM926.72 352.78c-17.67 0-32-14.33-32-32v-43.14c0-81.61-66.39-148-148-148h-82.39c-17.67 0-32-14.33-32-32s14.33-32 32-32h82.39c116.9 0 212 95.1 212 212v43.14c0 17.67-14.33 32-32 32zM96.43 352.78c-17.67 0-32-14.33-32-32v-43.14c0-116.9 95.1-212 212-212h97.55c17.67 0 32 14.33 32 32s-14.33 32-32 32h-97.55c-81.61 0-148 66.39-148 148v43.14c0 17.67-14.33 32-32 32z",
  "M927.57 543.86H97.58c-17.67 0-32-14.33-32-32s14.33-32 32-32h829.99c17.67 0 32 14.33 32 32s-14.33 32-32 32z",
];
const SMART_SELECTION_SKIN_ICON_PATHS = [
  "M981.333333 384h-256c-25.6 0-42.666667-17.066667-42.666666-42.666667 0-93.866667-76.8-170.666667-170.666667-170.666666S341.333333 247.466667 341.333333 341.333333c0 25.6-17.066667 42.666667-42.666666 42.666667H42.666667c-25.6 0-42.666667-17.066667-42.666667-42.666667s17.066667-42.666667 42.666667-42.666666h217.6C281.6 179.2 384 85.333333 512 85.333333s230.4 93.866667 251.733333 213.333334H981.333333c25.6 0 42.666667 17.066667 42.666667 42.666666s-17.066667 42.666667-42.666667 42.666667zM981.333333 938.666667H42.666667c-25.6 0-42.666667-17.066667-42.666667-42.666667s17.066667-42.666667 42.666667-42.666667h938.666666c25.6 0 42.666667 17.066667 42.666667 42.666667s-17.066667 42.666667-42.666667 42.666667z",
  "M512 384m-64 0a64 64 0 1 0 128 0 64 64 0 1 0-128 0Z",
  "M725.333333 682.666667c-81.066667 0-132.266667-51.2-136.533333-55.466667 0 0-29.866667-29.866667-76.8-29.866667s-76.8 29.866667-76.8 29.866667c-4.266667 4.266667-55.466667 55.466667-136.533333 55.466667s-132.266667-51.2-136.533334-55.466667c-17.066667-17.066667-17.066667-42.666667 0-59.733333 17.066667-17.066667 42.666667-17.066667 59.733334 0 0 0 29.866667 29.866667 76.8 29.866666s76.8-29.866667 76.8-29.866666c4.266667-4.266667 55.466667-55.466667 136.533333-55.466667 81.066667 0 132.266667 51.2 136.533333 55.466667 0 0 29.866667 29.866667 76.8 29.866666s76.8-29.866667 76.8-29.866666c17.066667-17.066667 42.666667-17.066667 59.733334 0 17.066667 17.066667 17.066667 42.666667 0 59.733333-4.266667 4.266667-55.466667 55.466667-136.533334 55.466667z",
];

const MAGIC_WAND_CURSOR_SVG = [
  "<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 1024 1024'>",
  `<path d='${MAGIC_WAND_ICON_PATH}' fill='#272636'/>`,
  "</svg>",
].join("");
const MAGIC_WAND_CURSOR = `url("data:image/svg+xml,${encodeURIComponent(MAGIC_WAND_CURSOR_SVG)}") 10 10, crosshair`;
const EYE_DROPPER_CURSOR_SVG = [
  "<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 1024 1024'>",
  `<path d='${ECOM_EYEDROPPER_ICON_PATH}' fill='#e6e6e6' stroke='#111111' stroke-width='24' paint-order='stroke'/>`,
  "</svg>",
].join("");
const EYE_DROPPER_CURSOR = `url("data:image/svg+xml,${encodeURIComponent(EYE_DROPPER_CURSOR_SVG)}") 4 28, crosshair`;
const LAYER_ERASER_CURSOR_SVG = [
  "<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 24 24'>",
  "<path d='m7 21-4-4L14.5 5.5a2.12 2.12 0 0 1 3 0l1 1a2.12 2.12 0 0 1 0 3L7 21Z' fill='#f5f5f5' stroke='#111' stroke-width='1.6' stroke-linejoin='round'/>",
  "<path d='m10 18-4-4' fill='none' stroke='#111' stroke-width='1.6'/>",
  "</svg>",
].join("");
const LAYER_ERASER_CURSOR = `url("data:image/svg+xml,${encodeURIComponent(LAYER_ERASER_CURSOR_SVG)}") 4 28, auto`;
const SMUDGE_TOOL_ICON_PATH = "M0.068267 948.4288c0 22.869333 18.568533 41.437867 41.506133 41.437867h983.6544a41.437867 41.437867 0 1 0 0-82.807467H468.650667a309.248 309.248 0 0 0 18.6368-16.9984 216.951467 216.951467 0 0 0 62.532266-126.020267c5.188267-42.666667-4.096-89.156267-35.566933-120.763733-5.802667-5.666133-11.605333-11.605333-17.476267-17.6128-10.308267-10.513067-20.616533-20.957867-30.1056-30.037333a279.074133 279.074133 0 0 0-50.3808-39.799467 133.051733 133.051733 0 0 0-65.1264-19.319467c-24.439467-0.546133-48.674133 6.007467-73.386666 18.363734-62.327467 31.061333-130.6624 96.597333-130.6624 198.382933 0 38.434133-16.042667 72.4992-38.638934 99.396267-23.210667 27.579733-50.517333 44.2368-66.833066 49.220266a41.506133 41.506133 0 0 0-14.062934 7.5776 41.437867 41.437867 0 0 0-27.511466 38.980267z m349.320533-329.1136a49.834667 49.834667 0 0 1 24.917333 7.9872c10.103467 5.9392 21.230933 15.018667 35.0208 28.194133 8.738133 8.328533 17.2032 16.9984 26.692267 26.624 6.075733 6.144 12.424533 12.6976 19.524267 19.729067 7.918933 7.918933 15.1552 25.736533 11.8784 52.292267-4.027733 29.354667-17.681067 56.5248-38.775467 77.346133-35.908267 35.771733-80.008533 53.4528-140.9024 61.781333-33.1776 4.573867-69.632 6.144-110.933333 6.7584 29.832533-37.546667 53.248-87.6544 53.248-146.773333 0-59.5968 39.389867-101.649067 84.7872-124.245333 15.701333-7.918933 26.624-9.898667 34.542933-9.693867zM1037.858133 63.624533a100.829867 100.829867 0 0 0-136.874666-5.188266l-386.389334 330.820266c-49.834667 42.8032-44.6464 117.691733 0.136534 158.3104 17.749333 16.042667 34.542933 33.109333 50.312533 51.131734 39.458133 44.373333 111.4112 49.288533 154.146133 3.072l346.385067-373.896534c36.727467-39.594667 35.498667-101.1712-2.730667-139.332266l-24.9856-24.917334z m-82.807466 57.617067a17.749333 17.749333 0 0 1 24.1664 0.955733l24.917333 24.849067a17.749333 17.749333 0 0 1 0.546133 24.644267L658.295467 545.518933c-7.031467 7.645867-21.9136 8.738133-31.197867-1.774933-17.749333-20.206933-36.590933-39.389867-56.593067-57.344-11.810133-10.922667-9.898667-27.306667-1.774933-34.269867l386.389333-330.888533h-0.068266z";

function SmudgeToolIcon({ className }: { className?: string }) {
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 1092 1024" fill="currentColor">
      <path d={SMUDGE_TOOL_ICON_PATH} />
    </svg>
  );
}

function MagicWandIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      viewBox="0 0 1024 1024"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {MAGIC_WAND_TOOL_ICON_PATHS.map((path) => (
        <path key={path} d={path} fill="currentColor" />
      ))}
    </svg>
  );
}

function FeatherSelectionIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      width="20"
      height="20"
      viewBox="0 0 1024 1024"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d={FEATHER_SELECTION_ICON_PATH} fill="currentColor" />
    </svg>
  );
}

function AiSmartSelectionIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      viewBox="0 0 1064 1024"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d={AI_SMART_SELECTION_ICON_PATH} fill="currentColor" />
    </svg>
  );
}

function PreviousStepIcon({ className }: { className?: string }) {
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d={PREVIOUS_STEP_ICON_PATH} fill="currentColor" />
    </svg>
  );
}

function NextStepIcon({ className }: { className?: string }) {
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d={NEXT_STEP_ICON_PATH} fill="currentColor" />
    </svg>
  );
}

interface SidebarToolButtonProps {
  active?: boolean;
  disabled?: boolean;
  isPanelTrigger?: boolean;
  label: ReactNode;
  shortcut?: string;
  title: string;
  children: ReactNode;
  onClick: (event: ReactMouseEvent<HTMLButtonElement>) => void;
}

function SidebarToolButton({
  active = false,
  disabled = false,
  isPanelTrigger = false,
  label,
  shortcut,
  title,
  children,
  onClick,
}: SidebarToolButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-image-edit-tool-trigger={isPanelTrigger ? "true" : undefined}
      aria-label={title}
      title={title}
      className={`flex h-[52px] w-[56px] flex-col items-center justify-center gap-0.5 rounded-[8px] border transition outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-35 ${
        active
          ? "border-white/20 bg-[#262626] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]"
          : "border-transparent bg-transparent text-white/45 hover:border-white/12 hover:bg-white/[0.04] hover:text-white/80"
      }`}
    >
      {children}
      <span className="select-none whitespace-pre-line text-center text-[10px] leading-[11px] tracking-[0]">
        {label}
      </span>
      {shortcut && (
        <kbd className="select-none rounded-[3px] border border-white/15 bg-white/[0.06] px-1 text-[8.5px] font-medium leading-[11px] text-white/55">
          {shortcut}
        </kbd>
      )}
    </button>
  );
}

function SelectionAddIcon() {
  return (
    <svg aria-hidden="true" width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
      <line x1="1" y1="1.5" x2="15" y2="1.5" stroke="#F6F6F6" />
      <line x1="1" y1="14.5" x2="8" y2="14.5" stroke="#F6F6F6" />
      <line x1="9" y1="13.5" x2="16" y2="13.5" stroke="#F6F6F6" />
      <line x1="12.5" y1="10" x2="12.5" y2="17" stroke="#F6F6F6" />
      <line x1="1.5" y1="1" x2="1.5" y2="15" stroke="#F6F6F6" />
      <line x1="14.5" y1="1" x2="14.5" y2="8" stroke="#F6F6F6" />
      <rect width="3" height="3" rx="1" fill="#F6F6F6" />
      <rect y="13" width="3" height="3" rx="1" fill="#F6F6F6" />
      <rect x="13" width="3" height="3" rx="1" fill="#F6F6F6" />
    </svg>
  );
}

function SelectionSubtractIcon() {
  return (
    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <line x1="1" y1="1.5" x2="15" y2="1.5" stroke="#B3B3B3" />
      <line x1="1" y1="14.5" x2="8" y2="14.5" stroke="#B3B3B3" />
      <line x1="10" y1="12.5" x2="16" y2="12.5" stroke="#B3B3B3" />
      <line x1="1.5" y1="1" x2="1.5" y2="15" stroke="#B3B3B3" />
      <line x1="14.5" y1="1" x2="14.5" y2="8" stroke="#B3B3B3" />
      <rect width="3" height="3" rx="1" fill="#D9D9D9" />
      <rect y="13" width="3" height="3" rx="1" fill="#D9D9D9" />
      <rect x="13" width="3" height="3" rx="1" fill="#D9D9D9" />
    </svg>
  );
}

function SelectionClearIcon() {
  return (
    <svg aria-hidden="true" width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <line x1="1" y1="1.5" x2="15" y2="1.5" stroke="#B3B3B3" />
      <line x1="1" y1="14.5" x2="8" y2="14.5" stroke="#B3B3B3" />
      <line x1="1.5" y1="1" x2="1.5" y2="15" stroke="#B3B3B3" />
      <line x1="14.5" y1="1" x2="14.5" y2="8" stroke="#B3B3B3" />
      <rect width="3" height="3" rx="1" fill="#D9D9D9" />
      <rect y="13" width="3" height="3" rx="1" fill="#D9D9D9" />
      <rect x="13" width="3" height="3" rx="1" fill="#D9D9D9" />
      <g clipPath="url(#selection-clear-icon-clip)">
        <path
          d="M13 8.33325C13.2652 8.33325 13.5196 8.43861 13.7071 8.62615C13.8946 8.81368 14 9.06804 14 9.33325H15.1453C15.4477 9.33325 15.617 9.36259 15.7847 9.44542L15.8162 9.46175C15.9816 9.54964 16.117 9.685 16.2048 9.85042C16.3003 10.0288 16.3333 10.1999 16.3333 10.5213V11.3333C16.3334 11.4167 16.3021 11.497 16.2458 11.5585C16.1894 11.62 16.1121 11.6582 16.029 11.6654L16.1517 13.1394C16.2587 14.4238 15.3043 15.5516 14.0202 15.6586C13.9557 15.6639 13.891 15.6666 13.8263 15.6666H10.7247C10.5863 15.6666 10.4495 15.6379 10.3228 15.5824C10.1962 15.5268 10.0824 15.4455 9.98873 15.3438C9.89506 15.242 9.82352 15.1219 9.77864 14.991C9.73377 14.8602 9.71652 14.7214 9.72801 14.5836L9.97101 11.6653C9.88795 11.658 9.81062 11.6199 9.75429 11.5584C9.69795 11.497 9.66669 11.4166 9.66667 11.3333V10.5213C9.66667 10.1999 9.69967 10.0288 9.79501 9.85042C9.88294 9.68497 10.0184 9.54961 10.1838 9.46175C10.3622 9.36625 10.5333 9.33325 10.8547 9.33325H12C12 9.06804 12.1054 8.81368 12.2929 8.62615C12.4804 8.43861 12.7348 8.33325 13 8.33325ZM15.3598 11.6666H10.64L10.3915 14.6528L10.3912 14.6666C10.3912 14.755 10.4263 14.8398 10.4888 14.9023C10.5513 14.9648 10.6361 14.9999 10.7245 14.9999H11.6033L11.6673 13.9791C11.6726 13.8952 11.7094 13.8163 11.7703 13.7584C11.8312 13.7004 11.9118 13.6676 11.9958 13.6666L12.0208 13.6673C12.1048 13.6725 12.1836 13.7093 12.2415 13.7702C12.2995 13.8311 12.3323 13.9117 12.3333 13.9958L12.3327 14.0208L12.2717 14.9999H13.8267C13.8574 14.9999 13.8881 14.9991 13.9188 14.9974L13.965 14.9941C14.8823 14.9178 15.564 14.1121 15.4875 13.1949L15.3598 11.6666ZM13 8.99992C12.9116 8.99992 12.8268 9.03504 12.7643 9.09755C12.7018 9.16006 12.6667 9.24485 12.6667 9.33325V9.66659C12.6667 9.75499 12.6316 9.83978 12.569 9.90229C12.5065 9.9648 12.4217 9.99992 12.3333 9.99992H10.815L10.7457 10.0014L10.688 10.0041C10.6093 10.0091 10.564 10.0196 10.5242 10.0369L10.4983 10.0496C10.4477 10.0766 10.41 10.1143 10.383 10.1649L10.3703 10.1908C10.353 10.2306 10.3427 10.2759 10.3375 10.3546L10.3348 10.4123L10.3333 10.4816V10.9999H15.6667V10.5213L15.6662 10.4496L15.6645 10.3894L15.6613 10.3388C15.6565 10.2769 15.648 10.2368 15.6347 10.2026L15.6237 10.1776L15.617 10.1649C15.5912 10.1156 15.551 10.0754 15.5017 10.0496L15.4758 10.0369C15.436 10.0196 15.3907 10.0093 15.312 10.0041L15.2543 10.0014L15.185 9.99992H13.6667C13.5783 9.99992 13.4935 9.9648 13.431 9.90229C13.3685 9.83978 13.3333 9.75499 13.3333 9.66659V9.33325C13.3333 9.24485 13.2982 9.16006 13.2357 9.09755C13.1732 9.03504 13.0884 8.99992 13 8.99992Z"
          fill="#B3B3B3"
        />
      </g>
      <defs>
        <clipPath id="selection-clear-icon-clip">
          <rect width="8" height="8" fill="white" transform="translate(9 8)" />
        </clipPath>
      </defs>
    </svg>
  );
}

function SmartSelectionSkinIcon({ className, size = 18 }: SmartSelectionIconProps) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      viewBox="0 0 1024 1024"
      width={size}
      height={size}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {SMART_SELECTION_SKIN_ICON_PATHS.map((path) => (
        <path key={path.slice(0, 40)} d={path} fill="currentColor" />
      ))}
    </svg>
  );
}

function SmartSelectionFaceIcon({ className, size = 18 }: SmartSelectionIconProps) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      viewBox="0 0 1024 1024"
      width={size}
      height={size}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {SMART_SELECTION_FACE_ICON_PATHS.map((path) => (
        <path key={path.slice(0, 40)} d={path} fill="currentColor" />
      ))}
    </svg>
  );
}

function SmartSelectionPersonIcon({ className, size = 18, strokeWidth = 1.65 }: SmartSelectionIconProps) {
  return <UserRound className={className} size={size} strokeWidth={strokeWidth} />;
}

function SmartSelectionClothIcon({ className, size = 18, strokeWidth = 1.65 }: SmartSelectionIconProps) {
  return <Shirt className={className} size={size} strokeWidth={strokeWidth} />;
}

function SmartSelectionCommodityIcon({ className, size = 18, strokeWidth = 1.65 }: SmartSelectionIconProps) {
  return <Package className={className} size={size} strokeWidth={strokeWidth} />;
}

function getSelectionShortcutMode(event: { altKey: boolean; shiftKey: boolean }): SelectionShortcutMode | null {
  if (event.altKey) return "subtract";
  if (event.shiftKey) return "add";
  return null;
}

function normalizeSelectionCombineMode(mode: SelectionCombineMode): SelectionShortcutMode {
  return mode === "subtract" ? "subtract" : "add";
}

interface SelectionMask {
  width: number;
  height: number;
  data: Uint8ClampedArray;
  outlineMode?: SelectionOutlineMode;
  version?: number;
}

interface CanvasTransform {
  scale: number;
  offsetX: number;
  offsetY: number;
}

interface CanvasViewportSize {
  width: number;
  height: number;
}

interface EyeDropperPreview {
  x: number;
  y: number;
  sampleX: number;
  sampleY: number;
  color: string;
  rgb: string;
}

interface WorkerSelectionMask {
  width: number;
  height: number;
  data: Uint8ClampedArray;
}

interface WorkerProcessMessage {
  type: "process";
  taskId: string;
  imageData: ImageData;
  adjustments: ColorAdjustments;
  mask?: WorkerSelectionMask;
  /** Real-time preview prefers local WebGL2 and falls back to the worker LUT path. */
  fastPreview?: boolean;
  /** Marks an exact preview task so it can be cancelled without touching export work. */
  previewTask?: boolean;
}

interface WorkerCancelMessage {
  type: "cancel";
  taskId?: string;
}

interface WorkerDoneMessage {
  type: "done";
  taskId: string;
  imageData: ImageData;
}

interface WorkerErrorMessage {
  type: "error";
  taskId: string;
  code: "E8101";
}

interface WorkerCancelledMessage {
  type: "cancelled";
  taskId: string;
  code: "E8101";
}

type WorkerResponseMessage = WorkerDoneMessage | WorkerErrorMessage | WorkerCancelledMessage;

interface PendingWorkerTask {
  resolve: (imageData: ImageData) => void;
  reject: (error: Error) => void;
}

interface PreviewProcessRequest {
  imageId: string;
  sourceImageData: ImageData;
  targetPreviewBuffer: HTMLCanvasElement;
  adjustments: ColorAdjustments;
  selectionMask?: SelectionMask;
  viewport: CanvasViewportSize;
  coordinateWidth: number;
  coordinateHeight: number;
  quality: PreviewQuality;
}

// "draft" controls scheduling only (slider changes start immediately). Both
// draft and full requests use the same full-resolution preview pixels.
type PreviewQuality = "draft" | "full";

interface BakedEditOperation {
  adjustments: ColorAdjustments;
  selectionMask?: SelectionMask;
  selectionMaskIsFeathered?: boolean;
}

interface ImageDimensions {
  width: number;
  height: number;
}

interface ExportProgress {
  current: number;
  total: number;
}

interface AdjustmentControlProps {
  label: ReactNode;
  value: number;
  gradient?: string;
  displayScale?: number;
  displayPrecision?: number;
  endLabels?: { left: string; right: string };
  thumbColor?: string;
  onChange: (nextValue: number) => void;
  onInteractionEnd: () => void;
}

interface EditHistorySnapshot {
  currentId: string;
  editScope: EditScope;
  selectionCombineMode: SelectionCombineMode;
  selectionTool: SelectionTool | null;
  wandTolerance: number;
  brushSize: number;
  adjustmentsByImageId: Record<string, ColorAdjustments>;
  selectionMasksByImageId: Record<string, SelectionMask>;
  /** Preview pixels that have been baked by Ctrl+D. */
  bakedPreviewImageDataByImageId: Record<string, ImageData>;
  bakedEditOperationsByImageId: Record<string, BakedEditOperation[]>;
  textLayers: ImageEditTextLayer[];
  stickerLayers: ImageEditStickerLayer[];
}

interface SelectionOutlinePathCache {
  mask: SelectionMask;
  maskVersion: number;
  displayVersion: string;
  targetWidth: number;
  targetHeight: number;
  path: string;
}

interface SmartSelectionCacheEntry {
  imageId: string;
  imageUrl: string;
  region: SegmentSelectionRegion;
  width: number;
  height: number;
  mask: SelectionMask;
}

interface WorkerMaskCache {
  mask: SelectionMask;
  maskVersion: number;
  radius: number;
  width: number;
  height: number;
  data: Uint8ClampedArray;
}

interface BrushStrokeState {
  active: boolean;
  lastX: number;
  lastY: number;
  mask: SelectionMask;
  previewPathData: string;
  previewLineWidth: number;
  erase: boolean;
}

interface SelectionOverlayFabricState {
  fabric: FabricModule;
  canvas: FabricStaticCanvas;
  width: number;
  height: number;
  outlinePathData: string | null;
  outlineBasePath: FabricPath | null;
  outlineDashPath: FabricPath | null;
  brushPathData: string | null;
  brushPath: FabricPath | null;
}

function toThumbnailUrl(url: string): string {
  if (!url.includes("/api/v1/public/storage/file/")) return url;
  if (/[?&]size=/.test(url)) return url;
  return url + (url.includes("?") ? "&" : "?") + "size=small";
}

const LOCAL_IMAGE_FILE_PATTERN = /\.(avif|bmp|gif|heic|heif|jpe?g|png|tiff?|webp)$/i;

function getLocalImageFilePath(file: File) {
  return (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
}

function isLocalImageFile(file: File) {
  return file.type.startsWith("image/") || LOCAL_IMAGE_FILE_PATTERN.test(file.name);
}

function sortLocalImageFiles(files: File[]) {
  return [...files].sort((a, b) => getLocalImageFilePath(a).localeCompare(getLocalImageFilePath(b), "zh-Hans-CN"));
}

function dedupeLocalImageFiles(files: File[]) {
  const seen = new Set<string>();
  return files.filter((file) => {
    const key = `${getLocalImageFilePath(file)}:${file.size}:${file.lastModified}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function isFileSystemFileEntry(entry: FileSystemEntry): entry is FileSystemFileEntry {
  return entry.isFile;
}

function isFileSystemDirectoryEntry(entry: FileSystemEntry): entry is FileSystemDirectoryEntry {
  return entry.isDirectory;
}

function readFileSystemEntryFile(entry: FileSystemFileEntry) {
  return new Promise<File | null>((resolve) => {
    entry.file(resolve, () => resolve(null));
  });
}

function readFileSystemDirectoryEntries(reader: FileSystemDirectoryReader) {
  return new Promise<FileSystemEntry[]>((resolve) => {
    reader.readEntries(resolve, () => resolve([]));
  });
}

async function readFilesFromFileSystemEntry(entry: FileSystemEntry): Promise<File[]> {
  if (isFileSystemFileEntry(entry)) {
    const file = await readFileSystemEntryFile(entry);
    return file ? [file] : [];
  }
  if (!isFileSystemDirectoryEntry(entry)) return [];

  const reader = entry.createReader();
  const entries: FileSystemEntry[] = [];
  for (;;) {
    const batch = await readFileSystemDirectoryEntries(reader);
    if (batch.length === 0) break;
    entries.push(...batch);
  }

  const nestedFiles = await Promise.all(entries.map((nestedEntry) => readFilesFromFileSystemEntry(nestedEntry)));
  return nestedFiles.flat();
}

async function getFilesFromDataTransfer(dataTransfer: DataTransfer) {
  const items = Array.from(dataTransfer.items || []);
  const entries = items
    .map((item) => item.webkitGetAsEntry?.() ?? null)
    .filter((entry): entry is FileSystemEntry => entry !== null);

  if (entries.length > 0) {
    const nestedFiles = await Promise.all(entries.map((entry) => readFilesFromFileSystemEntry(entry)));
    return nestedFiles.flat();
  }

  return Array.from(dataTransfer.files || []);
}

function clampAdjustment(value: number) {
  if (!Number.isFinite(value)) return 0;
  return Math.min(MAX_ADJUSTMENT, Math.max(MIN_ADJUSTMENT, Math.round(value)));
}

function normalizeAdjustments(adjustments?: Partial<ColorAdjustments>): ColorAdjustments {
  return normalizeColorAdjustments(adjustments);
}

function areAdjustmentsDefault(adjustments: ColorAdjustments) {
  return areColorAdjustmentsDefault(adjustments);
}

function adjustmentValueToPercent(value: number) {
  return ((clampAdjustment(value) - MIN_ADJUSTMENT) / (MAX_ADJUSTMENT - MIN_ADJUSTMENT)) * 100;
}

function clearNativeTextSelection() {
  if (typeof window === "undefined") return;
  const selection = window.getSelection?.();
  if (selection && !selection.isCollapsed) {
    selection.removeAllRanges();
  }
}

function formatAdjustmentDisplayValue(value: number, displayScale = 1, displayPrecision = 0) {
  const displayValue = clampAdjustment(value) / displayScale;
  return displayPrecision > 0 ? displayValue.toFixed(displayPrecision) : String(Math.round(displayValue));
}

function parseAdjustmentDisplayValue(value: string, displayScale = 1) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  return clampAdjustment(parsed * displayScale);
}

function safeImageEditErrorText(err: unknown, fallback: string): string {
  if (err instanceof Error) {
    const match = err.message.match(/E\d{4}\s+[^\n\r]+/);
    if (match) return match[0];
  }
  return fallback;
}

function clampTolerance(value: number) {
  if (!Number.isFinite(value)) return 28;
  return Math.min(100, Math.max(1, Math.round(value)));
}

function clampBrushSize(value: number) {
  if (!Number.isFinite(value)) return DEFAULT_BRUSH_SIZE;
  return Math.min(MAX_BRUSH_SIZE, Math.max(MIN_BRUSH_SIZE, Math.round(value)));
}

function clampSelectionFeatherRadius(value: number) {
  if (!Number.isFinite(value)) return DEFAULT_SELECTION_FEATHER_RADIUS;
  return Math.min(MAX_SELECTION_FEATHER_RADIUS, Math.max(MIN_SELECTION_FEATHER_RADIUS, Math.round(value)));
}

interface SelectionValueInputProps {
  "aria-label": string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}

function SelectionValueInput({
  "aria-label": ariaLabel,
  value,
  min,
  max,
  onChange,
}: SelectionValueInputProps) {
  const [draftValue, setDraftValue] = useState(String(value));
  const inputFocusedRef = useRef(false);

  useEffect(() => {
    if (!inputFocusedRef.current) setDraftValue(String(value));
  }, [value]);

  const commitDraftValue = useCallback(() => {
    const normalized = draftValue.trim();
    const parsed = normalized === "" ? min : Number(normalized);
    const nextValue = Number.isFinite(parsed)
      ? Math.min(max, Math.max(min, Math.round(parsed)))
      : value;
    onChange(nextValue);
    setDraftValue(String(nextValue));
  }, [draftValue, max, min, onChange, value]);

  return (
    <input
      type="text"
      inputMode="numeric"
      value={draftValue}
      aria-label={ariaLabel}
      onFocus={() => {
        inputFocusedRef.current = true;
        if (value === 0) setDraftValue("");
      }}
      onChange={(event) => {
        const nextDraft = event.target.value;
        if (!/^\d*$/.test(nextDraft)) return;
        setDraftValue(nextDraft);
        if (nextDraft === "") return;
        const parsed = Number(nextDraft);
        if (!Number.isFinite(parsed)) return;
        onChange(Math.min(max, Math.max(min, Math.round(parsed))));
      }}
      onBlur={() => {
        inputFocusedRef.current = false;
        commitDraftValue();
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.currentTarget.blur();
      }}
      className="ml-2 h-[22px] w-8 shrink-0 rounded-[4px] border-0 bg-[#494949]/60 px-1 text-center text-[11px] font-medium leading-none tabular-nums text-white outline-none focus:ring-1 focus:ring-white/30"
    />
  );
}

function isSelectedMaskValue(value: number) {
  return value > SELECTION_MASK_ALPHA_THRESHOLD;
}

function hasSelectedMaskPixels(mask: SelectionMask | undefined) {
  if (!mask) return false;
  for (let index = 0; index < mask.data.length; index += 1) {
    if (isSelectedMaskValue(mask.data[index])) return true;
  }
  return false;
}

function getSelectionOutlineMode(mask: SelectionMask): SelectionOutlineMode {
  return mask.outlineMode ?? "cleaned";
}

function getSelectionOutlineDisplayVersion(mask: SelectionMask) {
  return `${SELECTION_OUTLINE_ALGORITHM_VERSION}:mode:${getSelectionOutlineMode(mask)}`;
}

function clampZoom(value: number, minimumScale = MIN_CANVAS_ZOOM) {
  if (!Number.isFinite(value)) return 1;
  return Math.min(MAX_CANVAS_ZOOM, Math.max(minimumScale, value));
}

function getCanvasPixelRatio() {
  if (typeof window === "undefined") return 1;
  return Math.min(MAX_CANVAS_PIXEL_RATIO, Math.max(1, window.devicePixelRatio || 1));
}

function getPreviewScale(width: number, height: number) {
  if (width <= 0 || height <= 0) return 1;
  const edgeScale = Math.min(MAX_PREVIEW_EDGE / width, MAX_PREVIEW_EDGE / height, 1);
  const pixelScale = Math.min(Math.sqrt(MAX_PREVIEW_PIXELS / (width * height)), 1);
  return Math.min(edgeScale, pixelScale);
}

function scaleSelectionMaskToFrame(mask: SelectionMask, targetWidth: number, targetHeight: number): SelectionMask {
  if (mask.width === targetWidth && mask.height === targetHeight) {
    return cloneSelectionMask(mask);
  }
  if (targetWidth <= 0 || targetHeight <= 0) {
    return createEmptySelectionMask(Math.max(1, targetWidth), Math.max(1, targetHeight));
  }

  const next = createEmptySelectionMask(targetWidth, targetHeight);
  next.outlineMode = mask.outlineMode;
  next.version = mask.version;
  for (let y = 0; y < targetHeight; y += 1) {
    const sourceY = Math.min(mask.height - 1, Math.max(0, Math.floor((y + 0.5) * mask.height / targetHeight)));
    for (let x = 0; x < targetWidth; x += 1) {
      const sourceX = Math.min(mask.width - 1, Math.max(0, Math.floor((x + 0.5) * mask.width / targetWidth)));
      next.data[y * targetWidth + x] = mask.data[sourceY * mask.width + sourceX];
    }
  }
  return next;
}

function translateSvgPathData(pathData: string, offsetX: number, offsetY: number) {
  if (!pathData || (offsetX === 0 && offsetY === 0)) return pathData;
  let coordinateIndex = 0;
  return pathData.replace(/(-?\d*\.?\d+(?:e[-+]?\d+)?)/gi, (value) => {
    const translated = Number(value) + (coordinateIndex % 2 === 0 ? offsetX : offsetY);
    coordinateIndex += 1;
    return String(translated);
  });
}

function getImagePreviewPixels(
  item: ImageEditItem,
  sourcePreviewImageDataByImageId: Record<string, ImageData>,
  bakedPreviewImageDataByImageId: Record<string, ImageData>,
) {
  return bakedPreviewImageDataByImageId[item.id] || sourcePreviewImageDataByImageId[item.id] || null;
}

function cloneSelectionMask(mask: SelectionMask): SelectionMask {
  return {
    width: mask.width,
    height: mask.height,
    data: new Uint8ClampedArray(mask.data),
    outlineMode: mask.outlineMode,
    version: mask.version ?? 0,
  };
}

function createEmptySelectionMask(width: number, height: number): SelectionMask {
  return {
    width,
    height,
    data: new Uint8ClampedArray(Math.max(0, width * height)),
    outlineMode: "exact",
    version: 0,
  };
}

function boxBlurMaskData(data: Uint8ClampedArray, width: number, height: number, radius: number): Uint8ClampedArray {
  if (radius <= 0 || width <= 0 || height <= 0) return new Uint8ClampedArray(data);
  const horizontal = new Uint8ClampedArray(data.length);
  const blurred = new Uint8ClampedArray(data.length);
  const windowSize = radius * 2 + 1;

  for (let y = 0; y < height; y += 1) {
    let sum = 0;
    for (let x = -radius; x <= radius; x += 1) {
      const sampleX = Math.min(width - 1, Math.max(0, x));
      sum += data[y * width + sampleX];
    }
    for (let x = 0; x < width; x += 1) {
      horizontal[y * width + x] = Math.round(sum / windowSize);
      const removeX = Math.max(0, x - radius);
      const addX = Math.min(width - 1, x + radius + 1);
      sum += data[y * width + addX] - data[y * width + removeX];
    }
  }

  for (let x = 0; x < width; x += 1) {
    let sum = 0;
    for (let y = -radius; y <= radius; y += 1) {
      const sampleY = Math.min(height - 1, Math.max(0, y));
      sum += horizontal[sampleY * width + x];
    }
    for (let y = 0; y < height; y += 1) {
      blurred[y * width + x] = Math.round(sum / windowSize);
      const removeY = Math.max(0, y - radius);
      const addY = Math.min(height - 1, y + radius + 1);
      sum += horizontal[addY * width + x] - horizontal[removeY * width + x];
    }
  }

  return blurred;
}

function featherSelectionMask(mask: SelectionMask, radius: number): SelectionMask {
  const safeRadius = Math.max(0, Math.round(radius));
  if (safeRadius <= 0) return cloneSelectionMask(mask);
  return {
    width: mask.width,
    height: mask.height,
    data: boxBlurMaskData(mask.data, mask.width, mask.height, safeRadius),
  };
}

function cloneSelectionMasks(masks: Record<string, SelectionMask>): Record<string, SelectionMask> {
  return Object.fromEntries(Object.entries(masks).map(([imageId, mask]) => [imageId, cloneSelectionMask(mask)]));
}

function cloneAdjustmentsByImageId(adjustmentsByImageId: Record<string, ColorAdjustments>): Record<string, ColorAdjustments> {
  return Object.fromEntries(Object.entries(adjustmentsByImageId).map(([imageId, adjustments]) => [
    imageId,
    cloneColorAdjustments(adjustments),
  ]));
}

function cloneImageDataValue(imageData: ImageData): ImageData {
  return new ImageData(new Uint8ClampedArray(imageData.data), imageData.width, imageData.height);
}

function cloneImageDataByImageId(imageDataByImageId: Record<string, ImageData>): Record<string, ImageData> {
  return Object.fromEntries(Object.entries(imageDataByImageId).map(([imageId, imageData]) => [
    imageId,
    cloneImageDataValue(imageData),
  ]));
}

function cloneLayerEraseStrokes(strokes: LayerEraseStroke[] | undefined): LayerEraseStroke[] | undefined {
  return strokes?.map((stroke) => ({
    size: stroke.size,
    points: stroke.points.map((point) => ({ ...point })),
  }));
}

function cloneTextLayers(layers: ImageEditTextLayer[]): ImageEditTextLayer[] {
  return layers.map((layer) => ({
    ...layer,
    style: { ...layer.style },
    eraseStrokes: cloneLayerEraseStrokes(layer.eraseStrokes),
  }));
}

function cloneStickerLayers(layers: ImageEditStickerLayer[]): ImageEditStickerLayer[] {
  return layers.map((layer) => ({
    ...layer,
    eraseStrokes: cloneLayerEraseStrokes(layer.eraseStrokes),
  }));
}

function cloneBakedEditOperations(
  operationsByImageId: Record<string, BakedEditOperation[]>
): Record<string, BakedEditOperation[]> {
  return Object.fromEntries(Object.entries(operationsByImageId).map(([imageId, operations]) => [
    imageId,
    operations.map((operation) => ({
      adjustments: cloneColorAdjustments(operation.adjustments),
      selectionMask: operation.selectionMask ? cloneSelectionMask(operation.selectionMask) : undefined,
      selectionMaskIsFeathered: operation.selectionMaskIsFeathered,
    })),
  ]));
}

function createMagicWandMask(imageData: ImageData, startX: number, startY: number, tolerance: number): SelectionMask {
  const { width, height, data } = imageData;
  const safeX = Math.min(width - 1, Math.max(0, Math.floor(startX)));
  const safeY = Math.min(height - 1, Math.max(0, Math.floor(startY)));
  const startIndex = (safeY * width + safeX) * 4;
  const targetR = data[startIndex];
  const targetG = data[startIndex + 1];
  const targetB = data[startIndex + 2];
  const threshold = clampTolerance(tolerance) * 3;
  const selected = new Uint8ClampedArray(width * height);
  const visited = new Uint8Array(width * height);
  const queue = new Int32Array(width * height);
  let readIndex = 0;
  let writeIndex = 0;
  const startPixel = safeY * width + safeX;
  queue[writeIndex] = startPixel;
  writeIndex += 1;
  visited[startPixel] = 1;

  while (readIndex < writeIndex) {
    const pixel = queue[readIndex];
    readIndex += 1;
    const offset = pixel * 4;
    const diff =
      Math.abs(data[offset] - targetR) +
      Math.abs(data[offset + 1] - targetG) +
      Math.abs(data[offset + 2] - targetB);
    if (diff > threshold) continue;

    selected[pixel] = 255;
    const x = pixel % width;
    const y = Math.floor(pixel / width);
    const neighbors = [
      x > 0 ? pixel - 1 : -1,
      x < width - 1 ? pixel + 1 : -1,
      y > 0 ? pixel - width : -1,
      y < height - 1 ? pixel + width : -1,
    ];

    for (let i = 0; i < neighbors.length; i += 1) {
      const nextPixel = neighbors[i];
      if (nextPixel < 0 || visited[nextPixel]) continue;
      visited[nextPixel] = 1;
      queue[writeIndex] = nextPixel;
      writeIndex += 1;
    }
  }

  return { width, height, data: selected, outlineMode: "cleaned", version: 0 };
}

function combineSelectionMasks(
  previous: SelectionMask | undefined,
  nextMask: SelectionMask,
  mode: SelectionCombineMode
): SelectionMask {
  if (!previous || previous.width !== nextMask.width || previous.height !== nextMask.height || mode === "replace") {
    return {
      width: nextMask.width,
      height: nextMask.height,
      data: new Uint8ClampedArray(nextMask.data),
      outlineMode: nextMask.outlineMode,
      version: 0,
    };
  }

  const combined = new Uint8ClampedArray(previous.data);
  for (let i = 0; i < nextMask.data.length; i += 1) {
    if (mode === "add" && isSelectedMaskValue(nextMask.data[i])) combined[i] = 255;
    if (mode === "subtract" && isSelectedMaskValue(nextMask.data[i])) combined[i] = 0;
  }
  const outlineMode = getSelectionOutlineMode(previous) === "exact" || getSelectionOutlineMode(nextMask) === "exact"
    ? "exact"
    : "cleaned";
  return {
    width: previous.width,
    height: previous.height,
    data: combined,
    outlineMode,
    version: (previous.version ?? 0) + 1,
  };
}

function morphSelectionMaskData(
  data: Uint8ClampedArray,
  width: number,
  height: number,
  radius: number,
  mode: "dilate" | "erode"
): Uint8ClampedArray {
  const safeRadius = Math.max(0, Math.round(radius));
  if (safeRadius <= 0 || width <= 0 || height <= 0) return new Uint8ClampedArray(data);

  const horizontal = new Uint8ClampedArray(data.length);
  const result = new Uint8ClampedArray(data.length);

  for (let y = 0; y < height; y += 1) {
    const rowOffset = y * width;
    let selectedCount = 0;
    let windowSize = 0;

    for (let x = 0; x <= Math.min(width - 1, safeRadius); x += 1) {
      if (isSelectedMaskValue(data[rowOffset + x])) selectedCount += 1;
      windowSize += 1;
    }

    for (let x = 0; x < width; x += 1) {
      horizontal[rowOffset + x] = mode === "dilate"
        ? selectedCount > 0 ? 255 : 0
        : selectedCount === windowSize ? 255 : 0;

      const removeX = x - safeRadius;
      if (removeX >= 0) {
        if (isSelectedMaskValue(data[rowOffset + removeX])) selectedCount -= 1;
        windowSize -= 1;
      }

      const addX = x + safeRadius + 1;
      if (addX < width) {
        if (isSelectedMaskValue(data[rowOffset + addX])) selectedCount += 1;
        windowSize += 1;
      }
    }
  }

  for (let x = 0; x < width; x += 1) {
    let selectedCount = 0;
    let windowSize = 0;

    for (let y = 0; y <= Math.min(height - 1, safeRadius); y += 1) {
      if (isSelectedMaskValue(horizontal[y * width + x])) selectedCount += 1;
      windowSize += 1;
    }

    for (let y = 0; y < height; y += 1) {
      result[y * width + x] = mode === "dilate"
        ? selectedCount > 0 ? 255 : 0
        : selectedCount === windowSize ? 255 : 0;

      const removeY = y - safeRadius;
      if (removeY >= 0) {
        if (isSelectedMaskValue(horizontal[removeY * width + x])) selectedCount -= 1;
        windowSize -= 1;
      }

      const addY = y + safeRadius + 1;
      if (addY < height) {
        if (isSelectedMaskValue(horizontal[addY * width + x])) selectedCount += 1;
        windowSize += 1;
      }
    }
  }

  return result;
}

function createOutlineDisplayMaskData(
  data: Uint8ClampedArray,
  width: number,
  height: number
): Uint8ClampedArray {
  const totalPixels = width * height;
  const exterior = new Uint8Array(totalPixels);
  const queue = new Int32Array(totalPixels);
  let head = 0;
  let tail = 0;

  const enqueueExterior = (index: number) => {
    if (index < 0 || index >= totalPixels || exterior[index] || isSelectedMaskValue(data[index])) return;
    exterior[index] = 1;
    queue[tail] = index;
    tail += 1;
  };

  for (let x = 0; x < width; x += 1) {
    enqueueExterior(x);
    enqueueExterior((height - 1) * width + x);
  }
  for (let y = 1; y < height - 1; y += 1) {
    enqueueExterior(y * width);
    enqueueExterior(y * width + width - 1);
  }

  while (head < tail) {
    const index = queue[head];
    head += 1;
    const x = index % width;
    if (x > 0) enqueueExterior(index - 1);
    if (x < width - 1) enqueueExterior(index + 1);
    if (index >= width) enqueueExterior(index - width);
    if (index < totalPixels - width) enqueueExterior(index + width);
  }

  const displayData = new Uint8ClampedArray(totalPixels);
  for (let i = 0; i < totalPixels; i += 1) {
    if (isSelectedMaskValue(data[i]) || !exterior[i]) displayData[i] = 255;
  }
  return displayData;
}

function createSelectionDisplayMaskData(mask: SelectionMask): Uint8ClampedArray {
  if (getSelectionOutlineMode(mask) === "exact") {
    const exact = new Uint8ClampedArray(mask.data.length);
    for (let i = 0; i < mask.data.length; i += 1) {
      exact[i] = isSelectedMaskValue(mask.data[i]) ? 255 : 0;
    }
    return exact;
  }

  const dilated = morphSelectionMaskData(
    mask.data,
    mask.width,
    mask.height,
    SELECTION_DISPLAY_CLOSE_RADIUS,
    "dilate"
  );
  const closed = morphSelectionMaskData(
    dilated,
    mask.width,
    mask.height,
    SELECTION_DISPLAY_CLOSE_RADIUS,
    "erode"
  );
  return createOutlineDisplayMaskData(closed, mask.width, mask.height);
}

function formatPathNumber(value: number) {
  if (!Number.isFinite(value)) return "0";
  const rounded = Math.abs(value) < 0.005 ? 0 : Math.round(value * 100) / 100;
  if (Number.isInteger(rounded)) return String(rounded);
  return rounded.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
}

function createSvgLinePath(x1: number, y1: number, x2: number, y2: number) {
  return `M ${formatPathNumber(x1)} ${formatPathNumber(y1)} L ${formatPathNumber(x2)} ${formatPathNumber(y2)}`;
}

interface SelectionOutlineEdge {
  startKey: number;
  endKey: number;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  dx: number;
  dy: number;
}

interface GridPoint {
  x: number;
  y: number;
}

function getGridVertexKey(x: number, y: number, stride: number) {
  return y * stride + x;
}

function getTurnPreferenceScore(previousDx: number, previousDy: number, nextDx: number, nextDy: number) {
  const dot = previousDx * nextDx + previousDy * nextDy;
  const cross = previousDx * nextDy - previousDy * nextDx;
  if (dot > 0) return 4;
  if (cross < 0) return 3;
  if (cross > 0) return 2;
  return 1;
}

function simplifyGridPoints(points: GridPoint[], closed: boolean): GridPoint[] {
  const deduped: GridPoint[] = [];
  for (const point of points) {
    const previous = deduped[deduped.length - 1];
    if (!previous || previous.x !== point.x || previous.y !== point.y) {
      deduped.push(point);
    }
  }
  if (closed && deduped.length > 1) {
    const first = deduped[0];
    const last = deduped[deduped.length - 1];
    if (first.x === last.x && first.y === last.y) deduped.pop();
  }

  const isCollinear = (previous: GridPoint, current: GridPoint, next: GridPoint) => {
    const dx1 = current.x - previous.x;
    const dy1 = current.y - previous.y;
    const dx2 = next.x - current.x;
    const dy2 = next.y - current.y;
    return dx1 * dy2 === dy1 * dx2;
  };

  if (!closed) {
    if (deduped.length <= 2) return deduped;
    const simplified = [deduped[0]];
    for (let i = 1; i < deduped.length - 1; i += 1) {
      if (!isCollinear(deduped[i - 1], deduped[i], deduped[i + 1])) {
        simplified.push(deduped[i]);
      }
    }
    simplified.push(deduped[deduped.length - 1]);
    return simplified;
  }

  let simplified = deduped;
  let changed = true;
  while (changed && simplified.length > 2) {
    changed = false;
    const nextPoints: GridPoint[] = [];
    for (let i = 0; i < simplified.length; i += 1) {
      const previous = simplified[(i - 1 + simplified.length) % simplified.length];
      const current = simplified[i];
      const next = simplified[(i + 1) % simplified.length];
      if (isCollinear(previous, current, next)) {
        changed = true;
      } else {
        nextPoints.push(current);
      }
    }
    simplified = nextPoints;
  }
  return simplified;
}

function getGridPathLength(points: GridPoint[], closed: boolean, scaleX: number, scaleY: number) {
  let length = 0;
  for (let i = 1; i < points.length; i += 1) {
    length += Math.hypot((points[i].x - points[i - 1].x) * scaleX, (points[i].y - points[i - 1].y) * scaleY);
  }
  if (closed && points.length > 1) {
    const first = points[0];
    const last = points[points.length - 1];
    length += Math.hypot((first.x - last.x) * scaleX, (first.y - last.y) * scaleY);
  }
  return length;
}

function createSvgPolylinePath(points: GridPoint[], scaleX: number, scaleY: number, closed: boolean) {
  if (points.length === 0) return "";
  const commands = [
    `M ${formatPathNumber(points[0].x * scaleX)} ${formatPathNumber(points[0].y * scaleY)}`,
  ];
  for (let i = 1; i < points.length; i += 1) {
    commands.push(`L ${formatPathNumber(points[i].x * scaleX)} ${formatPathNumber(points[i].y * scaleY)}`);
  }
  if (closed) commands.push("Z");
  return commands.join(" ");
}

function createSelectionOutlinePath(
  mask: SelectionMask | undefined,
  targetWidth: number,
  targetHeight: number
): string | null {
  if (!mask) return null;
  const scaleX = targetWidth / mask.width;
  const scaleY = targetHeight / mask.height;
  const displayData = createSelectionDisplayMaskData(mask);
  const isSelected = (x: number, y: number) => {
    if (x < 0 || y < 0 || x >= mask.width || y >= mask.height) return false;
    return displayData[y * mask.width + x] > 0;
  };

  const vertexStride = mask.width + 1;
  const edges: SelectionOutlineEdge[] = [];
  const outgoingEdges = new Map<number, number[]>();

  const addEdge = (startX: number, startY: number, endX: number, endY: number) => {
    const startKey = getGridVertexKey(startX, startY, vertexStride);
    const endKey = getGridVertexKey(endX, endY, vertexStride);
    const edgeIndex = edges.length;
    edges.push({
      startKey,
      endKey,
      startX,
      startY,
      endX,
      endY,
      dx: Math.sign(endX - startX),
      dy: Math.sign(endY - startY),
    });
    const outgoing = outgoingEdges.get(startKey);
    if (outgoing) {
      outgoing.push(edgeIndex);
    } else {
      outgoingEdges.set(startKey, [edgeIndex]);
    }
  };

  for (let y = 0; y < mask.height; y += 1) {
    for (let x = 0; x < mask.width; x += 1) {
      if (!isSelected(x, y)) continue;
      if (!isSelected(x, y - 1)) addEdge(x, y, x + 1, y);
      if (!isSelected(x + 1, y)) addEdge(x + 1, y, x + 1, y + 1);
      if (!isSelected(x, y + 1)) addEdge(x + 1, y + 1, x, y + 1);
      if (!isSelected(x - 1, y)) addEdge(x, y + 1, x, y);
    }
  }

  const usedEdges = new Uint8Array(edges.length);
  const pathCommands: string[] = [];

  for (let edgeIndex = 0; edgeIndex < edges.length; edgeIndex += 1) {
    if (usedEdges[edgeIndex]) continue;

    const firstEdge = edges[edgeIndex];
    const points: GridPoint[] = [
      { x: firstEdge.startX, y: firstEdge.startY },
      { x: firstEdge.endX, y: firstEdge.endY },
    ];
    let currentKey = firstEdge.endKey;
    let previousDx = firstEdge.dx;
    let previousDy = firstEdge.dy;
    usedEdges[edgeIndex] = 1;

    for (let guard = 0; guard < edges.length && currentKey !== firstEdge.startKey; guard += 1) {
      const candidates = outgoingEdges.get(currentKey);
      if (!candidates) break;

      let nextEdgeIndex = -1;
      let nextScore = -Infinity;
      for (const candidateIndex of candidates) {
        if (usedEdges[candidateIndex]) continue;
        const candidate = edges[candidateIndex];
        const score = getTurnPreferenceScore(previousDx, previousDy, candidate.dx, candidate.dy);
        if (score > nextScore) {
          nextScore = score;
          nextEdgeIndex = candidateIndex;
        }
      }

      if (nextEdgeIndex < 0) break;
      const nextEdge = edges[nextEdgeIndex];
      usedEdges[nextEdgeIndex] = 1;
      points.push({ x: nextEdge.endX, y: nextEdge.endY });
      currentKey = nextEdge.endKey;
      previousDx = nextEdge.dx;
      previousDy = nextEdge.dy;
    }

    const closed = currentKey === firstEdge.startKey;
    const simplifiedPoints = simplifyGridPoints(points, closed);
    if (simplifiedPoints.length < (closed ? 3 : 2)) continue;
    if (getGridPathLength(simplifiedPoints, closed, scaleX, scaleY) < SELECTION_MIN_OUTLINE_CONTOUR_PX) {
      continue;
    }
    pathCommands.push(createSvgPolylinePath(simplifiedPoints, scaleX, scaleY, closed));
  }

  return pathCommands.length > 0 ? pathCommands.join(" ") : null;
}

function createFabricOverlayPath(
  fabric: FabricModule,
  pathData: string,
  options: {
    stroke: string;
    strokeWidth: number;
    strokeDashArray?: number[] | null;
    strokeDashOffset?: number;
    strokeLineCap?: CanvasLineCap;
  }
): FabricPath {
  return new fabric.Path(pathData, {
    fill: "",
    stroke: options.stroke,
    strokeWidth: options.strokeWidth,
    strokeDashArray: options.strokeDashArray ?? null,
    strokeDashOffset: options.strokeDashOffset ?? 0,
    strokeLineCap: options.strokeLineCap ?? "butt",
    strokeLineJoin: "round",
    selectable: false,
    evented: false,
    objectCaching: false,
    excludeFromExport: true,
  });
}

function removeFabricPath(canvas: FabricStaticCanvas, path: FabricPath | null) {
  if (path) canvas.remove(path);
}

function getFitCanvasTransform(
  imageData: ImageDimensions | null,
  viewport: CanvasViewportSize
): CanvasTransform {
  if (!imageData || viewport.width <= 0 || viewport.height <= 0) {
    return { scale: 1, offsetX: 0, offsetY: 0 };
  }

  const scale = Math.min(viewport.width / imageData.width, viewport.height / imageData.height, 1);
  return {
    scale,
    offsetX: (viewport.width - imageData.width * scale) / 2,
    offsetY: (viewport.height - imageData.height * scale) / 2,
  };
}

/**
 * 描边/圆角预览的显示宽度需要与外部详情页复刻结果列（固定 400px）一一对应，
 * 否则堆叠预览把所有屏竖排塞进画布时每屏会被缩到远小于 400px，描边看着比
 * 外面小很多。这里把每屏显示内容宽度锚定到外部列宽，描边/圆角比例与外部
 * 一致；超出视口的部分通过画布平移查看。
 */
const STROKE_PREVIEW_FRAME_DISPLAY_WIDTH = 400;

function getStrokePreviewFitTransform(
  imageData: ImageDimensions | null,
  viewport: CanvasViewportSize,
  zoomMultiplier = 1,
): CanvasTransform {
  if (!imageData || imageData.width <= 0 || viewport.width <= 0) {
    return { scale: 1, offsetX: 0, offsetY: 0 };
  }
  const anchorScale = STROKE_PREVIEW_FRAME_DISPLAY_WIDTH / imageData.width;
  const scale = anchorScale * zoomMultiplier;
  return {
    scale,
    offsetX: (viewport.width - imageData.width * scale) / 2,
    offsetY: (viewport.height - imageData.height * scale) / 2,
  };
}

const AdjustmentControl = memo(function AdjustmentControl({
  label,
  value,
  gradient = NEUTRAL_ADJUSTMENT_GRADIENT,
  displayScale = 1,
  displayPrecision = 0,
  endLabels,
  thumbColor = "#D2D2D2",
  onChange,
  onInteractionEnd,
}: AdjustmentControlProps) {
  const liveValueRef = useRef(value);
  const draftValueRef = useRef(formatAdjustmentDisplayValue(value, displayScale, displayPrecision));
  const rangeInputRef = useRef<HTMLInputElement | null>(null);
  const valueInputRef = useRef<HTMLInputElement | null>(null);
  const thumbMarkerRef = useRef<HTMLSpanElement | null>(null);
  const pendingValueRef = useRef<number | null>(null);
  const changeFrameRef = useRef<number | null>(null);
  const interactingRef = useRef(false);
  const onChangeRef = useRef(onChange);

  const syncControlElements = useCallback((nextValue: number, options?: { input?: boolean; range?: boolean }) => {
    const clampedValue = clampAdjustment(nextValue);
    const shouldSyncInput = options?.input !== false;
    const shouldSyncRange = options?.range !== false;
    if (shouldSyncRange && rangeInputRef.current) {
      rangeInputRef.current.value = String(clampedValue);
    }
    if (shouldSyncInput && valueInputRef.current) {
      valueInputRef.current.value = formatAdjustmentDisplayValue(clampedValue, displayScale, displayPrecision);
    }
    if (thumbMarkerRef.current) {
      thumbMarkerRef.current.style.left = `${adjustmentValueToPercent(clampedValue)}%`;
    }
  }, [displayPrecision, displayScale]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (interactingRef.current) return;
    liveValueRef.current = value;
    draftValueRef.current = formatAdjustmentDisplayValue(value, displayScale, displayPrecision);
    syncControlElements(value);
  }, [displayPrecision, displayScale, syncControlElements, value]);

  useEffect(() => () => {
    if (changeFrameRef.current !== null) {
      cancelAnimationFrame(changeFrameRef.current);
      changeFrameRef.current = null;
    }
  }, []);

  const scheduleChange = useCallback((nextValue: number) => {
    pendingValueRef.current = nextValue;
    if (changeFrameRef.current !== null) return;
    changeFrameRef.current = requestAnimationFrame(() => {
      changeFrameRef.current = null;
      const pendingValue = pendingValueRef.current;
      pendingValueRef.current = null;
      if (pendingValue !== null) onChangeRef.current(pendingValue);
    });
  }, []);

  const flushChange = useCallback((nextValue: number) => {
    if (changeFrameRef.current !== null) {
      cancelAnimationFrame(changeFrameRef.current);
      changeFrameRef.current = null;
    }
    pendingValueRef.current = null;
    onChangeRef.current(nextValue);
  }, []);

  const updateLiveValue = useCallback((nextValue: number) => {
    const clampedValue = clampAdjustment(nextValue);
    liveValueRef.current = clampedValue;
    draftValueRef.current = formatAdjustmentDisplayValue(clampedValue, displayScale, displayPrecision);
    syncControlElements(clampedValue);
    scheduleChange(clampedValue);
  }, [displayPrecision, displayScale, scheduleChange, syncControlElements]);

  const commitDraftValue = () => {
    const parsedValue = parseAdjustmentDisplayValue(draftValueRef.current, displayScale);
    const nextValue = parsedValue === null ? liveValueRef.current : parsedValue;
    liveValueRef.current = nextValue;
    draftValueRef.current = formatAdjustmentDisplayValue(nextValue, displayScale, displayPrecision);
    syncControlElements(nextValue);
    flushChange(nextValue);
  };

  return (
    <section className="select-none space-y-1.5">
      <style>{`
        input.image-edit-adjustment-range::-webkit-slider-runnable-track {
          height: 100%;
          appearance: none;
          border-radius: 9999px;
          background: transparent;
        }

        input.image-edit-adjustment-range::-webkit-slider-thumb {
          height: 20px;
          width: 18px;
          appearance: none;
          border: 0;
          background: transparent;
        }

        input.image-edit-adjustment-range::-moz-range-track,
        input.image-edit-adjustment-range::-moz-range-progress {
          height: 100%;
          border-radius: 9999px;
          background: transparent;
        }

        input.image-edit-adjustment-range::-moz-range-thumb {
          height: 20px;
          width: 18px;
          border: 0;
          background: transparent;
        }
      `}</style>
      <div className={`relative flex items-center gap-3 ${endLabels ? "h-[18px] justify-between" : "justify-between"}`}>
        {endLabels ? (
          <>
            <span className="text-[11px] text-[#A0A0A0]">{endLabels.left}</span>
            <span className="sr-only">{label}</span>
            <span className="text-[11px] text-[#A0A0A0]">{endLabels.right}</span>
          </>
        ) : (
          <label className="shrink-0 text-[11px] font-medium text-[#F6F6F6]">{label}</label>
        )}
        <input
          ref={valueInputRef}
          type="text"
          inputMode={displayPrecision > 0 ? "decimal" : "numeric"}
          defaultValue={formatAdjustmentDisplayValue(value, displayScale, displayPrecision)}
          onFocus={(e) => {
            if (Number(e.currentTarget.value) !== 0) return;
            e.currentTarget.value = "";
            draftValueRef.current = "";
          }}
          onChange={(e) => {
            const nextValue = e.target.value.trim();
            const validDraft = displayPrecision > 0 ? /^-?\d*(?:\.\d*)?$/.test(nextValue) : /^-?\d*$/.test(nextValue);
            if (!validDraft) {
              e.currentTarget.value = draftValueRef.current;
              return;
            }
            draftValueRef.current = nextValue;
            const parsedValue = parseAdjustmentDisplayValue(nextValue, displayScale);
            if (parsedValue !== null) {
              liveValueRef.current = parsedValue;
              syncControlElements(parsedValue, { input: false });
              scheduleChange(parsedValue);
            }
          }}
          onBlur={() => {
            commitDraftValue();
            onInteractionEnd();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
          }}
          aria-label={typeof label === "string" ? label : undefined}
          className={`select-text h-[18px] w-[38px] rounded-[4px] border-0 bg-[#3a3a3d] px-1 text-center text-[11px] font-medium leading-none text-white/90 outline-none transition focus:bg-[#4a4a4d] ${endLabels ? "absolute left-1/2 top-0 -translate-x-1/2" : ""}`}
        />
      </div>
      <div className="relative h-[14px]">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-0 right-0 top-[6px] h-[3px] rounded-full shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)]"
          style={{ background: gradient }}
        />
        <span
          ref={thumbMarkerRef}
          aria-hidden="true"
          className="pointer-events-none absolute top-[-2px] h-[17px] w-[18px] -translate-x-1/2 text-[#D2D2D2] drop-shadow-[0_1px_2px_rgba(0,0,0,0.65)]"
          style={{
            left: `${adjustmentValueToPercent(value)}%`,
            color: thumbColor,
          }}
        >
          <span className="absolute left-1/2 top-0 h-0 w-0 -translate-x-1/2 border-l-[9px] border-r-[9px] border-b-[9px] border-l-transparent border-r-transparent border-b-[#6b6b70]" />
          <span className="absolute left-0 top-[8px] h-[8px] w-[18px] rounded-[1px] bg-[#6b6b70]" />
          <span className="absolute left-1/2 top-[1px] h-0 w-0 -translate-x-1/2 border-l-[8px] border-r-[8px] border-b-[8px] border-l-transparent border-r-transparent border-b-current" />
          <span className="absolute left-[1px] top-[8px] h-[7px] w-[16px] rounded-[1px] bg-current" />
        </span>
        <input
          ref={rangeInputRef}
          type="range"
          min={MIN_ADJUSTMENT}
          max={MAX_ADJUSTMENT}
          defaultValue={value}
          onChange={(e) => updateLiveValue(Number(e.target.value))}
          onPointerDown={() => {
            // A previous drag over a label can leave a native text selection
            // active. Clear it before the transparent range receives the drag
            // so the browser does not keep treating the gesture as selection.
            clearNativeTextSelection();
            interactingRef.current = true;
          }}
          onPointerUp={() => {
            interactingRef.current = false;
            flushChange(liveValueRef.current);
            onInteractionEnd();
          }}
          onPointerCancel={() => {
            interactingRef.current = false;
            flushChange(liveValueRef.current);
            onInteractionEnd();
          }}
          onBlur={() => {
            interactingRef.current = false;
            flushChange(liveValueRef.current);
            syncControlElements(liveValueRef.current);
            onInteractionEnd();
          }}
          className="image-edit-adjustment-range absolute inset-x-[-1px] top-0 h-[14px] cursor-pointer appearance-none bg-transparent opacity-0 outline-none"
        />
      </div>
    </section>
  );
});

async function selectionMaskFromPngBase64(maskPngBase64: string, width: number, height: number): Promise<SelectionMask> {
  if (!maskPngBase64 || width <= 0 || height <= 0) {
    throw new Error("E8106 智能选区失败，请手动选择区域");
  }

  const { img, cleanup } = await loadImage(`data:image/png;base64,${maskPngBase64}`);
  try {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) throw new Error("E8106 智能选区失败，请手动选择区域");

    ctx.drawImage(img, 0, 0, width, height);
    const imageData = ctx.getImageData(0, 0, width, height);
    const useAlpha = imageDataHasUsefulAlpha(imageData);
    const maskData = new Uint8ClampedArray(width * height);
    let selectedPixels = 0;
    for (let pixel = 0; pixel < width * height; pixel += 1) {
      const offset = pixel * 4;
      const value = useAlpha
        ? imageData.data[offset + 3]
        : Math.round(
            imageData.data[offset] * 0.299 +
              imageData.data[offset + 1] * 0.587 +
              imageData.data[offset + 2] * 0.114
          );
      maskData[pixel] = value;
      if (isSelectedMaskValue(value)) selectedPixels += 1;
    }
    if (selectedPixels < Math.max(8, Math.floor((width * height) / 2000))) {
      throw new Error("E8107 未识别到可用区域");
    }
    return { width, height, data: maskData, outlineMode: "exact", version: 0 };
  } finally {
    cleanup();
  }
}

function imageDataHasUsefulAlpha(imageData: ImageData): boolean {
  let minAlpha = 255;
  let maxAlpha = 0;
  for (let i = 3; i < imageData.data.length; i += 4) {
    const alpha = imageData.data[i];
    if (alpha < minAlpha) minAlpha = alpha;
    if (alpha > maxAlpha) maxAlpha = alpha;
  }
  return maxAlpha > minAlpha && minAlpha < 250;
}

async function renderEditedBlob(
  url: string,
  processImageData: (imageData: ImageData) => Promise<ImageData>,
  composite?: (ctx: CanvasRenderingContext2D, width: number, height: number) => void | Promise<void>,
  frameStyle?: ImageFrameStyle,
  frameStyleDisplayWidth?: number,
): Promise<Blob> {
  const { img, cleanup } = await loadImage(url);
  try {
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) throw new Error("E8101 编辑参数有误，请调整后重试");

    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    ctx.putImageData(await processImageData(imageData), 0, 0);
    await composite?.(ctx, canvas.width, canvas.height);
    if (frameStyle) {
      // 描边和圆角都以 400px 显示宽为基准；导出时一次性映射到源图像素并烘焙。
      const frameStyleToApply = frameStyleDisplayWidth && frameStyleDisplayWidth > 0
        ? scaleImageFrameStyle(
            frameStyle,
            canvas.width / frameStyleDisplayWidth,
            canvas.width / frameStyleDisplayWidth,
          )
        : frameStyle;
      applyImageFrameStyleToCanvas(canvas, frameStyleToApply);
    }

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("E8102 图片保存失败，请稍后重试"));
          return;
        }
        resolve(blob);
      }, "image/png", 0.95);
    });
  } finally {
    cleanup();
  }
}

async function drawStickerLayersToCanvas(
  ctx: CanvasRenderingContext2D,
  layers: ImageEditStickerLayer[],
  imageId: string,
  targetWidth: number,
  targetHeight: number,
) {
  for (const layer of layers) {
    if (layer.imageId !== imageId) continue;
    const { img, cleanup } = await loadImage(layer.url);
    let contextSaved = false;
    try {
      const scaleX = targetWidth / Math.max(1, layer.coordinateWidth);
      const scaleY = targetHeight / Math.max(1, layer.coordinateHeight);
      const targetContext = layer.eraseStrokes?.length && typeof document !== "undefined"
        ? document.createElement("canvas")
        : null;
      if (targetContext) {
        targetContext.width = targetWidth;
        targetContext.height = targetHeight;
      }
      const drawCtx = targetContext?.getContext("2d") ?? ctx;
      drawCtx.save();
      contextSaved = true;
      drawCtx.translate(
        (layer.x + layer.width / 2) * scaleX,
        (layer.y + layer.height / 2) * scaleY,
      );
      drawCtx.rotate(layer.rotation * Math.PI / 180);
      drawCtx.drawImage(
        img,
        -layer.width * scaleX / 2,
        -layer.height * scaleY / 2,
        layer.width * scaleX,
        layer.height * scaleY,
      );
      drawCtx.restore();
      contextSaved = false;
      if (layer.eraseStrokes?.length && targetContext) {
        drawCtx.save();
        drawCtx.globalCompositeOperation = "destination-out";
        drawCtx.lineCap = "round";
        drawCtx.lineJoin = "round";
        for (const stroke of layer.eraseStrokes) {
          if (!stroke.points.length) continue;
          drawCtx.lineWidth = Math.max(1, stroke.size * Math.min(scaleX, scaleY));
          drawCtx.beginPath();
          drawCtx.moveTo(stroke.points[0].x * scaleX, stroke.points[0].y * scaleY);
          for (let index = 1; index < stroke.points.length; index += 1) {
            drawCtx.lineTo(stroke.points[index].x * scaleX, stroke.points[index].y * scaleY);
          }
          if (stroke.points.length === 1) drawCtx.lineTo(stroke.points[0].x * scaleX + 0.01, stroke.points[0].y * scaleY);
          drawCtx.stroke();
        }
        drawCtx.restore();
        ctx.drawImage(targetContext, 0, 0);
      }
    } finally {
      if (contextSaved) ctx.restore();
      cleanup();
    }
  }
}

async function drawOrderedImageEditLayersToCanvas(
  ctx: CanvasRenderingContext2D,
  textLayers: ImageEditTextLayer[],
  stickerLayers: ImageEditStickerLayer[],
  layerOrder: ImageEditLayerOrderEntry[],
  imageId: string,
  targetWidth: number,
  targetHeight: number,
) {
  const textById = new Map(textLayers.map((layer) => [layer.id, layer]));
  const stickerById = new Map(stickerLayers.map((layer) => [layer.id, layer]));
  for (const orderedLayer of layerOrder) {
    if (orderedLayer.imageId !== imageId) continue;
    if (orderedLayer.kind === "text") {
      const layer = textById.get(orderedLayer.id);
      if (layer) drawTextLayersToCanvas(ctx, [layer], imageId, targetWidth, targetHeight);
      continue;
    }
    const layer = stickerById.get(orderedLayer.id);
    if (layer) await drawStickerLayersToCanvas(ctx, [layer], imageId, targetWidth, targetHeight);
  }
}

function paintPreviewImageDataToBuffer(
  imageData: ImageData,
  targetCanvas: HTMLCanvasElement,
  coordinateWidth: number,
  coordinateHeight: number,
  scratchCanvas: HTMLCanvasElement
) {
  const targetWidth = Math.max(1, Math.round(coordinateWidth));
  const targetHeight = Math.max(1, Math.round(coordinateHeight));
  targetCanvas.width = targetWidth;
  targetCanvas.height = targetHeight;

  const targetCtx = targetCanvas.getContext("2d", { willReadFrequently: true });
  if (!targetCtx) return false;

  if (imageData.width === targetWidth && imageData.height === targetHeight) {
    targetCtx.putImageData(imageData, 0, 0);
    return true;
  }

  scratchCanvas.width = imageData.width;
  scratchCanvas.height = imageData.height;
  const scratchCtx = scratchCanvas.getContext("2d", { willReadFrequently: true });
  if (!scratchCtx) return false;

  scratchCtx.putImageData(imageData, 0, 0);
  targetCtx.clearRect(0, 0, targetWidth, targetHeight);
  targetCtx.imageSmoothingEnabled = true;
  targetCtx.imageSmoothingQuality = "medium";
  targetCtx.drawImage(scratchCanvas, 0, 0, targetWidth, targetHeight);
  return true;
}

function drawRoundedCanvasRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) {
  const safeRadius = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + safeRadius, y);
  ctx.lineTo(x + width - safeRadius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
  ctx.lineTo(x + width, y + height - safeRadius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height);
  ctx.lineTo(x + safeRadius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
  ctx.lineTo(x, y + safeRadius);
  ctx.quadraticCurveTo(x, y, x + safeRadius, y);
  ctx.closePath();
}

function getComparisonBadgeMetrics(ctx: CanvasRenderingContext2D, label: string) {
  ctx.font = "600 12px system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
  return {
    width: Math.ceil(ctx.measureText(label).width) + 18,
    height: 24,
  };
}

function drawComparisonBadge(
  ctx: CanvasRenderingContext2D,
  label: string,
  x: number,
  y: number,
  width: number,
  height: number
) {
  ctx.save();
  ctx.font = "600 12px system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "rgba(8,8,10,0.64)";
  drawRoundedCanvasRect(ctx, x, y, width, height, 6);
  ctx.fill();
  ctx.strokeStyle = "rgba(255,255,255,0.2)";
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.fillStyle = "rgba(255,255,255,0.92)";
  ctx.fillText(label, x + 9, y + height / 2 + 0.5);
  ctx.restore();
}

function drawComparisonLabels(
  ctx: CanvasRenderingContext2D,
  imageData: ImageDimensions,
  transform: CanvasTransform,
  split: number,
  viewport: CanvasViewportSize
) {
  const imageLeft = transform.offsetX;
  const imageTop = transform.offsetY;
  const imageRight = imageLeft + imageData.width * transform.scale;
  const imageBottom = imageTop + imageData.height * transform.scale;
  const splitX = imageLeft + imageData.width * transform.scale * split;
  const labelTop = Math.min(
    Math.max(imageTop + 12, 12),
    Math.max(12, Math.min(imageBottom - 36, viewport.height - 36))
  );

  const labels = [
    { text: "原图", left: Math.max(imageLeft, 0), right: Math.min(splitX, viewport.width) },
    { text: "调色图", left: Math.max(splitX, 0), right: Math.min(imageRight, viewport.width) },
  ];

  labels.forEach(({ text, left, right }) => {
    const metrics = getComparisonBadgeMetrics(ctx, text);
    const availableWidth = right - left;
    if (availableWidth < metrics.width + 12) return;
    const badgeLeft = Math.min(
      Math.max(left + 12, left + 6),
      right - metrics.width - 6
    );
    drawComparisonBadge(ctx, text, badgeLeft, labelTop, metrics.width, metrics.height);
  });
}

function downloadBlob(blob: Blob, filename: string) {
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
}

export default function ImageEditModal(props: ImageEditModalProps) {
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  useEffect(() => setPortalContainer(document.body), []);

  // Mount the editor only once its portal can render the canvas. Otherwise the
  // initial effects see null refs and skip viewport sizing, image loading and
  // native listeners when callers mount an already-open modal.
  if (!portalContainer) return null;
  return <ImageEditModalContent {...props} editorPortalContainer={portalContainer} />;
}

function ImageEditModalContent({
  open,
  mode: initialMode,
  images,
  initialImageId,
  taskId,
  previewLayout: previewLayoutProp,
  saveAsCopy = false,
  onClose,
  onSave,
  editorPortalContainer,
}: ImageEditModalProps & { editorPortalContainer: HTMLElement }) {
  const editorPortal = useEcomPortalController();
  const { user } = useShellState();
  const tutorialApi = useSceneTutorialApi();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const selectionOverlayCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const canvasViewportRef = useRef<HTMLDivElement | null>(null);
  const selectionPanelRef = useRef<HTMLElement | null>(null);
  const localFileInputRef = useRef<HTMLInputElement | null>(null);
  const localFolderInputRef = useRef<HTMLInputElement | null>(null);
  const thumbnailViewportRef = useRef<HTMLDivElement | null>(null);
  const fontFileInputRef = useRef<HTMLInputElement | null>(null);
  const fontUploadRunRef = useRef(createFontUploadRunSession());
  const fontUploadBusyRef = useRef(false);
  const personalFontDeleteCancelRef = useRef<HTMLButtonElement | null>(null);
  const activeTextAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const eyeDropperMagnifierCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const customFontUrlsRef = useRef<string[]>([]);
  const registeredFontFacesRef = useRef<Array<{ face: FontFace; personalFontUuid?: string }>>([]);
  const platformFontsRegisteredRef = useRef<Promise<void> | null>(null);

  // canvas 合成是一次性绘制,不会随 FontFace 注册自动重绘;
  // 导出/描边合成前先等平台字体注册完,并按文本层实际样式触发 document.fonts.load。
  const ensureTextLayerFontsReady = useCallback(async (layers: ImageEditTextLayer[]) => {
    await platformFontsRegisteredRef.current;
    if (typeof document === "undefined" || !document.fonts) return;
    await Promise.allSettled(layers.map((layer) => document.fonts.load(
      `${layer.style.italic ? "italic " : ""}${layer.style.fontWeight} ${layer.style.fontSize}px ${layer.style.fontFamily}`,
    )));
  }, []);
  const modalOpenRef = useRef(open);
  const previewBufferCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const originalPreviewBufferCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const previewScratchCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const basePreviewImageDataRef = useRef<ImageData | null>(null);
  const draftPreviewImageDataRef = useRef<ImageData | null>(null);
  const strokePreviewSourcesRef = useRef<StrokePreviewFrameSource[]>([]);
  const strokePreviewCacheSessionRef = useRef(0);
  const strokePreviewLoadRunRef = useRef(0);
  const strokePreviewSourceSessionsRef = useRef({ display: 0 });
  const strokePreviewFrameRef = useRef<number | null>(null);
  const requestStrokePreviewFrameRef = useRef<() => void>(() => {});
  // React state keeps the controls controlled; the render loop reads this ref
  // so pointermove never waits for a component commit before painting.
  const sliderValuesRef = useRef({ topBottom: 0, leftRight: 0, cornerRadius: 0 });
  const renderedPreviewImageDataRef = useRef<ImageDimensions | null>(null);
  const previewProcessRequestRef = useRef<PreviewProcessRequest | null>(null);
  const requestPreviewProcessingRef = useRef<(
    nextAdjustments: ColorAdjustments,
    selectionMask?: SelectionMask,
    quality?: PreviewQuality
  ) => void>(() => {});
  const previewProcessingRef = useRef(false);
  const previewRunIdRef = useRef(0);
  const previewRerunQueuedRef = useRef(false);
  const dragStartRef = useRef<{
    clientX: number;
    clientY: number;
    offsetX: number;
    offsetY: number;
    allowWand: boolean;
    wandShortcutMode: SelectionShortcutMode | null;
  } | null>(null);
  const brushStrokeRef = useRef<BrushStrokeState | null>(null);
  const selectionShortcutKeysRef = useRef<{ altKey: boolean; shiftKey: boolean }>({ altKey: false, shiftKey: false });
  const brushPreviewFrameRef = useRef<number | null>(null);
  const selectionAnimationFrameRef = useRef<number | null>(null);
  const previewProcessFrameRef = useRef<number | null>(null);
  const canvasRedrawFrameRef = useRef<number | null>(null);
  const cursorUpdateFrameRef = useRef<number | null>(null);
  const canvasTransformRef = useRef<CanvasTransform>({ scale: 1, offsetX: 0, offsetY: 0 });
  const canvasZoomMultiplierRef = useRef(1);
  const canvasViewportSizeRef = useRef<CanvasViewportSize>({ width: 1, height: 1 });
  const comparisonModeRef = useRef(false);
  const comparisonSplitRef = useRef(0.5);
  const comparisonDragPointerRef = useRef<number | null>(null);
  const currentSelectionMaskRef = useRef<SelectionMask | undefined>(undefined);
  const skipNextSelectionPreviewRef = useRef(false);
  const selectionOutlinePathCacheRef = useRef<SelectionOutlinePathCache | null>(null);
  const selectionOverlayFabricRef = useRef<SelectionOverlayFabricState | null>(null);
  const smartSelectionCacheRef = useRef<Record<string, SmartSelectionCacheEntry>>({});
  const workerMaskCacheRef = useRef<WorkerMaskCache | null>(null);
  const editScopeRef = useRef<EditScope>("selection");
  const selectionFeatherRadiusRef = useRef(DEFAULT_SELECTION_FEATHER_RADIUS);
  const selectionOutlineVisibleRef = useRef(true);
  const selectionDashOffsetRef = useRef(0);
  const basePreviewImageIdRef = useRef("");
  const liveAdjustmentsRef = useRef<ColorAdjustments>(DEFAULT_ADJUSTMENTS);
  const adjustmentsByImageIdRef = useRef<Record<string, ColorAdjustments>>({});
  const selectionMasksByImageIdRef = useRef<Record<string, SelectionMask>>({});
  // Keep the original preview pixels separate from pixels baked by Ctrl+D so
  // undo/redo can restore the exact visual state without refetching the image.
  const sourcePreviewImageDataByImageIdRef = useRef<Record<string, ImageData>>({});
  const bakedPreviewImageDataByImageIdRef = useRef<Record<string, ImageData>>({});
  const stackPreviewLayoutRef = useRef<StackPreviewLayout | null>(null);
  const strokeStackDirectLayoutRef = useRef<StrokeStackPreviewLayout | null>(null);
  const bakedEditOperationsByImageIdRef = useRef<Record<string, BakedEditOperation[]>>({});
  const ctrlDBakeInProgressRef = useRef(false);
  const ctrlDCommitFrameRef = useRef<number | null>(null);
  const ctrlDCommitPromiseRef = useRef<Promise<void> | null>(null);
  const ctrlDCommitResolveRef = useRef<(() => void) | null>(null);
  const rightPanelTabRequestRef = useRef(0);
  const pendingBrushCursorRef = useRef<{ x: number; y: number } | null>(null);
  const pendingWandCursorRef = useRef<{ x: number; y: number; overImage: boolean } | null>(null);
  const localImageUrlsRef = useRef<Set<string>>(new Set());
  const localImageSequenceRef = useRef(0);
  const localUploadDragDepthRef = useRef(0);
  const localSmartSelectionUploadPromisesRef = useRef<Record<string, Promise<string>>>({});
  const [mode, setMode] = useState<EditMode>(initialMode);
  const [editScope, setEditScope] = useState<EditScope>("selection");
  const [selectionCombineMode, setSelectionCombineMode] = useState<SelectionCombineMode>("add");
  const [selectionTool, setSelectionTool] = useState<SelectionTool | null>(null);
  const [selectionPanelOpen, setSelectionPanelOpen] = useState(false);
  const [selectionPanelTop, setSelectionPanelTop] = useState(12);
  const [selectionOutlineVisible, setSelectionOutlineVisible] = useState(true);
  const [smartSelectionLoadingRegion, setSmartSelectionLoadingRegion] = useState<SegmentSelectionRegion | null>(null);
  const [smartSelectionActiveRegion, setSmartSelectionActiveRegion] = useState<SegmentSelectionRegion | null>(null);
  const [smartSelectionMenuOpen, setSmartSelectionMenuOpen] = useState(false);
  const [wandTolerance, setWandTolerance] = useState(15);
  const [brushSize, setBrushSize] = useState(DEFAULT_BRUSH_SIZE);
  const [selectionFeatherRadius, setSelectionFeatherRadius] = useState(DEFAULT_SELECTION_FEATHER_RADIUS);
  const [featherPanelOpen, setFeatherPanelOpen] = useState(false);
  const [brushCursorPosition, setBrushCursorPosition] = useState<{ x: number; y: number } | null>(null);
  const [wandCursorPosition, setWandCursorPosition] = useState<{ x: number; y: number } | null>(null);
  const [wandCursorOverImage, setWandCursorOverImage] = useState(false);
  const [selectionShortcutMode, setSelectionShortcutMode] = useState<SelectionShortcutMode | null>(null);
  const [canvasViewportSize, setCanvasViewportSize] = useState<CanvasViewportSize>({ width: 1, height: 1 });
  const [canvasTransform, setCanvasTransform] = useState<CanvasTransform>({ scale: 1, offsetX: 0, offsetY: 0 });
  const [canvasDragging, setCanvasDragging] = useState(false);
  const [canvasPanMode, setCanvasPanMode] = useState(false);
  const [canvasShortcutPanMode, setCanvasShortcutPanMode] = useState(false);
  const [comparisonMode, setComparisonMode] = useState(false);
  const [comparisonSplit, setComparisonSplit] = useState(0.5);
  const [comparisonDragging, setComparisonDragging] = useState(false);
  const [previewSourceVersion, setPreviewSourceVersion] = useState(0);
  const [currentId, setCurrentId] = useState(initialImageId || images[0]?.id || "");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [temperature, setTemperature] = useState(0);
  const [tone, setTone] = useState(0);
  const [exposure, setExposure] = useState(0);
  const [brightness, setBrightness] = useState(0);
  const [contrast, setContrast] = useState(0);
  const [highlights, setHighlights] = useState(0);
  const [shadows, setShadows] = useState(0);
  const [vibrance, setVibrance] = useState(0);
  const [saturation, setSaturation] = useState(0);
  const [hueSaturation, setHueSaturation] = useState(() => cloneColorAdjustments(DEFAULT_ADJUSTMENTS).hueSaturation);
  const [colorBalance, setColorBalance] = useState(() => cloneColorAdjustments(DEFAULT_ADJUSTMENTS).colorBalance);
  const [activeHueChannel, setActiveHueChannel] = useState<HueChannel>("master");
  const [activeColorBalanceTone, setActiveColorBalanceTone] = useState<ColorBalanceTone>("midtones");
  const [adjustmentsByImageId, setAdjustmentsByImageId] = useState<Record<string, ColorAdjustments>>({});
  const [selectionMasksByImageId, setSelectionMasksByImageId] = useState<Record<string, SelectionMask>>({});
  const [saving, setSaving] = useState(false);
  const [savingAction, setSavingAction] = useState<"download" | "save" | null>(null);
  const [exportProgress, setExportProgress] = useState<ExportProgress | null>(null);
  const [localImages, setLocalImages] = useState<ImageEditItem[]>([]);
  const [localDropActive, setLocalDropActive] = useState(false);
  const [hasThumbnailOverflow, setHasThumbnailOverflow] = useState(false);
  const [canScrollThumbnailPrevious, setCanScrollThumbnailPrevious] = useState(false);
  const [canScrollThumbnailNext, setCanScrollThumbnailNext] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: "success" | "error" } | null>(null);
  const [imageEditorTutorial, setImageEditorTutorial] = useState<SceneTutorial | null>(null);
  const [imageEditorTutorialDeviceId, setImageEditorTutorialDeviceId] = useState("server");
  // 右侧面板 tab 切换（调色/字体/贴纸/描边圆角）
  const [rightPanelTab, setRightPanelTab] = useState<"color" | "font" | "sticker" | "stroke">("color");
  const [layerEraserActive, setLayerEraserActive] = useState(false);
  const [layerEraserPanelOpen, setLayerEraserPanelOpen] = useState(false);
  const [layerEraserSize, setLayerEraserSize] = useState(DEFAULT_LAYER_ERASER_SIZE);
  const [layerEraserPanelTop, setLayerEraserPanelTop] = useState(12);
  const [layerEraserCursorPosition, setLayerEraserCursorPosition] = useState<{ x: number; y: number } | null>(null);
  const [platformFonts, setPlatformFonts] = useState<PlatformFont[]>([]);
  const [fontUploading, setFontUploading] = useState(false);
  const [fontUploadProgress, setFontUploadProgress] = useState({ completed: 0, total: 0 });
  const [textStyle, setTextStyle] = useState<TextLayerStyle>(DEFAULT_TEXT_LAYER_STYLE);
  const [textLayers, setTextLayers] = useState<ImageEditTextLayer[]>([]);
  const [selectedTextLayerId, setSelectedTextLayerId] = useState<string | null>(null);
  const [editingTextLayerId, setEditingTextLayerId] = useState<string | null>(null);
  const [fontCursorPosition, setFontCursorPosition] = useState<{ x: number; y: number } | null>(null);
  const [eyeDropperActive, setEyeDropperActive] = useState(false);
  const [eyeDropperPreview, setEyeDropperPreview] = useState<EyeDropperPreview | null>(null);
  const [stickers, setStickers] = useState<ImageEditSticker[]>([]);
  const [stickerLayers, setStickerLayers] = useState<ImageEditStickerLayer[]>([]);
  const [layerOrder, setLayerOrder] = useState<ImageEditLayerOrderEntry[]>([]);
  const [selectedStickerLayerId, setSelectedStickerLayerId] = useState<string | null>(null);
  const stickerLayerPointerOperationRef = useRef<StickerLayerPointerOperation | null>(null);
  const textLayerPointerOperationRef = useRef<TextLayerPointerOperation | null>(null);
  const layerEraserStrokeOperationRef = useRef<LayerEraserStrokeOperation | null>(null);
  const [stickerListLoading, setStickerListLoading] = useState(false);
  const [stickerUploading, setStickerUploading] = useState(false);
  const [stickerUploadProgress, setStickerUploadProgress] = useState({ completed: 0, total: 0 });
  const [stickerError, setStickerError] = useState<string | null>(null);
  const [pendingStickerDelete, setPendingStickerDelete] = useState<ImageEditSticker | null>(null);
  const [deletingStickerId, setDeletingStickerId] = useState<string | null>(null);
  const [pendingPersonalFontDelete, setPendingPersonalFontDelete] = useState<PlatformFont | null>(null);
  const [deletingPersonalFontFamily, setDeletingPersonalFontFamily] = useState<string | null>(null);
  const stickerFileInputRef = useRef<HTMLInputElement | null>(null);
  // 描边/圆角 tab 本地 state
  const [strokeTopBottom, setStrokeTopBottom] = useState(0);
  const [strokeLeftRight, setStrokeLeftRight] = useState(0);
  const [cornerRadius, setCornerRadius] = useState(0);
  // 滑杆拖动只允许白边对称生长，不允许重置用户的平移/缩放；只有进入 tab、
  // 重开弹窗或切换布局时才重新 fit；滑杆只更新 400px 逻辑几何。
  const strokePreviewViewKeyRef = useRef<string | null>(null);
  const strokePreviewRenderedDimsRef = useRef<{
    width: number;
    height: number;
  } | null>(null);
  const strokePreviewSessionRef = useRef(0);
  const [strokePreviewBackend, setStrokePreviewBackend] = useState<ImageFramePreviewBackend>("canvas2d");
  const [strokePreviewMetrics, setStrokePreviewMetrics] = useState({ width: 0, height: 0 });
  const workerRef = useRef<Worker | null>(null);
  const pendingWorkerTasksRef = useRef<Map<string, PendingWorkerTask>>(new Map());
  // Preview work has its own worker so cancelling a stale slider frame never
  // rejects an in-progress full-quality export or Ctrl+D bake.
  const previewWorkerRef = useRef<Worker | null>(null);
  const pendingPreviewWorkerTasksRef = useRef<Map<string, PendingWorkerTask>>(new Map());
  const activePreviewWorkerTaskIdRef = useRef("");
  const activePreviewWorkerFastPathRef = useRef(false);
  const taskSequenceRef = useRef(0);
  const latestPreviewTaskIdRef = useRef("");
  const lastPreviewImageIdRef = useRef("");
  const lastPreviewViewModeRef = useRef("");
  const activeAdjustmentEditKeyRef = useRef<string | null>(null);
  const editHistoryRef = useRef<EditHistorySnapshot[]>([]);
  const redoHistoryRef = useRef<EditHistorySnapshot[]>([]);
  const [historyRevision, setHistoryRevision] = useState(0);
  const canUndoEdit = historyRevision >= 0 && editHistoryRef.current.length > 0;
  const canRedoEdit = historyRevision >= 0 && redoHistoryRef.current.length > 0;

  // 标尺和辅助线状态
  const horizontalRulerRef = useRef<HTMLCanvasElement | null>(null);
  const verticalRulerRef = useRef<HTMLCanvasElement | null>(null);
  const guidesCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const activeGuideDragRef = useRef<{
    id: string;
    type: Guide["type"];
    pointerId: number;
    startPointerClient: number;
    startPosition: number;
  } | null>(null);
  const activeRulerDragRef = useRef<Guide["type"] | null>(null);
  const rulerGuidePreviewRef = useRef<Guide | null>(null);
  const guideModifierPressedRef = useRef(false);
  const guidePointerPositionRef = useRef<{ x: number; y: number } | null>(null);
  const guidesRef = useRef<Guide[]>([]);
  const [guides, setGuides] = useState<Guide[]>([]);
  const [rulerGuidePreview, setRulerGuidePreview] = useState<Guide | null>(null);
  const [guideMoveCursorActive, setGuideMoveCursorActive] = useState(false);
  guidesRef.current = guides;

  const updateGuideMoveCursorAtPointer = useCallback((
    screenX: number,
    screenY: number,
    modifierPressed: boolean = guideModifierPressedRef.current,
  ) => {
    guidePointerPositionRef.current = { x: screenX, y: screenY };
    setGuideMoveCursorActive(shouldShowGuideMoveCursor(
      guidesRef.current,
      screenX,
      screenY,
      canvasTransformRef.current,
      modifierPressed,
      Boolean(activeGuideDragRef.current),
    ));
  }, []);

  useEffect(() => {
    if (!open) {
      guidesRef.current = [];
      setGuides([]);
      activeGuideDragRef.current = null;
      activeRulerDragRef.current = null;
      rulerGuidePreviewRef.current = null;
      setRulerGuidePreview(null);
      guideModifierPressedRef.current = false;
      guidePointerPositionRef.current = null;
      setGuideMoveCursorActive(false);
      return;
    }

    const refreshFromKeyboard = (modifierPressed: boolean) => {
      guideModifierPressedRef.current = modifierPressed;
      const pointer = guidePointerPositionRef.current;
      if (!pointer) {
        setGuideMoveCursorActive(Boolean(activeGuideDragRef.current));
        return;
      }
      updateGuideMoveCursorAtPointer(pointer.x, pointer.y, modifierPressed);
    };
    const handleGuideModifierKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Control" || event.key === "Meta") refreshFromKeyboard(true);
    };
    const handleGuideModifierKeyUp = (event: KeyboardEvent) => {
      if (event.key === "Control") refreshFromKeyboard(event.metaKey);
      if (event.key === "Meta") refreshFromKeyboard(event.ctrlKey);
    };
    const handleGuideModifierBlur = () => refreshFromKeyboard(false);

    window.addEventListener("keydown", handleGuideModifierKeyDown, true);
    window.addEventListener("keyup", handleGuideModifierKeyUp, true);
    window.addEventListener("blur", handleGuideModifierBlur);
    return () => {
      window.removeEventListener("keydown", handleGuideModifierKeyDown, true);
      window.removeEventListener("keyup", handleGuideModifierKeyUp, true);
      window.removeEventListener("blur", handleGuideModifierBlur);
      guideModifierPressedRef.current = false;
      guidePointerPositionRef.current = null;
      setGuideMoveCursorActive(false);
    };
  }, [open, updateGuideMoveCursorAtPointer]);

  const sourceImages = useMemo(() => images.filter((img) => img.url), [images]);
  const validImages = useMemo(
    () => [
      ...sourceImages,
      ...localImages.map((item, index) => ({
        ...item,
        index: sourceImages.length + index,
      })),
    ],
    [localImages, sourceImages]
  );
  const currentImage = validImages.find((img) => img.id === currentId) || validImages[0];
  const previewLayout = resolveImageEditPreviewLayoutForTab(
    previewLayoutProp,
    validImages.length,
    rightPanelTab,
  );
  const isStackPreview = previewLayout === "stack";
  const previewSourceImage = isStackPreview ? validImages[0] : currentImage;
  const stackPreviewStateRevision = isStackPreview ? historyRevision : 0;
  const updateThumbnailScrollState = useCallback(() => {
    const thumbnailViewport = thumbnailViewportRef.current;
    if (!thumbnailViewport || isStackPreview) {
      setHasThumbnailOverflow(false);
      setCanScrollThumbnailPrevious(false);
      setCanScrollThumbnailNext(false);
      return;
    }

    const maxScrollLeft = Math.max(0, thumbnailViewport.scrollWidth - thumbnailViewport.clientWidth);
    setHasThumbnailOverflow(maxScrollLeft > 1);
    setCanScrollThumbnailPrevious(thumbnailViewport.scrollLeft > 1);
    setCanScrollThumbnailNext(thumbnailViewport.scrollLeft < maxScrollLeft - 1);
  }, [isStackPreview]);
  const showThumbnailScrollControls = hasThumbnailOverflow;

  useEffect(() => {
    const thumbnailViewport = thumbnailViewportRef.current;
    if (!thumbnailViewport) return undefined;

    updateThumbnailScrollState();
    thumbnailViewport.addEventListener("scroll", updateThumbnailScrollState, { passive: true });
    const observer = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(updateThumbnailScrollState)
      : null;
    observer?.observe(thumbnailViewport);

    return () => {
      thumbnailViewport.removeEventListener("scroll", updateThumbnailScrollState);
      observer?.disconnect();
    };
  }, [open, validImages.length, updateThumbnailScrollState]);

  const scrollThumbnailViewport = (direction: -1 | 1) => {
    const thumbnailViewport = thumbnailViewportRef.current;
    if (!thumbnailViewport) return;
    thumbnailViewport.scrollBy({
      left: direction * thumbnailViewport.clientWidth,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    // Even a single-image stroke preview uses 400px logical coordinates.
    // Invalidate the fitted image when crossing that boundary so the loaded
    // preview is fitted using its own dimensions, not the previous tab's view.
    const viewMode = `${previewLayout}:${rightPanelTab === "stroke" ? "stroke" : "image"}`;
    if (lastPreviewViewModeRef.current === viewMode) return;
    lastPreviewViewModeRef.current = viewMode;
    lastPreviewImageIdRef.current = "";
    canvasZoomMultiplierRef.current = 1;
    strokeStackDirectLayoutRef.current = null;
  }, [previewLayout, rightPanelTab]);
  const activePlatformFont = platformFonts.find((font) => font.css_family === textStyle.fontFamily);
  const selectedFontLabel = getFontPickerSelectedLabel(textStyle.fontFamily, platformFonts);
  const fontWeightOptions = Array.from(new Set(
    activePlatformFont && activePlatformFont.weights.length > 0
      ? activePlatformFont.weights
      : FALLBACK_FONT_WEIGHTS,
  )).sort((a, b) => a - b);
  const regularFontWeight = fontWeightOptions.reduce((nearest, weight) => (
    Math.abs(weight - 400) < Math.abs(nearest - 400) ? weight : nearest
  ), fontWeightOptions[0] ?? 400);
  const boldFontWeight = fontWeightOptions.reduce((nearest, weight) => (
    Math.abs(weight - 700) < Math.abs(nearest - 700) ? weight : nearest
  ), fontWeightOptions[0] ?? 400);
  const currentSelectionMask = currentImage?.id ? selectionMasksByImageId[currentImage.id] : undefined;
  const previewSelectionMask = editScope === "selection" ? currentSelectionMask : undefined;
  // Hint visibility is derived from selection state so every mask-producing
  // path (wand, brush, smart selection, undo/redo restore) triggers it without
  // per-path calls. Each new selection re-shows the hint for 10s.
  const selectionActive = useMemo(
    () => editScope === "selection" && hasSelectedMaskPixels(currentSelectionMask),
    [editScope, currentSelectionMask],
  );
  const [selectionHintDismissed, setSelectionHintDismissed] = useState(false);
  useEffect(() => {
    if (!selectionActive) {
      setSelectionHintDismissed(false);
      return;
    }
    setSelectionHintDismissed(false);
    const dismissTimer = setTimeout(() => setSelectionHintDismissed(true), 10000);
    return () => clearTimeout(dismissTimer);
  }, [selectionActive]);
  const selectionHintVisible = selectionActive && !selectionHintDismissed;
  const revokeLocalImageUrls = useCallback(() => {
    localImageUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    localImageUrlsRef.current.clear();
  }, []);
  const adjustments = useMemo<ColorAdjustments>(
    () => ({
      temperature,
      tone,
      exposure,
      brightness,
      contrast,
      highlights,
      shadows,
      vibrance,
      saturation,
      hueSaturation,
      colorBalance,
    }),
    [
      brightness,
      contrast,
      exposure,
      highlights,
      saturation,
      shadows,
      temperature,
      tone,
      vibrance,
      hueSaturation,
      colorBalance,
    ]
  );

  useEffect(() => () => revokeLocalImageUrls(), [revokeLocalImageUrls]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void Promise.allSettled([listPlatformFonts(), listPersonalFonts()]).then(async ([platformResult, personalResult]) => {
      if (cancelled) return;
      const enabledFonts = platformResult.status === "fulfilled"
        ? platformResult.value.filter((font) => font.enabled && font.css_family.trim())
        : [];
      const personalFonts = personalResult.status === "fulfilled" ? personalResult.value : [];
      const fetchedPersonalFonts = await Promise.allSettled(personalFonts.map(async (font) => {
        const response = await fetch(font.file_url);
        if (!response.ok) throw new Error("font unavailable");
        const fontBuffer = await response.arrayBuffer();
        const metadata = parseFontMetadata(fontBuffer);
        return { font, metadata, fontBuffer };
      }));
      const availablePersonalFonts = fetchedPersonalFonts.flatMap((result) => (
        result.status === "fulfilled" ? [result.value] : []
      ));
      const personalGroups = groupPersonalFonts(availablePersonalFonts);
      const cssFamilyByUuid = new Map(personalGroups.flatMap((group) => (
        (group.personalFontUuids ?? []).map((uuid) => [uuid, group.css_family] as const)
      )));
      // Every UUID gets a FontFace. A same-weight DELETE can partially fail;
      // retaining its sibling face keeps the surviving weight usable.
      const registerUuids = new Set(personalGroups.flatMap((group) => (
        getPersonalFontFilesForFontFace(group).map((file) => file.uuid)
      )));
      const restoredFonts = await Promise.allSettled(availablePersonalFonts
        .filter(({ font }) => registerUuids.has(font.uuid))
        .map(async ({ font, metadata, fontBuffer }) => {
        const family = cssFamilyByUuid.get(font.uuid);
        if (!family) throw new Error("font family unavailable");
        if (typeof FontFace === "undefined") throw new Error("unsupported");
        const face = await new FontFace(
          family,
          fontBuffer,
          { weight: getFontFaceWeightDescriptor(metadata) },
        ).load();
        if (cancelled || !modalOpenRef.current) throw new Error("cancelled");
        document.fonts.add(face);
        registeredFontFacesRef.current.push({ face, personalFontUuid: font.uuid });
        return font.uuid;
      }));
      if (cancelled) return;
      const registeredPersonalFontUuids = new Set(restoredFonts.flatMap((result) => (
        result.status === "fulfilled" ? [result.value] : []
      )));
      const loadedPersonalFonts = filterPersonalFontGroupsByRegisteredUuids(
        personalGroups,
        registeredPersonalFontUuids,
      ).filter((font) => font.license_type === "personal");
      setPlatformFonts((currentFonts) => mergePersonalFontGroups([
        ...currentFonts.filter((font) => font.license_type === "personal"),
        ...loadedPersonalFonts,
        ...enabledFonts,
      ].filter((font) => font.license_type !== "personal" || font.personalFontFiles?.length)));
      if (typeof FontFace !== "undefined") {
        // 平台字体注册完成前 canvas 合成会退回系统字体,导出/描边路径通过
        // platformFontsRegisteredRef 等待注册结束后再绘制。
        platformFontsRegisteredRef.current = Promise.allSettled(enabledFonts.flatMap((font) => (
          getPlatformFontFaceEntries(font).map(async ({ url, weight }) => {
            const loaded = await new FontFace(
              font.family,
              getFontFaceSource(url),
              weight ? { weight } : undefined,
            ).load();
            if (cancelled || !modalOpenRef.current) return;
            document.fonts.add(loaded);
            registeredFontFacesRef.current.push({ face: loaded });
          })
        ))).then(() => undefined);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || rightPanelTab !== "sticker") return;
    let cancelled = false;
    setStickerListLoading(true);
    setStickerError(null);
    void imageEditApi.listStickers()
      .then((items) => {
        if (!cancelled) setStickers(items);
      })
      .catch(() => {
        if (!cancelled) setStickerError("E1005 贴纸加载失败，请稍后重试");
      })
      .finally(() => {
        if (!cancelled) setStickerListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, rightPanelTab]);

  const handleStickerUpload = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const selectedFiles = Array.from(input.files ?? []);
    input.value = "";
    if (selectedFiles.length === 0 || stickerUploading) return;

    const allowedTypes = new Set(["image/jpeg", "image/png", "image/webp"]);
    const files = selectedFiles.filter((file) => (
      allowedTypes.has(file.type) && /\.(jpe?g|png|webp)$/i.test(file.name)
    ));
    let failed = files.length !== selectedFiles.length;
    if (files.length === 0) {
      setStickerError("E1013 仅支持 JPG、PNG、WebP 格式");
      return;
    }

    setStickerUploading(true);
    setStickerError(null);
    setStickerUploadProgress({ completed: 0, total: files.length });
    const uploaded: ImageEditSticker[] = [];
    for (const file of files) {
      try {
        uploaded.push(await imageEditApi.uploadSticker(file));
      } catch {
        failed = true;
      } finally {
        setStickerUploadProgress((progress) => ({ ...progress, completed: progress.completed + 1 }));
      }
    }
    if (uploaded.length > 0) {
      setStickers((current) => [...uploaded, ...current]);
    }
    if (failed) {
      setStickerError("E5003 部分贴纸上传失败，请稍后重试");
    }
    setStickerUploading(false);
  }, [stickerUploading]);

  const handleConfirmStickerDelete = useCallback(async () => {
    if (!pendingStickerDelete || deletingStickerId) return;
    const uuid = pendingStickerDelete.uuid;
    setDeletingStickerId(uuid);
    setStickerError(null);
    try {
      await imageEditApi.deleteSticker(uuid);
      setStickers((items) => items.filter((item) => item.uuid !== uuid));
      setPendingStickerDelete(null);
    } catch {
      setStickerError("E9003 贴纸删除失败，请稍后重试");
    } finally {
      setDeletingStickerId(null);
    }
  }, [deletingStickerId, pendingStickerDelete]);

  useEffect(() => {
    if (!editingTextLayerId) return;
    requestAnimationFrame(() => {
      activeTextAreaRef.current?.focus();
      activeTextAreaRef.current?.setSelectionRange(
        activeTextAreaRef.current.value.length,
        activeTextAreaRef.current.value.length,
      );
    });
  }, [editingTextLayerId]);

  const updateTextStyle = useCallback((patch: Partial<TextLayerStyle>) => {
    setTextStyle((current) => ({ ...current, ...patch }));
    if (!selectedTextLayerId) return;
    setTextLayers((layers) => layers.map((layer) => {
      if (layer.id !== selectedTextLayerId) return layer;
      const style = { ...layer.style, ...patch };
      // A bigger font (or taller line-height) must stretch the layer box, not
      // stay clipped at the height measured for the previous style. Only grow:
      // a manual shrink via the resize handles stays authoritative.
      return {
        ...layer,
        style,
        height: Math.max(
          layer.height,
          MIN_TEXT_LAYER_SIZE,
          measureTextLayerFittedHeight(style, layer.text, layer.width),
        ),
      };
    }));
  }, [selectedTextLayerId]);

  const releaseCustomFonts = useCallback(() => {
    customFontUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    customFontUrlsRef.current = [];
    registeredFontFacesRef.current.forEach(({ face }) => document.fonts.delete(face));
    registeredFontFacesRef.current = [];
    setPlatformFonts((fonts) => fonts.filter((font) => (
      font.license_type !== "custom" && font.license_type !== "personal"
    )));
  }, []);

  const importPersonalFontFile = useCallback(async (file: File, isRunCurrent: () => boolean) => {
    if (!/\.(ttf|otf|woff2?)$/i.test(file.name) || file.size > MAX_PERSONAL_FONT_FILE_BYTES) {
      throw new Error("invalid font file");
    }
    let uploadedFont: PersonalFont | null = null;
    let fontLoaded = false;
    try {
      if (typeof FontFace === "undefined") throw new Error("unsupported");
      const fontBuffer = await file.arrayBuffer();
      const metadata: FontMetadata = parseFontMetadata(fontBuffer);
      // Verify with the same browser FontFace implementation before creating a
      // server record. The backend intentionally accepts browser-tolerant font
      // containers, but a malformed local file must not look "uploaded" and
      // then trigger a compensating DELETE after the user sees a success call.
      await new FontFace(
        "FlowXPersonalPreflight",
        fontBuffer.slice(0),
        { weight: getFontFaceWeightDescriptor(metadata) },
      ).load();
      uploadedFont = await uploadPersonalFont(file);
      // A request that began before close may still persist its server record,
      // but it must never register into a newer modal session after reopen.
      if (!isRunCurrent()) return null;
      const family = getPersonalFontCssFamily(metadata, uploadedFont.uuid);
      const loadedFace = await new FontFace(
        family,
        fontBuffer.slice(0),
        { weight: getFontFaceWeightDescriptor(metadata) },
      ).load();
      fontLoaded = true;
      if (!isRunCurrent()) return null;
      document.fonts.add(loadedFace);
      registeredFontFacesRef.current.push({ face: loadedFace, personalFontUuid: uploadedFont.uuid });
      return { font: uploadedFont, metadata };
    } catch {
      if (uploadedFont && !fontLoaded && isRunCurrent()) {
        try {
          await deletePersonalFont(uploadedFont.uuid);
        } catch {
          // The user-facing error remains generic even if best-effort rollback fails.
        }
      }
      throw new Error("font import failed");
    }
  }, []);

  const handleImportCustomFonts = useCallback(async (files: File[]) => {
    if (fontUploadBusyRef.current || files.length === 0) return;
    const plan = planFontUploadBatches(files);
    const runId = fontUploadRunRef.current.start();
    const isRunCurrent = () => (
      modalOpenRef.current && fontUploadRunRef.current.isCurrent(runId)
    );
    fontUploadBusyRef.current = true;
    setFontUploading(true);
    setFontUploadProgress({ completed: plan.rejected.length, total: files.length });
    setMessage(null);
    try {
      const execution = await executeFontUploadBatches(
        plan.batches,
        (file) => importPersonalFontFile(file, isRunCurrent),
        {
          shouldContinue: isRunCurrent,
          onSettled: () => {
            if (!isRunCurrent()) return;
            setFontUploadProgress((progress) => ({
              ...progress,
              completed: progress.completed + 1,
            }));
          },
        },
      );
      if (!isRunCurrent()) return;

      const importedFonts = execution.successes.flatMap(({ result }) => result ? [result] : []);
      if (importedFonts.length > 0) {
        const customFonts = importedFonts.map(({ font, metadata }) => groupPersonalFonts([{ font, metadata }])[0]);
        const nextFonts = mergePersonalFontGroups([...platformFonts, ...customFonts]);
        setPlatformFonts((fonts) => mergePersonalFontGroups([...fonts, ...customFonts]));
        const lastImported = importedFonts[importedFonts.length - 1];
        const family = getPersonalFontCssFamily(lastImported.metadata, lastImported.font.uuid);
        const selectedFont = nextFonts.find((font) => font.css_family === family);
        const weights = selectedFont?.weights.length ? selectedFont.weights : [400];
        const selectedWeight = weights.reduce((nearest, weight) => (
          Math.abs(weight - 400) < Math.abs(nearest - 400) ? weight : nearest
        ), weights[0]);
        updateTextStyle({ fontFamily: family, fontWeight: selectedWeight });
      }
      const failedCount = plan.rejected.length + execution.failures.length;
      setMessage(failedCount > 0
        ? { text: "E8108 部分字体导入失败，请检查字体格式或大小", type: "error" }
        : null);
    } finally {
      if (!isRunCurrent()) return;
      fontUploadBusyRef.current = false;
      setFontUploading(false);
    }
  }, [importPersonalFontFile, platformFonts, updateTextStyle]);

  const handleConfirmPersonalFontDelete = useCallback(async () => {
    if (!pendingPersonalFontDelete || deletingPersonalFontFamily) return;
    const uuids = pendingPersonalFontDelete.personalFontUuids
      ?? (pendingPersonalFontDelete.personalFontUuid ? [pendingPersonalFontDelete.personalFontUuid] : []);
    if (!uuids.length) return;
    setDeletingPersonalFontFamily(pendingPersonalFontDelete.css_family);
    const results = await Promise.allSettled(uuids.map((uuid) => deletePersonalFont(uuid)));
    const deletedUuids = results.flatMap((result, index) => result.status === "fulfilled" ? [uuids[index]] : []);
    if (deletedUuids.length > 0) {
      const deleted = new Set(deletedUuids);
      registeredFontFacesRef.current = registeredFontFacesRef.current.filter((entry) => {
        if (!entry.personalFontUuid || !deleted.has(entry.personalFontUuid)) return true;
        document.fonts.delete(entry.face);
        return false;
      });
      const nextFonts = removeDeletedPersonalFontFiles(platformFonts, deletedUuids);
      setPlatformFonts(nextFonts);
      if (textStyle.fontFamily === pendingPersonalFontDelete.css_family) {
        const remaining = nextFonts.find((font) => font.css_family === textStyle.fontFamily);
        if (remaining) {
          const weights = remaining.weights.length ? remaining.weights : [400];
          const nextWeight = weights.includes(textStyle.fontWeight)
            ? textStyle.fontWeight
            : weights.reduce((nearest, weight) => (
              Math.abs(weight - textStyle.fontWeight) < Math.abs(nearest - textStyle.fontWeight)
                ? weight
                : nearest
            ), weights[0]);
          updateTextStyle({ fontWeight: nextWeight });
        } else {
          updateTextStyle({
            fontFamily: DEFAULT_TEXT_LAYER_STYLE.fontFamily,
            fontWeight: DEFAULT_TEXT_LAYER_STYLE.fontWeight,
          });
        }
      }
    }
    if (deletedUuids.length !== uuids.length) {
      setMessage({ text: "E8108 字体操作失败，请稍后重试", type: "error" });
    }
    setPendingPersonalFontDelete(null);
    setDeletingPersonalFontFamily(null);
  }, [deletingPersonalFontFamily, pendingPersonalFontDelete, platformFonts, textStyle, updateTextStyle]);

  useEffect(() => {
    if (!pendingPersonalFontDelete || deletingPersonalFontFamily) return;
    const focusFrame = requestAnimationFrame(() => personalFontDeleteCancelRef.current?.focus());
    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setPendingPersonalFontDelete(null);
    };
    document.addEventListener("keydown", handleEscape);
    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [deletingPersonalFontFamily, pendingPersonalFontDelete]);

  useEffect(() => () => releaseCustomFonts(), [releaseCustomFonts]);

  useEffect(() => {
    const uploadRunSession = fontUploadRunRef.current;
    modalOpenRef.current = open;
    if (!open) {
      uploadRunSession.invalidate();
      fontUploadBusyRef.current = false;
    }
    return () => {
      modalOpenRef.current = false;
      uploadRunSession.invalidate();
      fontUploadBusyRef.current = false;
    };
  }, [open]);

  useEffect(() => {
    setImageEditorTutorial(null);
    if (!open) return;

    let cancelled = false;
    void tutorialApi.getTutorial(IMAGE_EDITOR_TUTORIAL_SCENE_KEY)
      .then((tutorial) => {
        if (!cancelled) setImageEditorTutorial(tutorial);
      })
      .catch(() => {
        if (!cancelled) setImageEditorTutorial(null);
      });

    return () => {
      cancelled = true;
    };
  }, [open, tutorialApi]);

  useEffect(() => {
    setImageEditorTutorialDeviceId(getEcomHelpTipDeviceId());
  }, []);

  useEffect(() => {
    if (!open) releaseCustomFonts();
  }, [open, releaseCustomFonts]);

  const selectTextLayer = useCallback((layer: ImageEditTextLayer, edit = false) => {
    setCurrentId(layer.imageId);
    setSelectedTextLayerId(layer.id);
    setSelectedStickerLayerId(null);
    setEditingTextLayerId(edit ? layer.id : null);
    setTextStyle(layer.style);
    // 普通选中保留当前标签页（贴纸/字体选中交互互通），仅进入文字编辑态时切到字体面板。
    if (edit) setRightPanelTab("font");
  }, []);

  const finishTextLayerEditing = useCallback((layerId: string | null) => {
    if (!layerId) return;
    const activeLayer = textLayers.find((layer) => layer.id === layerId);
    setEditingTextLayerId((current) => current === layerId ? null : current);
    if (activeLayer?.text.trim()) return;
    setTextLayers((layers) => layers.filter((layer) => layer.id !== layerId));
    setLayerOrder((order) => order.filter((layer) => layer.id !== layerId));
    setSelectedTextLayerId((current) => current === layerId ? null : current);
  }, [textLayers]);

  const handleLayerEraserToggle = useCallback((panelTop?: number) => {
    finishTextLayerEditing(editingTextLayerId);
    setEditingTextLayerId(null);
    setSelectedTextLayerId(null);
    setSelectedStickerLayerId(null);
    setEyeDropperActive(false);
    setEyeDropperPreview(null);
    setSelectionPanelOpen(false);
    setSelectionTool(null);
    setSmartSelectionMenuOpen(false);
    setFeatherPanelOpen(false);
    setBrushCursorPosition(null);
    setWandCursorPosition(null);
    setWandCursorOverImage(false);
    if (typeof panelTop === "number") setLayerEraserPanelTop(panelTop);
    setLayerEraserActive((active) => {
      const nextActive = !active;
      setLayerEraserPanelOpen(nextActive);
      if (!nextActive) setLayerEraserCursorPosition(null);
      return nextActive;
    });
  }, [editingTextLayerId, finishTextLayerEditing]);

  useEffect(() => {
    if (rightPanelTab === "font") return;
    finishTextLayerEditing(editingTextLayerId);
    setEyeDropperActive(false);
    setEyeDropperPreview(null);
  }, [editingTextLayerId, finishTextLayerEditing, rightPanelTab]);

  useEffect(() => {
    if (!open || !localFolderInputRef.current) return;
    localFolderInputRef.current.setAttribute("webkitdirectory", "");
    localFolderInputRef.current.setAttribute("directory", "");
  }, [open]);

  useEffect(() => {
    if (open) return;
    revokeLocalImageUrls();
    setLocalImages([]);
    setLocalDropActive(false);
    localUploadDragDepthRef.current = 0;
    localSmartSelectionUploadPromisesRef.current = {};
    if (localFileInputRef.current) {
      localFileInputRef.current.value = "";
    }
    if (localFolderInputRef.current) {
      localFolderInputRef.current.value = "";
    }
    setStickers([]);
    setStickerError(null);
    setPendingStickerDelete(null);
    setFontUploading(false);
    setFontUploadProgress({ completed: 0, total: 0 });
  }, [open, revokeLocalImageUrls]);

  useEffect(() => {
    adjustmentsByImageIdRef.current = adjustmentsByImageId;
  }, [adjustmentsByImageId]);

  useEffect(() => {
    selectionMasksByImageIdRef.current = selectionMasksByImageId;
  }, [selectionMasksByImageId]);

  useEffect(() => {
    canvasTransformRef.current = canvasTransform;
  }, [canvasTransform]);

  useEffect(() => {
    canvasViewportSizeRef.current = canvasViewportSize;
  }, [canvasViewportSize]);

  useEffect(() => {
    currentSelectionMaskRef.current = currentSelectionMask;
  }, [currentSelectionMask]);

  useEffect(() => {
    if (!currentSelectionMask && !selectionOutlineVisible) {
      setSelectionOutlineVisible(true);
    }
  }, [currentSelectionMask, selectionOutlineVisible]);

  useEffect(() => {
    editScopeRef.current = editScope;
  }, [editScope]);

  const getSelectionOutlinePath = useCallback((
    mask: SelectionMask | undefined,
    targetWidth: number,
    targetHeight: number
  ) => {
    if (!mask) return null;
    const cache = selectionOutlinePathCacheRef.current;
    const maskVersion = mask.version ?? 0;
    const displayVersion = getSelectionOutlineDisplayVersion(mask);
    if (
      cache &&
      cache.mask === mask &&
      cache.maskVersion === maskVersion &&
      cache.displayVersion === displayVersion &&
      cache.targetWidth === targetWidth &&
      cache.targetHeight === targetHeight
    ) {
      return cache.path;
    }

    const path = createSelectionOutlinePath(mask, targetWidth, targetHeight);
    if (path) {
      selectionOutlinePathCacheRef.current = {
        mask,
        maskVersion,
        displayVersion,
        targetWidth,
        targetHeight,
        path,
      };
    }
    return path;
  }, []);

  const drawCanvasPreview = useCallback(() => {
    const canvas = canvasRef.current;
    const renderedPreview = renderedPreviewImageDataRef.current;
    const previewBuffer = previewBufferCanvasRef.current;
    const originalPreviewBuffer = originalPreviewBufferCanvasRef.current;
    const directStrokeLayout = rightPanelTab === "stroke" ? strokeStackDirectLayoutRef.current : null;
    const directStrokeSources = directStrokeLayout ? strokePreviewSourcesRef.current : [];
    if (!canvas || !renderedPreview || (!previewBuffer && directStrokeSources.length === 0)) return;

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;

    const pixelRatio = canvasViewportSize.width > 0 ? canvas.width / canvasViewportSize.width : 1;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.save();
    ctx.translate(canvasTransformRef.current.offsetX, canvasTransformRef.current.offsetY);
    ctx.scale(canvasTransformRef.current.scale, canvasTransformRef.current.scale);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = rightPanelTab === "stroke" ? "high" : "medium";
    if (directStrokeLayout && directStrokeSources.length > 0) {
      drawStrokeStackPreviewDirect(
        ctx,
        directStrokeSources,
        directStrokeLayout,
        canvasTransformRef.current,
        canvasViewportSizeRef.current,
      );
      ctx.restore();
      return;
    }
    if (!previewBuffer) {
      ctx.restore();
      return;
    }
    const sharpPreviewBuffer = previewBuffer;
    const canCompare = Boolean(
      comparisonModeRef.current &&
        originalPreviewBuffer &&
        originalPreviewBuffer.width > 0 &&
        originalPreviewBuffer.height > 0
    );

    if (canCompare && originalPreviewBuffer) {
      const sharpOriginalBuffer = originalPreviewBuffer;
      const splitX = Math.min(
        renderedPreview.width,
        Math.max(0, renderedPreview.width * comparisonSplitRef.current)
      );
      ctx.drawImage(sharpOriginalBuffer, 0, 0, renderedPreview.width, renderedPreview.height);
      ctx.save();
      ctx.beginPath();
      ctx.rect(splitX, 0, renderedPreview.width - splitX, renderedPreview.height);
      ctx.clip();
      ctx.drawImage(sharpPreviewBuffer, 0, 0, renderedPreview.width, renderedPreview.height);
      ctx.restore();

      const lineWidth = Math.max(1 / Math.max(canvasTransformRef.current.scale, 0.01), 0.75);
      ctx.save();
      ctx.lineCap = "round";
      ctx.strokeStyle = "rgba(255,255,255,0.92)";
      ctx.lineWidth = lineWidth;
      ctx.setLineDash([lineWidth * 6, lineWidth * 4]);
      ctx.beginPath();
      ctx.moveTo(splitX, 0);
      ctx.lineTo(splitX, renderedPreview.height);
      ctx.stroke();
      ctx.restore();
    } else {
      ctx.drawImage(sharpPreviewBuffer, 0, 0, renderedPreview.width, renderedPreview.height);
    }
    ctx.restore();
    if (canCompare && originalPreviewBuffer) {
      drawComparisonLabels(
        ctx,
        renderedPreview,
        canvasTransformRef.current,
        comparisonSplitRef.current,
        canvasViewportSizeRef.current
      );
    }
  }, [canvasViewportSize.width, rightPanelTab]);

  const drawSelectionOverlay = useCallback((dashOffset = selectionDashOffsetRef.current) => {
    const overlay = selectionOverlayFabricRef.current;
    if (!overlay) return;

    const viewport = canvasViewportSizeRef.current;
    const width = Math.max(1, Math.round(viewport.width));
    const height = Math.max(1, Math.round(viewport.height));
    if (overlay.width !== width || overlay.height !== height) {
      overlay.canvas.setDimensions({ width, height });
      overlay.width = width;
      overlay.height = height;
    }

    const transform = canvasTransformRef.current;
    const safeDisplayScale = Math.max(0.1, transform.scale);
    overlay.canvas.setViewportTransform([
      transform.scale,
      0,
      0,
      transform.scale,
      transform.offsetX,
      transform.offsetY,
    ]);

    const renderedPreview = renderedPreviewImageDataRef.current;
    const canShowOutline =
      Boolean(renderedPreview) &&
      editScopeRef.current === "selection" &&
      selectionOutlineVisibleRef.current;
    const activeStackFrame = stackPreviewLayoutRef.current?.frames.find(
      (frame) => frame.id === basePreviewImageIdRef.current,
    );
    const localOutlinePathData = canShowOutline && renderedPreview
      ? getSelectionOutlinePath(
          currentSelectionMaskRef.current,
          activeStackFrame?.width ?? renderedPreview.width,
          activeStackFrame?.height ?? renderedPreview.height,
        )
      : null;
    const outlinePathData = localOutlinePathData && activeStackFrame
      ? translateSvgPathData(localOutlinePathData, activeStackFrame.x, activeStackFrame.y)
      : localOutlinePathData;
    const outlineStrokeWidth = SELECTION_OUTLINE_STROKE_WIDTH / safeDisplayScale;
    const outlineDashArray = [
      SELECTION_DASH_SEGMENT / safeDisplayScale,
      SELECTION_DASH_GAP / safeDisplayScale,
    ];
    const outlineDashOffset = dashOffset / safeDisplayScale;

    if (!outlinePathData) {
      removeFabricPath(overlay.canvas, overlay.outlineBasePath);
      removeFabricPath(overlay.canvas, overlay.outlineDashPath);
      overlay.outlinePathData = null;
      overlay.outlineBasePath = null;
      overlay.outlineDashPath = null;
    } else if (
      overlay.outlinePathData !== outlinePathData ||
      !overlay.outlineBasePath ||
      !overlay.outlineDashPath
    ) {
      removeFabricPath(overlay.canvas, overlay.outlineBasePath);
      removeFabricPath(overlay.canvas, overlay.outlineDashPath);
      overlay.outlinePathData = outlinePathData;
      overlay.outlineBasePath = createFabricOverlayPath(overlay.fabric, outlinePathData, {
        stroke: SELECTION_OUTLINE_BASE_COLOR,
        strokeWidth: outlineStrokeWidth,
        strokeLineCap: "round",
      });
      overlay.outlineDashPath = createFabricOverlayPath(overlay.fabric, outlinePathData, {
        stroke: SELECTION_OUTLINE_DASH_COLOR,
        strokeWidth: outlineStrokeWidth,
        strokeDashArray: outlineDashArray,
        strokeDashOffset: outlineDashOffset,
        strokeLineCap: "round",
      });
      overlay.canvas.add(overlay.outlineBasePath, overlay.outlineDashPath);
    } else {
      overlay.outlineBasePath.set({
        stroke: SELECTION_OUTLINE_BASE_COLOR,
        strokeWidth: outlineStrokeWidth,
        strokeLineCap: "round",
      });
      overlay.outlineDashPath.set({
        stroke: SELECTION_OUTLINE_DASH_COLOR,
        strokeWidth: outlineStrokeWidth,
        strokeDashArray: outlineDashArray,
        strokeDashOffset: outlineDashOffset,
        strokeLineCap: "round",
      });
    }

    const activeBrushStroke = brushStrokeRef.current?.active ? brushStrokeRef.current : null;
    const localBrushPathData = activeBrushStroke?.previewPathData ?? null;
    const brushPathData = localBrushPathData && activeStackFrame
      ? translateSvgPathData(localBrushPathData, activeStackFrame.x, activeStackFrame.y)
      : localBrushPathData;
    // 减去模式只保留半透明白色笔迹预览，不再叠一根细黑中线。
    if (!brushPathData) {
      removeFabricPath(overlay.canvas, overlay.brushPath);
      overlay.brushPathData = null;
      overlay.brushPath = null;
    } else if (overlay.brushPathData !== brushPathData || !overlay.brushPath) {
      removeFabricPath(overlay.canvas, overlay.brushPath);
      overlay.brushPathData = brushPathData;
      overlay.brushPath = createFabricOverlayPath(overlay.fabric, brushPathData, {
        stroke: activeBrushStroke?.erase ? "rgba(255,255,255,0.6)" : "rgba(239,68,68,0.5)",
        strokeWidth: activeBrushStroke?.previewLineWidth ?? 1,
        strokeLineCap: "round",
      });
      overlay.canvas.add(overlay.brushPath);
    } else {
      overlay.brushPath.set({
        stroke: activeBrushStroke?.erase ? "rgba(255,255,255,0.6)" : "rgba(239,68,68,0.5)",
        strokeWidth: activeBrushStroke?.previewLineWidth ?? 1,
      });
    }

    overlay.canvas.requestRenderAll();
  }, [getSelectionOutlinePath]);

  const scheduleCanvasRedraw = useCallback(() => {
    if (canvasRedrawFrameRef.current !== null) return;

    canvasRedrawFrameRef.current = requestAnimationFrame(() => {
      canvasRedrawFrameRef.current = null;
      drawCanvasPreview();
      drawSelectionOverlay();
    });
  }, [drawCanvasPreview, drawSelectionOverlay]);

  const scheduleCursorStateUpdate = useCallback(() => {
    if (cursorUpdateFrameRef.current !== null) return;

    cursorUpdateFrameRef.current = requestAnimationFrame(() => {
      cursorUpdateFrameRef.current = null;
      setBrushCursorPosition((current) => {
        const next = pendingBrushCursorRef.current;
        if (!next) return current === null ? current : null;
        if (current && current.x === next.x && current.y === next.y) return current;
        return next;
      });
      setWandCursorPosition((current) => {
        const next = pendingWandCursorRef.current;
        if (!next) return current === null ? current : null;
        if (current && current.x === next.x && current.y === next.y) return current;
        return { x: next.x, y: next.y };
      });
      setWandCursorOverImage((current) => {
        const next = pendingWandCursorRef.current?.overImage ?? false;
        return current === next ? current : next;
      });
    });
  }, []);

  const clearCanvasCursorState = useCallback((options?: { brush?: boolean; wand?: boolean }) => {
    const clearBrush = options?.brush ?? true;
    const clearWand = options?.wand ?? true;
    if (clearBrush) pendingBrushCursorRef.current = null;
    if (clearWand) pendingWandCursorRef.current = null;
    scheduleCursorStateUpdate();
  }, [scheduleCursorStateUpdate]);

  useEffect(() => {
    if (!open) return;
    const canvasElement = selectionOverlayCanvasRef.current;
    if (!canvasElement) return;

    let cancelled = false;
    let createdOverlay: SelectionOverlayFabricState | null = null;

    import("fabric").then((fabric) => {
      if (cancelled || selectionOverlayCanvasRef.current !== canvasElement) return;

      const viewport = canvasViewportSizeRef.current;
      const width = Math.max(1, Math.round(viewport.width));
      const height = Math.max(1, Math.round(viewport.height));
      const fabricCanvas = new fabric.StaticCanvas(canvasElement, {
        width,
        height,
        renderOnAddRemove: false,
        enableRetinaScaling: true,
      });

      createdOverlay = {
        fabric,
        canvas: fabricCanvas,
        width,
        height,
        outlinePathData: null,
        outlineBasePath: null,
        outlineDashPath: null,
        brushPathData: null,
        brushPath: null,
      };
      selectionOverlayFabricRef.current = createdOverlay;
      drawSelectionOverlay();
    });

    return () => {
      cancelled = true;
      if (createdOverlay && selectionOverlayFabricRef.current === createdOverlay) {
        selectionOverlayFabricRef.current = null;
        void createdOverlay.canvas.dispose();
      }
    };
  }, [drawSelectionOverlay, open]);

  useEffect(() => {
    selectionOutlineVisibleRef.current = selectionOutlineVisible;
    drawSelectionOverlay();
  }, [drawSelectionOverlay, selectionOutlineVisible]);

  useEffect(() => {
    drawSelectionOverlay();
  }, [currentSelectionMask, drawSelectionOverlay, editScope]);

  const scheduleBrushPreview = useCallback(() => {
    if (brushPreviewFrameRef.current !== null) return;

    brushPreviewFrameRef.current = requestAnimationFrame(() => {
      brushPreviewFrameRef.current = null;
      drawSelectionOverlay();
    });
  }, [drawSelectionOverlay]);

  const resetCanvasToFit = useCallback(() => {
    canvasZoomMultiplierRef.current = 1;
    setCanvasTransform(
      rightPanelTab === "stroke"
        ? getStrokePreviewFitTransform(renderedPreviewImageDataRef.current, canvasViewportSize)
        : getFitCanvasTransform(renderedPreviewImageDataRef.current, canvasViewportSize),
    );
  }, [canvasViewportSize, rightPanelTab]);

  const updateCanvasZoom = useCallback((nextScale: number, anchor?: { x: number; y: number }) => {
    const imageData = renderedPreviewImageDataRef.current;
    if (!imageData) return;

    const fitScale = rightPanelTab === "stroke"
      ? getStrokePreviewFitTransform(imageData, canvasViewportSize).scale
      : getFitCanvasTransform(imageData, canvasViewportSize).scale;
    const minimumScale = Math.min(MIN_CANVAS_ZOOM, fitScale);
    const scale = clampZoom(nextScale, minimumScale);
    canvasZoomMultiplierRef.current = scale / Math.max(fitScale, 0.01);

    setCanvasTransform((prev) => {
      const anchorPoint = anchor || {
        x: canvasViewportSize.width / 2,
        y: canvasViewportSize.height / 2,
      };
      const imageX = (anchorPoint.x - prev.offsetX) / prev.scale;
      const imageY = (anchorPoint.y - prev.offsetY) / prev.scale;
      return {
        scale,
        offsetX: anchorPoint.x - imageX * scale,
        offsetY: anchorPoint.y - imageY * scale,
      };
    });
  }, [canvasViewportSize, rightPanelTab]);

  const updateStrokeFrameValue = useCallback((
    key: "topBottom" | "leftRight" | "cornerRadius",
    setValue: (value: number) => void,
    nextValue: number | null,
  ) => {
    // Source canvases stay cached; each control event only replaces geometry
    // values and coalesces one lightweight direct-compositor animation frame.
    const nextStrokeValues = { ...sliderValuesRef.current, [key]: clampImageFrameControlValue(nextValue) };
    sliderValuesRef.current = nextStrokeValues;
    setValue(nextStrokeValues[key]);
    requestStrokePreviewFrameRef.current();
  }, []);

  const applyAdjustmentsToControls = useCallback((nextAdjustments: ColorAdjustments) => {
    const normalizedAdjustments = normalizeAdjustments(nextAdjustments);
    liveAdjustmentsRef.current = normalizedAdjustments;
    setTemperature(normalizedAdjustments.temperature);
    setTone(normalizedAdjustments.tone);
    setExposure(normalizedAdjustments.exposure);
    setBrightness(normalizedAdjustments.brightness);
    setContrast(normalizedAdjustments.contrast);
    setHighlights(normalizedAdjustments.highlights);
    setShadows(normalizedAdjustments.shadows);
    setVibrance(normalizedAdjustments.vibrance);
    setSaturation(normalizedAdjustments.saturation);
    setHueSaturation(normalizedAdjustments.hueSaturation);
    setColorBalance(normalizedAdjustments.colorBalance);
  }, []);

  const createEditSnapshot = useCallback((): EditHistorySnapshot => ({
    currentId,
    editScope,
    selectionCombineMode,
    selectionTool,
    wandTolerance,
    brushSize,
    adjustmentsByImageId: cloneAdjustmentsByImageId(adjustmentsByImageIdRef.current),
    selectionMasksByImageId: cloneSelectionMasks(selectionMasksByImageIdRef.current),
    bakedPreviewImageDataByImageId: cloneImageDataByImageId(bakedPreviewImageDataByImageIdRef.current),
    bakedEditOperationsByImageId: cloneBakedEditOperations(bakedEditOperationsByImageIdRef.current),
    textLayers: cloneTextLayers(textLayers),
    stickerLayers: cloneStickerLayers(stickerLayers),
  }), [brushSize, currentId, editScope, selectionCombineMode, selectionTool, stickerLayers, textLayers, wandTolerance]);

  const pushEditHistory = useCallback(() => {
    const snapshot = createEditSnapshot();
    editHistoryRef.current = [...editHistoryRef.current.slice(-(MAX_EDIT_HISTORY - 1)), snapshot];
    redoHistoryRef.current = [];
    setHistoryRevision((revision) => revision + 1);
  }, [createEditSnapshot]);

  const restoreEditSnapshot = useCallback((snapshot: EditHistorySnapshot) => {
    setCurrentId(snapshot.currentId);
    setEditScope(snapshot.editScope);
    setSelectionCombineMode(normalizeSelectionCombineMode(snapshot.selectionCombineMode));
    setSelectionTool(snapshot.selectionTool);
    setWandTolerance(snapshot.wandTolerance);
    setBrushSize(snapshot.brushSize);
    const nextAdjustmentsByImageId = cloneAdjustmentsByImageId(snapshot.adjustmentsByImageId);
    const currentAdjustments = nextAdjustmentsByImageId[snapshot.currentId] || cloneColorAdjustments(DEFAULT_ADJUSTMENTS);
    activeAdjustmentEditKeyRef.current = null;
    workerMaskCacheRef.current = null;
    bakedPreviewImageDataByImageIdRef.current = cloneImageDataByImageId(snapshot.bakedPreviewImageDataByImageId);
    bakedEditOperationsByImageIdRef.current = cloneBakedEditOperations(snapshot.bakedEditOperationsByImageId);
    adjustmentsByImageIdRef.current = nextAdjustmentsByImageId;
    selectionMasksByImageIdRef.current = cloneSelectionMasks(snapshot.selectionMasksByImageId);
    setTextLayers(cloneTextLayers(snapshot.textLayers));
    setStickerLayers(cloneStickerLayers(snapshot.stickerLayers));
    setAdjustmentsByImageId(nextAdjustmentsByImageId);
    applyAdjustmentsToControls(currentAdjustments);
    setSelectionMasksByImageId(selectionMasksByImageIdRef.current);
    setSmartSelectionActiveRegion(null);
    setMessage(null);

    // If the restored image is already loaded, swap its preview source now.
    // The preview effect is also bumped so the worker redraws actual pixels,
    // not merely the right-hand control values.
    if (basePreviewImageIdRef.current === snapshot.currentId) {
      const restoredBase =
        bakedPreviewImageDataByImageIdRef.current[snapshot.currentId] ||
        sourcePreviewImageDataByImageIdRef.current[snapshot.currentId];
      if (restoredBase) {
        basePreviewImageDataRef.current = cloneImageDataValue(restoredBase);
        draftPreviewImageDataRef.current = cloneImageDataValue(restoredBase);
        setPreviewSourceVersion((version) => version + 1);
      }
    }
  }, [applyAdjustmentsToControls]);

  const undoLastEdit = useCallback(() => {
    const previousSnapshot = editHistoryRef.current.pop();
    if (!previousSnapshot) return;
    const currentSnapshot = createEditSnapshot();
    redoHistoryRef.current = [...redoHistoryRef.current.slice(-(MAX_EDIT_HISTORY - 1)), currentSnapshot];
    restoreEditSnapshot(previousSnapshot);
    setHistoryRevision((revision) => revision + 1);
  }, [createEditSnapshot, restoreEditSnapshot]);

  const redoLastEdit = useCallback(() => {
    const nextSnapshot = redoHistoryRef.current.pop();
    if (!nextSnapshot) return;
    const currentSnapshot = createEditSnapshot();
    editHistoryRef.current = [...editHistoryRef.current.slice(-(MAX_EDIT_HISTORY - 1)), currentSnapshot];
    restoreEditSnapshot(nextSnapshot);
    setHistoryRevision((revision) => revision + 1);
  }, [createEditSnapshot, restoreEditSnapshot]);

  const getImageAdjustments = useCallback(
    (imageId: string): ColorAdjustments => normalizeAdjustments(adjustmentsByImageIdRef.current[imageId]),
    []
  );

  const updateCurrentAdjustment = useCallback(
    (key: keyof ColorAdjustments, value: number) => {
      const nextValue = clampAdjustment(value);
      const currentAdjustments = normalizeAdjustments(liveAdjustmentsRef.current);
      if (currentAdjustments[key] === nextValue) return;
      if (activeAdjustmentEditKeyRef.current !== key) {
        pushEditHistory();
        activeAdjustmentEditKeyRef.current = key;
      }
      const nextAdjustments = normalizeAdjustments({ ...currentAdjustments, [key]: nextValue });
      liveAdjustmentsRef.current = nextAdjustments;
      if (!currentImage?.id) return;
      adjustmentsByImageIdRef.current = {
        ...adjustmentsByImageIdRef.current,
        [currentImage.id]: nextAdjustments,
      };
      requestPreviewProcessingRef.current(
        nextAdjustments,
        editScopeRef.current === "selection" ? currentSelectionMaskRef.current : undefined,
        "draft"
      );
    },
    [currentImage?.id, pushEditHistory]
  );

  const endAdjustmentInteraction = useCallback(() => {
    activeAdjustmentEditKeyRef.current = null;
    const nextAdjustments = normalizeAdjustments(liveAdjustmentsRef.current);
    liveAdjustmentsRef.current = nextAdjustments;
    applyAdjustmentsToControls(nextAdjustments);
    setAdjustmentsByImageId(adjustmentsByImageIdRef.current);
    requestPreviewProcessingRef.current(
      nextAdjustments,
      editScopeRef.current === "selection" ? currentSelectionMaskRef.current : undefined,
      "full"
    );
  }, [applyAdjustmentsToControls]);

  const adjustmentChangeHandlers = useMemo<Record<AdjustmentKey, (value: number) => void>>(() => ({
    temperature: (value) => updateCurrentAdjustment("temperature", value),
    tone: (value) => updateCurrentAdjustment("tone", value),
    exposure: (value) => updateCurrentAdjustment("exposure", value),
    brightness: (value) => updateCurrentAdjustment("brightness", value),
    contrast: (value) => updateCurrentAdjustment("contrast", value),
    highlights: (value) => updateCurrentAdjustment("highlights", value),
    shadows: (value) => updateCurrentAdjustment("shadows", value),
    vibrance: (value) => updateCurrentAdjustment("vibrance", value),
    saturation: (value) => updateCurrentAdjustment("saturation", value),
  }), [updateCurrentAdjustment]);

  const updateHueChannelAdjustment = useCallback((
    channel: HueChannel,
    key: HueAdjustmentKey,
    value: number
  ) => {
    const nextValue = clampAdjustment(value);
    const currentAdjustments = normalizeAdjustments(liveAdjustmentsRef.current);
    if (currentAdjustments.hueSaturation[channel][key] === nextValue) return;
    const interactionKey = `hue:${channel}:${key}`;
    if (activeAdjustmentEditKeyRef.current !== interactionKey) {
      pushEditHistory();
      activeAdjustmentEditKeyRef.current = interactionKey;
    }
    const nextAdjustments = cloneColorAdjustments(currentAdjustments);
    nextAdjustments.hueSaturation[channel][key] = nextValue;
    liveAdjustmentsRef.current = nextAdjustments;
    if (!currentImage?.id) return;
    adjustmentsByImageIdRef.current = {
      ...adjustmentsByImageIdRef.current,
      [currentImage.id]: cloneColorAdjustments(nextAdjustments),
    };
    requestPreviewProcessingRef.current(
      nextAdjustments,
      editScopeRef.current === "selection" ? currentSelectionMaskRef.current : undefined,
      "draft"
    );
  }, [currentImage?.id, pushEditHistory]);

  const updateColorBalanceAdjustment = useCallback((
    toneKey: ColorBalanceTone,
    axis: ColorBalanceAxis,
    value: number
  ) => {
    const nextValue = clampAdjustment(value);
    const currentAdjustments = normalizeAdjustments(liveAdjustmentsRef.current);
    if (currentAdjustments.colorBalance[toneKey][axis] === nextValue) return;
    const interactionKey = `balance:${toneKey}:${axis}`;
    if (activeAdjustmentEditKeyRef.current !== interactionKey) {
      pushEditHistory();
      activeAdjustmentEditKeyRef.current = interactionKey;
    }
    const nextAdjustments = cloneColorAdjustments(currentAdjustments);
    nextAdjustments.colorBalance[toneKey][axis] = nextValue;
    liveAdjustmentsRef.current = nextAdjustments;
    if (!currentImage?.id) return;
    adjustmentsByImageIdRef.current = {
      ...adjustmentsByImageIdRef.current,
      [currentImage.id]: cloneColorAdjustments(nextAdjustments),
    };
    requestPreviewProcessingRef.current(
      nextAdjustments,
      editScopeRef.current === "selection" ? currentSelectionMaskRef.current : undefined,
      "draft"
    );
  }, [currentImage?.id, pushEditHistory]);

  const rejectPendingWorkerTasks = useCallback(() => {
    pendingWorkerTasksRef.current.forEach((task) => {
      task.reject(new Error("E8101 编辑参数有误，请调整后重试"));
    });
    pendingWorkerTasksRef.current.clear();
  }, []);

  const rejectPendingPreviewWorkerTasks = useCallback(() => {
    pendingPreviewWorkerTasksRef.current.forEach((task) => {
      task.reject(new Error("E8101"));
    });
    pendingPreviewWorkerTasksRef.current.clear();
    activePreviewWorkerTaskIdRef.current = "";
    activePreviewWorkerFastPathRef.current = false;
  }, []);

  const getWorker = useCallback(() => {
    if (workerRef.current) return workerRef.current;

    const worker = new Worker(new URL("../../workers/image-edit.worker.ts", import.meta.url));
    worker.onmessage = (event: MessageEvent<WorkerResponseMessage>) => {
      const response = event.data;
      const pendingTask = pendingWorkerTasksRef.current.get(response.taskId);
      if (!pendingTask) return;

      pendingWorkerTasksRef.current.delete(response.taskId);
      if (response.type === "done") {
        pendingTask.resolve(response.imageData);
        return;
      }
      pendingTask.reject(new Error(response.code));
    };
    worker.onerror = () => {
      rejectPendingWorkerTasks();
    };
    workerRef.current = worker;
    return worker;
  }, [rejectPendingWorkerTasks]);

  const getPreviewWorker = useCallback(() => {
    if (previewWorkerRef.current) return previewWorkerRef.current;

    const worker = new Worker(new URL("../../workers/image-edit.worker.ts", import.meta.url));
    worker.onmessage = (event: MessageEvent<WorkerResponseMessage>) => {
      const response = event.data;
      const pendingTask = pendingPreviewWorkerTasksRef.current.get(response.taskId);
      if (!pendingTask) return;

      pendingPreviewWorkerTasksRef.current.delete(response.taskId);
      if (activePreviewWorkerTaskIdRef.current === response.taskId) {
        activePreviewWorkerTaskIdRef.current = "";
        activePreviewWorkerFastPathRef.current = false;
      }
      if (response.type === "done") {
        pendingTask.resolve(response.imageData);
        return;
      }
      pendingTask.reject(new Error(response.code));
    };
    worker.onerror = () => {
      rejectPendingPreviewWorkerTasks();
    };
    previewWorkerRef.current = worker;
    return worker;
  }, [rejectPendingPreviewWorkerTasks]);

  const cancelActivePreviewWorkerTask = useCallback(() => {
    const worker = previewWorkerRef.current;
    if (!worker || !activePreviewWorkerTaskIdRef.current) return;
    if (!activePreviewWorkerFastPathRef.current) {
      // Exact full preview processing is intentionally isolated from export
      // work. It is synchronous inside the worker, so terminate just this
      // preview worker rather than allowing a new draft value to queue behind
      // it. The pending preview promise is rejected and the latest request
      // will create a fresh worker on demand.
      worker.terminate();
      previewWorkerRef.current = null;
      rejectPendingPreviewWorkerTasks();
      return;
    }
    worker.postMessage({
      type: "cancel",
      taskId: activePreviewWorkerTaskIdRef.current,
    } satisfies WorkerCancelMessage);
  }, [rejectPendingPreviewWorkerTasks]);

  const processImageDataWithAdjustments = useCallback(
    (
      imageData: ImageData,
      nextAdjustments: ColorAdjustments,
      selectionMask?: SelectionMask,
      selectionMaskIsFeathered = false,
      fastPreview = false,
      previewTask = false
    ): Promise<ImageData> => {
      const worker = previewTask ? getPreviewWorker() : getWorker();
      const pendingTasks = previewTask
        ? pendingPreviewWorkerTasksRef.current
        : pendingWorkerTasksRef.current;
      const taskId = `image-edit-${Date.now()}-${taskSequenceRef.current + 1}`;
      taskSequenceRef.current += 1;
      if (previewTask) {
        activePreviewWorkerTaskIdRef.current = taskId;
        activePreviewWorkerFastPathRef.current = fastPreview;
      }
      let workerMask: WorkerSelectionMask | undefined;
      if (selectionMask) {
        const maskVersion = selectionMask.version ?? 0;
        const featherRadius = selectionFeatherRadiusRef.current;
        if (selectionMaskIsFeathered) {
          workerMask = {
            width: selectionMask.width,
            height: selectionMask.height,
            data: new Uint8ClampedArray(selectionMask.data),
          };
        } else {
          const cached = workerMaskCacheRef.current;
          if (
            !cached ||
            cached.mask !== selectionMask ||
            cached.maskVersion !== maskVersion ||
            cached.radius !== featherRadius
          ) {
            const featheredMask = featherSelectionMask(selectionMask, featherRadius);
            workerMaskCacheRef.current = {
              mask: selectionMask,
              maskVersion,
              radius: featherRadius,
              width: featheredMask.width,
              height: featheredMask.height,
              data: new Uint8ClampedArray(featheredMask.data),
            };
          }
          const nextCached = workerMaskCacheRef.current;
          if (nextCached) {
            workerMask = {
              width: nextCached.width,
              height: nextCached.height,
              data: new Uint8ClampedArray(nextCached.data),
            };
          }
        }
      }
      const message: WorkerProcessMessage = {
        type: "process",
        taskId,
        imageData,
        adjustments: nextAdjustments,
        mask: workerMask,
        fastPreview,
        previewTask,
      };

      return new Promise((resolve, reject) => {
        pendingTasks.set(taskId, { resolve, reject });
        const transfer: Transferable[] = [imageData.data.buffer as ArrayBuffer];
        if (workerMask) transfer.push(workerMask.data.buffer as ArrayBuffer);
        worker.postMessage(message, transfer);
      });
    },
    [getPreviewWorker, getWorker]
  );

  const runLatestPreviewProcessing = useCallback(async () => {
    if (previewProcessingRef.current) {
      previewRerunQueuedRef.current = true;
      return;
    }

    const request = previewProcessRequestRef.current;
    if (!request) return;
    // A queued rerun is consumed by this invocation. If a pointer event starts
    // the latest request immediately before the queued RAF fires, clearing the
    // flag here prevents that RAF from scheduling the same frame again.
    previewRerunQueuedRef.current = false;

    if (areAdjustmentsDefault(request.adjustments)) {
      if (previewProcessRequestRef.current !== request) return;
      if (basePreviewImageIdRef.current !== request.imageId) return;

      const scratchCanvas = previewScratchCanvasRef.current || document.createElement("canvas");
      previewScratchCanvasRef.current = scratchCanvas;
      if (!paintPreviewImageDataToBuffer(
        request.sourceImageData,
        request.targetPreviewBuffer,
        request.coordinateWidth,
        request.coordinateHeight,
        scratchCanvas
      )) {
        return;
      }
      renderedPreviewImageDataRef.current = {
        width: request.coordinateWidth,
        height: request.coordinateHeight,
      };
      const shouldFitPreview =
        lastPreviewImageIdRef.current !== request.imageId ||
        (canvasTransformRef.current.scale === 1 &&
          canvasTransformRef.current.offsetX === 0 &&
          canvasTransformRef.current.offsetY === 0);
      if (shouldFitPreview) {
        lastPreviewImageIdRef.current = request.imageId;
        // The tab's toolbar can resize the viewport before ResizeObserver has
        // updated state. Fit against the current DOM bounds after loading.
        const viewport = canvasViewportRef.current?.getBoundingClientRect() ?? request.viewport;
        setCanvasTransform(getFitCanvasTransform(renderedPreviewImageDataRef.current, viewport));
      } else {
        drawCanvasPreview();
        drawSelectionOverlay();
      }
      return;
    }

    previewProcessingRef.current = true;
    const previewRunId = previewRunIdRef.current + 1;
    previewRunIdRef.current = previewRunId;
    const previewTaskId = `preview-${Date.now()}-${taskSequenceRef.current + 1}`;
    taskSequenceRef.current += 1;
    latestPreviewTaskIdRef.current = previewTaskId;

    try {
      const imageData = new ImageData(
        new Uint8ClampedArray(request.sourceImageData.data),
        request.sourceImageData.width,
        request.sourceImageData.height
      );
      const processedImageData = await processImageDataWithAdjustments(
        imageData,
        request.adjustments,
        request.selectionMask,
        false,
        request.quality === "draft",
        true
      );
      if (previewProcessRequestRef.current !== request || latestPreviewTaskIdRef.current !== previewTaskId) return;
      if (basePreviewImageIdRef.current !== request.imageId) return;

      const scratchCanvas = previewScratchCanvasRef.current || document.createElement("canvas");
      previewScratchCanvasRef.current = scratchCanvas;
      if (!paintPreviewImageDataToBuffer(
        processedImageData,
        request.targetPreviewBuffer,
        request.coordinateWidth,
        request.coordinateHeight,
        scratchCanvas
      )) {
        return;
      }
      renderedPreviewImageDataRef.current = {
        width: request.coordinateWidth,
        height: request.coordinateHeight,
      };
      const shouldFitPreview =
        lastPreviewImageIdRef.current !== request.imageId ||
        (canvasTransformRef.current.scale === 1 &&
          canvasTransformRef.current.offsetX === 0 &&
          canvasTransformRef.current.offsetY === 0);
      if (shouldFitPreview) {
        lastPreviewImageIdRef.current = request.imageId;
        const viewport = canvasViewportRef.current?.getBoundingClientRect() ?? request.viewport;
        setCanvasTransform(getFitCanvasTransform(renderedPreviewImageDataRef.current, viewport));
      } else {
        drawCanvasPreview();
        drawSelectionOverlay();
      }
    } catch {
      if (previewProcessRequestRef.current === request) {
        setMessage({ text: "E8101 编辑参数有误，请调整后重试", type: "error" });
      }
    } finally {
      if (previewRunIdRef.current !== previewRunId) return;
      previewProcessingRef.current = false;
      if (previewRerunQueuedRef.current) {
        previewRerunQueuedRef.current = false;
        if (previewProcessFrameRef.current === null) {
          previewProcessFrameRef.current = requestAnimationFrame(() => {
            previewProcessFrameRef.current = null;
            void runLatestPreviewProcessing();
          });
        }
      }
    }
  }, [drawCanvasPreview, drawSelectionOverlay, processImageDataWithAdjustments]);

  const scheduleLatestPreviewProcessing = useCallback((immediate = false) => {
    if (immediate) {
      if (previewProcessFrameRef.current !== null) {
        cancelAnimationFrame(previewProcessFrameRef.current);
        previewProcessFrameRef.current = null;
      }
      void runLatestPreviewProcessing();
      return;
    }
    if (previewProcessFrameRef.current !== null) return;
    previewProcessFrameRef.current = requestAnimationFrame(() => {
      previewProcessFrameRef.current = null;
      void runLatestPreviewProcessing();
    });
  }, [runLatestPreviewProcessing]);

  const requestPreviewProcessing = useCallback((
    nextAdjustments: ColorAdjustments,
    selectionMask?: SelectionMask,
    quality: PreviewQuality = "full"
  ) => {
    if (!open || !currentImage?.id) return;
    if (basePreviewImageIdRef.current !== currentImage.id) return;

    const basePreview = basePreviewImageDataRef.current;
    const targetPreviewBuffer = previewBufferCanvasRef.current;
    if (!basePreview || !targetPreviewBuffer) return;
    // Keep the interactive source at the same dimensions as the displayed
    // preview. The previous draft path downscaled to ~160k pixels while a
    // slider was moving, then enlarged that result into the full-size buffer;
    // this made the image visibly soft during the drag. Requests are now
    // coalesced by `runLatestPreviewProcessing`, so a fast drag stays current
    // without ever presenting a low-resolution frame.
    const sourceImageData = basePreview;

    // A new pointer value must not wait for the previous full-resolution
    // preview frame. GPU results are discarded by request identity when stale;
    // the CPU fallback cancels between MessageChannel-scheduled chunks.
    cancelActivePreviewWorkerTask();

    previewProcessRequestRef.current = {
      imageId: currentImage.id,
      sourceImageData,
      targetPreviewBuffer,
      adjustments: nextAdjustments,
      selectionMask,
      viewport: canvasViewportSizeRef.current,
      coordinateWidth: basePreview.width,
      coordinateHeight: basePreview.height,
      quality,
    };
    // Keep the source at full preview dimensions. Draft slider updates use a
    // local WebGL2 with a cancellable LUT fallback; an exact full preview is
    // isolated as well and terminated if a new draft starts.
    scheduleLatestPreviewProcessing(quality === "draft");
  }, [cancelActivePreviewWorkerTask, currentImage?.id, open, scheduleLatestPreviewProcessing]);

  useEffect(() => {
    requestPreviewProcessingRef.current = requestPreviewProcessing;
  }, [requestPreviewProcessing]);

  /**
   * Ctrl+D commits the current adjustment into the image, then clears the
   * selection and returns the controls to neutral values. The commit is kept
   * both at preview resolution (for the live canvas) and as an operation list
   * (for full-resolution save/download).
   */
  const clearSelectionAndResetAdjustments = useCallback(async () => {
    const imageId = currentImage?.id;
    const basePreview = basePreviewImageDataRef.current;
    const selectionMask =
      editScopeRef.current === "selection" ? currentSelectionMaskRef.current : undefined;
    const currentAdjustments = normalizeAdjustments(liveAdjustmentsRef.current);
    const hasSelection = Boolean(selectionMask);
    const hasAdjustments = !areAdjustmentsDefault(currentAdjustments);
    if (!imageId || !basePreview || (!hasSelection && !hasAdjustments) || ctrlDBakeInProgressRef.current) return;

    ctrlDBakeInProgressRef.current = true;
    const historyLengthBefore = editHistoryRef.current.length;
    pushEditHistory();
    try {
      let bakedImageData = cloneImageDataValue(basePreview);
      const bakedSelectionMask = selectionMask
        ? featherSelectionMask(selectionMask, selectionFeatherRadiusRef.current)
        : undefined;
      if (hasAdjustments) {
        bakedImageData = await processImageDataWithAdjustments(
          new ImageData(new Uint8ClampedArray(basePreview.data), basePreview.width, basePreview.height),
          currentAdjustments,
          bakedSelectionMask,
          true
        );
      }

      // The user may have changed images while the worker was running.
      if (basePreviewImageIdRef.current !== imageId || currentImage?.id !== imageId) {
        if (editHistoryRef.current.length === historyLengthBefore + 1) {
          editHistoryRef.current = editHistoryRef.current.slice(0, -1);
          setHistoryRevision((revision) => revision + 1);
        }
        return;
      }
      bakedPreviewImageDataByImageIdRef.current = {
        ...bakedPreviewImageDataByImageIdRef.current,
        [imageId]: cloneImageDataValue(bakedImageData),
      };
      if (hasAdjustments) {
        bakedEditOperationsByImageIdRef.current = {
          ...bakedEditOperationsByImageIdRef.current,
          [imageId]: [
            ...(bakedEditOperationsByImageIdRef.current[imageId] || []),
            {
              adjustments: cloneColorAdjustments(currentAdjustments),
              selectionMask: bakedSelectionMask ? cloneSelectionMask(bakedSelectionMask) : undefined,
              selectionMaskIsFeathered: Boolean(bakedSelectionMask),
            },
          ],
        };
      }
      basePreviewImageDataRef.current = cloneImageDataValue(bakedImageData);
      draftPreviewImageDataRef.current = cloneImageDataValue(bakedImageData);
      workerMaskCacheRef.current = null;
      brushStrokeRef.current = null;
      if (brushPreviewFrameRef.current !== null) {
        cancelAnimationFrame(brushPreviewFrameRef.current);
        brushPreviewFrameRef.current = null;
      }
      dragStartRef.current = null;
      currentSelectionMaskRef.current = undefined;
      selectionOutlinePathCacheRef.current = null;
      delete smartSelectionCacheRef.current[imageId];
      setSmartSelectionActiveRegion(null);
      setSelectionMasksByImageId((previous) => {
        const next = { ...previous };
        delete next[imageId];
        selectionMasksByImageIdRef.current = next;
        return next;
      });

      const freshDefaultAdjustments = cloneColorAdjustments(DEFAULT_ADJUSTMENTS);
      activeAdjustmentEditKeyRef.current = null;
      applyAdjustmentsToControls(freshDefaultAdjustments);
      adjustmentsByImageIdRef.current = {
        ...adjustmentsByImageIdRef.current,
        [imageId]: freshDefaultAdjustments,
      };
      setAdjustmentsByImageId(adjustmentsByImageIdRef.current);
      setPreviewSourceVersion((version) => version + 1);
      requestPreviewProcessingRef.current(freshDefaultAdjustments, undefined, "full");
    } catch {
      if (editHistoryRef.current.length === historyLengthBefore + 1) {
        editHistoryRef.current = editHistoryRef.current.slice(0, -1);
        setHistoryRevision((revision) => revision + 1);
      }
      setMessage({ text: "E8101 编辑参数有误，请调整后重试", type: "error" });
    } finally {
      ctrlDBakeInProgressRef.current = false;
    }
  }, [applyAdjustmentsToControls, currentImage?.id, processImageDataWithAdjustments, pushEditHistory]);

  const scheduleClearSelectionAndResetAdjustments = useCallback((): Promise<void> => {
    if (ctrlDCommitPromiseRef.current) return ctrlDCommitPromiseRef.current;
    // Adjustment controls coalesce their latest value with requestAnimationFrame.
    // Commit on the following frame so Ctrl+D cannot clear the mask with stale
    // neutral values and then apply the pending adjustment to the whole image.
    let resolveCommit: () => void = () => {};
    const commitPromise = new Promise<void>((resolve) => {
      resolveCommit = resolve;
      ctrlDCommitResolveRef.current = resolve;
      ctrlDCommitFrameRef.current = requestAnimationFrame(() => {
        ctrlDCommitFrameRef.current = null;
        void clearSelectionAndResetAdjustments().finally(resolve);
      });
    }).finally(() => {
      if (ctrlDCommitPromiseRef.current === commitPromise) {
        ctrlDCommitPromiseRef.current = null;
      }
      if (ctrlDCommitResolveRef.current === resolveCommit) {
        ctrlDCommitResolveRef.current = null;
      }
    });
    ctrlDCommitPromiseRef.current = commitPromise;
    return commitPromise;
  }, [clearSelectionAndResetAdjustments]);

  const switchRightPanelTab = useCallback(async (
    nextTab: "color" | "font" | "sticker" | "stroke",
  ) => {
    const requestId = ++rightPanelTabRequestRef.current;
    if (
      rightPanelTab === "color"
      && nextTab !== "color"
      && editScopeRef.current === "selection"
      && currentImage?.id
      && hasSelectedMaskPixels(selectionMasksByImageIdRef.current[currentImage.id])
    ) {
      await scheduleClearSelectionAndResetAdjustments();
    }
    if (rightPanelTabRequestRef.current !== requestId) return;
    setLayerEraserActive(false);
    if (nextTab === "font" || nextTab === "sticker") {
      setSelectionPanelOpen(false);
      setSelectionTool(null);
      setSmartSelectionMenuOpen(false);
      setFeatherPanelOpen(false);
      setLayerEraserPanelOpen(false);
      clearCanvasCursorState();
    }
    setRightPanelTab(nextTab);
  }, [clearCanvasCursorState, currentImage?.id, rightPanelTab, scheduleClearSelectionAndResetAdjustments]);

  useEffect(() => {
    if (!open) {
      workerRef.current?.terminate();
      workerRef.current = null;
      rejectPendingWorkerTasks();
      previewWorkerRef.current?.terminate();
      previewWorkerRef.current = null;
      rejectPendingPreviewWorkerTasks();
      latestPreviewTaskIdRef.current = "";
      previewProcessRequestRef.current = null;
      previewRerunQueuedRef.current = false;
      if (brushPreviewFrameRef.current !== null) {
        cancelAnimationFrame(brushPreviewFrameRef.current);
        brushPreviewFrameRef.current = null;
      }
      if (selectionAnimationFrameRef.current !== null) {
        cancelAnimationFrame(selectionAnimationFrameRef.current);
        selectionAnimationFrameRef.current = null;
      }
      if (previewProcessFrameRef.current !== null) {
        cancelAnimationFrame(previewProcessFrameRef.current);
        previewProcessFrameRef.current = null;
      }
      if (canvasRedrawFrameRef.current !== null) {
        cancelAnimationFrame(canvasRedrawFrameRef.current);
        canvasRedrawFrameRef.current = null;
      }
      if (cursorUpdateFrameRef.current !== null) {
        cancelAnimationFrame(cursorUpdateFrameRef.current);
        cursorUpdateFrameRef.current = null;
      }
      if (ctrlDCommitFrameRef.current !== null) {
        cancelAnimationFrame(ctrlDCommitFrameRef.current);
        ctrlDCommitFrameRef.current = null;
        ctrlDCommitResolveRef.current?.();
        ctrlDCommitResolveRef.current = null;
      }
      setExportProgress(null);
      editHistoryRef.current = [];
      redoHistoryRef.current = [];
      setHistoryRevision((revision) => revision + 1);
      setCanvasShortcutPanMode(false);
      setComparisonMode(false);
      setComparisonSplit(0.5);
      setComparisonDragging(false);
      setTextLayers([]);
      setLayerEraserActive(false);
      setLayerEraserCursorPosition(null);
      layerEraserStrokeOperationRef.current = null;
      setTextStyle(DEFAULT_TEXT_LAYER_STYLE);
      setSelectedTextLayerId(null);
      setEditingTextLayerId(null);
      setStickerLayers([]);
      setLayerOrder([]);
      setSelectedStickerLayerId(null);
      stickerLayerPointerOperationRef.current = null;
      setFontCursorPosition(null);
      setEyeDropperActive(false);
      setEyeDropperPreview(null);
      comparisonModeRef.current = false;
      comparisonSplitRef.current = 0.5;
      comparisonDragPointerRef.current = null;
      return;
    }

    getWorker();
    return () => {
      workerRef.current?.terminate();
      workerRef.current = null;
      rejectPendingWorkerTasks();
      previewWorkerRef.current?.terminate();
      previewWorkerRef.current = null;
      rejectPendingPreviewWorkerTasks();
      latestPreviewTaskIdRef.current = "";
      previewProcessRequestRef.current = null;
      previewRerunQueuedRef.current = false;
      if (brushPreviewFrameRef.current !== null) {
        cancelAnimationFrame(brushPreviewFrameRef.current);
        brushPreviewFrameRef.current = null;
      }
      if (selectionAnimationFrameRef.current !== null) {
        cancelAnimationFrame(selectionAnimationFrameRef.current);
        selectionAnimationFrameRef.current = null;
      }
      if (previewProcessFrameRef.current !== null) {
        cancelAnimationFrame(previewProcessFrameRef.current);
        previewProcessFrameRef.current = null;
      }
      if (canvasRedrawFrameRef.current !== null) {
        cancelAnimationFrame(canvasRedrawFrameRef.current);
        canvasRedrawFrameRef.current = null;
      }
      if (cursorUpdateFrameRef.current !== null) {
        cancelAnimationFrame(cursorUpdateFrameRef.current);
        cursorUpdateFrameRef.current = null;
      }
      if (ctrlDCommitFrameRef.current !== null) {
        cancelAnimationFrame(ctrlDCommitFrameRef.current);
        ctrlDCommitFrameRef.current = null;
        ctrlDCommitResolveRef.current?.();
        ctrlDCommitResolveRef.current = null;
      }
      setCanvasShortcutPanMode(false);
    };
  }, [
    getWorker,
    open,
    rejectPendingPreviewWorkerTasks,
    rejectPendingWorkerTasks,
  ]);

  useEffect(() => {
    if (!open) return;

    const isEditableTarget = (target: HTMLElement | null) => (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement ||
      !!target?.isContentEditable
    );

    const blurFocusedControl = () => {
      const activeElement = document.activeElement;
      if (!(activeElement instanceof HTMLElement)) return;
      if (activeElement === document.body || activeElement === document.documentElement) return;
      if (isEditableTarget(activeElement)) return;
      activeElement.blur();
    };

    const syncSelectionShortcutMode = () => {
      setSelectionShortcutMode(getSelectionShortcutMode(selectionShortcutKeysRef.current));
    };

    const updateSelectionShortcutKey = (event: KeyboardEvent, pressed: boolean) => {
      const isShiftKey = event.key === "Shift" || event.code === "ShiftLeft" || event.code === "ShiftRight";
      const isAltKey = event.key === "Alt" || event.code === "AltLeft" || event.code === "AltRight";
      if (!isShiftKey && !isAltKey) return;
      if (isAltKey) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
      if (isShiftKey) selectionShortcutKeysRef.current.shiftKey = pressed;
      if (isAltKey) selectionShortcutKeysRef.current.altKey = pressed;
      syncSelectionShortcutMode();
    };

    const isSpaceKey = (event: KeyboardEvent) => (
      event.code === "Space" || event.key === " " || event.key === "Spacebar"
    );

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        (event.ctrlKey || event.metaKey) &&
        !event.shiftKey &&
        (event.key.toLowerCase() === "d" || event.code === "KeyD") &&
        !isEditableTarget(target)
      ) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        if (!event.repeat) {
          scheduleClearSelectionAndResetAdjustments();
        }
        return;
      }
      updateSelectionShortcutKey(event, true);
      if (isSpaceKey(event) && !event.ctrlKey && !event.metaKey && !event.altKey) {
        if (isEditableTarget(target)) return;
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        if (!event.repeat) {
          blurFocusedControl();
          setCanvasShortcutPanMode(true);
          clearCanvasCursorState({ wand: false });
        }
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      updateSelectionShortcutKey(event, false);
      if (!isSpaceKey(event)) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      setCanvasShortcutPanMode(false);
    };

    const handleBlur = () => {
      setCanvasShortcutPanMode(false);
      selectionShortcutKeysRef.current = { altKey: false, shiftKey: false };
      setSelectionShortcutMode(null);
    };

    window.addEventListener("keydown", handleKeyDown, true);
    window.addEventListener("keyup", handleKeyUp, true);
    window.addEventListener("blur", handleBlur);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
      window.removeEventListener("keyup", handleKeyUp, true);
      window.removeEventListener("blur", handleBlur);
      selectionShortcutKeysRef.current = { altKey: false, shiftKey: false };
      setCanvasShortcutPanMode(false);
      setSelectionShortcutMode(null);
    };
  }, [clearCanvasCursorState, open, scheduleClearSelectionAndResetAdjustments]);

  useEffect(() => {
    if (!open) return;
    const handleLayerDeleteKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const editing = (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        Boolean(target?.isContentEditable) ||
        Boolean(target?.closest('[contenteditable]:not([contenteditable="false"])'))
      );
      if (event.key !== "Delete" && event.key !== "Backspace") return;

      // Keep the native edit behavior inside inputs while preventing this shortcut
      // from escaping the open editor and triggering a parent image deletion.
      event.stopPropagation();
      if (editing) return;

      event.preventDefault();
      if (selectedTextLayerId) {
        const remainingTextLayers = textLayers.filter((layer) => layer.id !== selectedTextLayerId);
        const selectableTextLayers = isStackPreview
          ? remainingTextLayers
          : remainingTextLayers.filter((layer) => layer.imageId === currentImage?.id);
        const selectableStickerLayers = isStackPreview
          ? stickerLayers
          : stickerLayers.filter((layer) => layer.imageId === currentImage?.id);
        const nextTextLayer = selectableTextLayers.length > 0
          ? selectableTextLayers[Math.floor(Math.random() * selectableTextLayers.length)]
          : null;
        const nextStickerLayer = !nextTextLayer && selectableStickerLayers.length > 0
          ? selectableStickerLayers[Math.floor(Math.random() * selectableStickerLayers.length)]
          : null;
        setTextLayers(remainingTextLayers);
        setLayerOrder((order) => order.filter((layer) => layer.id !== selectedTextLayerId));
        setEditingTextLayerId(null);
        setSelectedTextLayerId(nextTextLayer?.id ?? null);
        setSelectedStickerLayerId(nextStickerLayer?.id ?? null);
        if (nextTextLayer) setTextStyle(nextTextLayer.style);
        return;
      }
      if (selectedStickerLayerId) {
        const remainingStickerLayers = stickerLayers.filter((layer) => layer.id !== selectedStickerLayerId);
        const selectableStickerLayers = isStackPreview
          ? remainingStickerLayers
          : remainingStickerLayers.filter((layer) => layer.imageId === currentImage?.id);
        const selectableTextLayers = isStackPreview
          ? textLayers
          : textLayers.filter((layer) => layer.imageId === currentImage?.id);
        const nextStickerLayer = selectableStickerLayers.length > 0
          ? selectableStickerLayers[Math.floor(Math.random() * selectableStickerLayers.length)]
          : null;
        const nextTextLayer = !nextStickerLayer && selectableTextLayers.length > 0
          ? selectableTextLayers[Math.floor(Math.random() * selectableTextLayers.length)]
          : null;
        setStickerLayers(remainingStickerLayers);
        setLayerOrder((order) => order.filter((layer) => layer.id !== selectedStickerLayerId));
        setSelectedStickerLayerId(nextStickerLayer?.id ?? null);
        setSelectedTextLayerId(nextTextLayer?.id ?? null);
        if (nextTextLayer) setTextStyle(nextTextLayer.style);
      }
    };
    window.addEventListener("keydown", handleLayerDeleteKeyDown, true);
    return () => window.removeEventListener("keydown", handleLayerDeleteKeyDown, true);
  }, [currentImage?.id, isStackPreview, open, selectedStickerLayerId, selectedTextLayerId, stickerLayers, textLayers]);

  useEffect(() => {
    if (!open) return;
    const handleTextLayerKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const editing = (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        Boolean(target?.isContentEditable) ||
        Boolean(target?.closest('[contenteditable]:not([contenteditable="false"])'))
      );
      // 小键盘 Enter：结束图层交互并直接取消选中（比 Escape 多取消选中一步）；
      // 主键盘 Enter 保持原生行为。无编辑、无选中时不劫持原生行为。
      if (event.code === "NumpadEnter" && (editingTextLayerId || selectedTextLayerId || selectedStickerLayerId)) {
        event.preventDefault();
        const activeElement = document.activeElement;
        if (activeElement instanceof HTMLElement) activeElement.blur();
        setEditingTextLayerId(null);
        setSelectedTextLayerId(null);
        setSelectedStickerLayerId(null);
        return;
      }
      if (event.key === "Escape" && target?.closest(".flowx-ecom-layer--tutorial")) return;
      if (event.key === "Escape" && editingTextLayerId && (editing || activeTextAreaRef.current)) {
        event.preventDefault();
        activeTextAreaRef.current?.blur();
        setEditingTextLayerId(null);
      }
    };
    window.addEventListener("keydown", handleTextLayerKeyDown);
    return () => window.removeEventListener("keydown", handleTextLayerKeyDown);
  }, [editingTextLayerId, selectedStickerLayerId, selectedTextLayerId, open]);

  useEffect(() => {
    if (!open) return;

    const preventBrowserCtrlWheelZoom = (event: WheelEvent) => {
      if (!event.ctrlKey) return;
      event.preventDefault();
    };

    const isZoomEditableTarget = (target: EventTarget | null) => {
      const element = target as HTMLElement | null;
      if (!element) return false;
      return (
        element.tagName === "TEXTAREA" ||
        element.isContentEditable ||
        (element instanceof HTMLInputElement && element.type !== "range")
      );
    };

    const preventBrowserKeyboardZoom = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey)) return;
      const isZoomKey =
        event.key === "+" ||
        event.key === "=" ||
        event.key === "-" ||
        event.key === "_" ||
        event.key === "0" ||
        event.code === "Equal" ||
        event.code === "Minus" ||
        event.code === "Digit0" ||
        event.code === "NumpadAdd" ||
        event.code === "NumpadSubtract" ||
        event.code === "Numpad0";
      if (!isZoomKey) return;
      event.preventDefault();
      event.stopPropagation();
      // Ctrl/Cmd+加减号联动画布缩放，步长与底部缩放按钮一致（CANVAS_ZOOM_STEP）。
      const zoomIn =
        event.key === "+" ||
        event.key === "=" ||
        event.code === "Equal" ||
        event.code === "NumpadAdd";
      const zoomOut =
        event.key === "-" ||
        event.key === "_" ||
        event.code === "Minus" ||
        event.code === "NumpadSubtract";
      if ((!zoomIn && !zoomOut) || isZoomEditableTarget(event.target)) return;
      updateCanvasZoom(
        canvasTransformRef.current.scale * (zoomIn ? CANVAS_ZOOM_STEP : 1 / CANVAS_ZOOM_STEP),
      );
    };

    window.addEventListener("wheel", preventBrowserCtrlWheelZoom, { capture: true, passive: false });
    window.addEventListener("keydown", preventBrowserKeyboardZoom, true);
    return () => {
      window.removeEventListener("wheel", preventBrowserCtrlWheelZoom, { capture: true });
      window.removeEventListener("keydown", preventBrowserKeyboardZoom, true);
    };
  }, [open, updateCanvasZoom]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== "z") return;
      const target = event.target as HTMLElement | null;
      if (target?.tagName === "TEXTAREA" || target?.isContentEditable) return;
      if (target instanceof HTMLInputElement && target.type !== "range") return;
      event.preventDefault();
      if (event.shiftKey) redoLastEdit();
      else undoLastEdit();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, redoLastEdit, undoLastEdit]);

  useEffect(() => {
    if (!open) return;
    const viewport = canvasViewportRef.current;
    if (!viewport) return;

    const updateViewportSize = () => {
      const rect = viewport.getBoundingClientRect();
      setCanvasViewportSize({
        width: Math.max(1, Math.round(rect.width)),
        height: Math.max(1, Math.round(rect.height)),
      });
    };

    updateViewportSize();
    const resizeObserver = new ResizeObserver(updateViewportSize);
    resizeObserver.observe(viewport);
    return () => resizeObserver.disconnect();
  }, [open]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const pixelRatio = getCanvasPixelRatio();
    const width = Math.max(1, Math.round(canvasViewportSize.width * pixelRatio));
    const height = Math.max(1, Math.round(canvasViewportSize.height * pixelRatio));
    canvas.width = width;
    canvas.height = height;
    drawCanvasPreview();
    drawSelectionOverlay();
  }, [canvasViewportSize, drawCanvasPreview, drawSelectionOverlay]);

  useEffect(() => {
    drawCanvasPreview();
    drawSelectionOverlay();
  }, [canvasTransform, drawCanvasPreview, drawSelectionOverlay]);

  useEffect(() => {
    if (!open) return;
    strokePreviewCacheSessionRef.current += 1;
    strokePreviewLoadRunRef.current += 1;
    rightPanelTabRequestRef.current += 1;
    strokePreviewSourceSessionsRef.current = { display: 0 };
    strokePreviewSourcesRef.current = [];
    strokeStackDirectLayoutRef.current = null;
    stackPreviewLayoutRef.current = null;
    strokePreviewViewKeyRef.current = null;
    strokePreviewRenderedDimsRef.current = null;
    requestStrokePreviewFrameRef.current = () => {};
    if (strokePreviewFrameRef.current !== null) {
      cancelAnimationFrame(strokePreviewFrameRef.current);
      strokePreviewFrameRef.current = null;
    }
    setMode(initialMode);
    const initial = initialImageId || sourceImages[0]?.id || "";
    setCurrentId(initial);
    if (initialMode === "batch") {
      setSelectedIds(new Set(sourceImages.slice(0, MAX_BATCH_COUNT).map((img) => img.id)));
    } else {
      setSelectedIds(new Set(initial ? [initial] : []));
    }
    const freshDefaultAdjustments = cloneColorAdjustments(DEFAULT_ADJUSTMENTS);
    applyAdjustmentsToControls(freshDefaultAdjustments);
    liveAdjustmentsRef.current = freshDefaultAdjustments;
    adjustmentsByImageIdRef.current = {};
    selectionMasksByImageIdRef.current = {};
    sourcePreviewImageDataByImageIdRef.current = {};
    bakedPreviewImageDataByImageIdRef.current = {};
    bakedEditOperationsByImageIdRef.current = {};
    currentSelectionMaskRef.current = undefined;
    workerMaskCacheRef.current = null;
    pendingBrushCursorRef.current = null;
    pendingWandCursorRef.current = null;
    setAdjustmentsByImageId({});
    setSelectionMasksByImageId({});
    editScopeRef.current = "selection";
    setEditScope("selection");
    setSelectionCombineMode("add");
    setSelectionTool(null);
    setSelectionPanelOpen(false);
    setSelectionPanelTop(12);
    setSelectionOutlineVisible(true);
    setSmartSelectionMenuOpen(false);
    setFeatherPanelOpen(false);
    setActiveHueChannel("master");
    setActiveColorBalanceTone("midtones");
    setWandTolerance(15);
    setBrushSize(DEFAULT_BRUSH_SIZE);
    selectionFeatherRadiusRef.current = DEFAULT_SELECTION_FEATHER_RADIUS;
    setSelectionFeatherRadius(DEFAULT_SELECTION_FEATHER_RADIUS);
    clearCanvasCursorState();
    canvasZoomMultiplierRef.current = 1;
    setCanvasTransform({ scale: 1, offsetX: 0, offsetY: 0 });
    setCanvasDragging(false);
    setCanvasPanMode(false);
    setCanvasShortcutPanMode(false);
    setComparisonMode(false);
    setComparisonSplit(0.5);
    setComparisonDragging(false);
    comparisonModeRef.current = false;
    comparisonSplitRef.current = 0.5;
    comparisonDragPointerRef.current = null;
    setPreviewSourceVersion(0);
    basePreviewImageDataRef.current = null;
    draftPreviewImageDataRef.current = null;
    renderedPreviewImageDataRef.current = null;
    originalPreviewBufferCanvasRef.current = null;
    previewProcessRequestRef.current = null;
    previewRerunQueuedRef.current = false;
    if (previewProcessFrameRef.current !== null) {
      cancelAnimationFrame(previewProcessFrameRef.current);
      previewProcessFrameRef.current = null;
    }
    activeAdjustmentEditKeyRef.current = null;
    basePreviewImageIdRef.current = "";
    lastPreviewImageIdRef.current = "";
    dragStartRef.current = null;
    brushStrokeRef.current = null;
    editHistoryRef.current = [];
    redoHistoryRef.current = [];
    setHistoryRevision((revision) => revision + 1);
    smartSelectionCacheRef.current = {};
    setExportProgress(null);
    setSmartSelectionLoadingRegion(null);
    setSmartSelectionActiveRegion(null);
    setMessage(null);
  }, [applyAdjustmentsToControls, clearCanvasCursorState, initialImageId, initialMode, open, sourceImages]);

  useEffect(() => {
    if (mode === "single") {
      setSelectedIds(new Set(currentId ? [currentId] : []));
      return;
    }
    setSelectedIds((prev) => {
      const next = new Set(Array.from(prev).filter((id) => validImages.some((img) => img.id === id)));
      if (next.size === 0) {
        validImages.slice(0, MAX_BATCH_COUNT).forEach((img) => next.add(img.id));
      }
      return next;
    });
  }, [currentId, mode, validImages]);

  useEffect(() => {
    if (!open) return;

    const handleOutsidePanelPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (selectionPanelRef.current?.contains(target)) return;
      if (target.closest("[data-image-edit-tool-trigger='true']")) return;

      setSelectionPanelOpen(false);
      setSmartSelectionMenuOpen(false);
      setFeatherPanelOpen(false);
      setLayerEraserPanelOpen(false);
    };

    document.addEventListener("pointerdown", handleOutsidePanelPointerDown);
    return () => document.removeEventListener("pointerdown", handleOutsidePanelPointerDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const loadRun = ++strokePreviewLoadRunRef.current;
    const loadSession = strokePreviewCacheSessionRef.current;
    let cancelled = false;
    const isStaleLoad = () => (
      cancelled
      || loadRun !== strokePreviewLoadRunRef.current
      || loadSession !== strokePreviewCacheSessionRef.current
    );

    async function loadPreviewSource() {
      const canvas = canvasRef.current;
      if (!canvas || !previewSourceImage?.url) return;
      // A queued paint from the previous tab must not consume the pending fit
      // with its old pixel dimensions while the new source is loading.
      previewProcessRequestRef.current = null;
      renderedPreviewImageDataRef.current = null;
      basePreviewImageDataRef.current = null;
      draftPreviewImageDataRef.current = null;
      // A zoom rebuild also owns a bounded, already-composited fast sibling.
      // Keep it alive across zoom/fast effects so a new drag has no async gap.
      if (!isStackPreview) strokePreviewSourcesRef.current = [];
      originalPreviewBufferCanvasRef.current = null;
      basePreviewImageIdRef.current = "";
      let cleanup = () => {};
      try {
        if (isStackPreview) {
          const loadedImages = await Promise.all(validImages.map(async (item) => ({
            item,
            loaded: await loadImage(item.url),
          })));
          cleanup = () => loadedImages.forEach(({ loaded }) => loaded.cleanup());
          if (isStaleLoad()) return;

          const naturalSizes = loadedImages.map(({ item, loaded }) => ({
            id: item.id,
            width: loaded.img.naturalWidth || loaded.img.width,
            height: loaded.img.naturalHeight || loaded.img.height,
          }));
          const frameScales = naturalSizes.map((source) => getStrokeStackSourceScale(
            source.width,
            source.height,
            STROKE_PREVIEW_FRAME_DISPLAY_WIDTH,
          ));
          const strokeFrameSources: StrokePreviewFrameSource[] = [];

          for (let frameIndex = 0; frameIndex < loadedImages.length; frameIndex += 1) {
            const { item, loaded } = loadedImages[frameIndex];
            const frameScale = frameScales[frameIndex] ?? 1;
            const sourceFrameWidth = Math.max(1, Math.round((loaded.img.naturalWidth || loaded.img.width) * frameScale));
            const sourceFrameHeight = Math.max(1, Math.round((loaded.img.naturalHeight || loaded.img.height) * frameScale));
            const frameCanvas = document.createElement("canvas");
            frameCanvas.width = sourceFrameWidth;
            frameCanvas.height = sourceFrameHeight;
            const frameContext = frameCanvas.getContext("2d", { willReadFrequently: true });
            if (!frameContext) continue;
            frameContext.imageSmoothingEnabled = true;
            frameContext.imageSmoothingQuality = "high";
            frameContext.drawImage(loaded.img, 0, 0, sourceFrameWidth, sourceFrameHeight);
            const rawFramePixels = frameContext.getImageData(0, 0, sourceFrameWidth, sourceFrameHeight);
            sourcePreviewImageDataByImageIdRef.current[item.id] = cloneImageDataValue(rawFramePixels);
            const storedFramePixels = getImagePreviewPixels(
              item,
              sourcePreviewImageDataByImageIdRef.current,
              bakedPreviewImageDataByImageIdRef.current,
            );
            let editedFramePixels = storedFramePixels
              ? cloneImageDataValue(storedFramePixels)
              : cloneImageDataValue(rawFramePixels);
            if (editedFramePixels.width !== sourceFrameWidth || editedFramePixels.height !== sourceFrameHeight) {
              const resizedFrameCanvas = document.createElement("canvas");
              paintPreviewImageDataToBuffer(
                editedFramePixels,
                resizedFrameCanvas,
                sourceFrameWidth,
                sourceFrameHeight,
                document.createElement("canvas"),
              );
              const resizedFrameContext = resizedFrameCanvas.getContext("2d", { willReadFrequently: true });
              if (resizedFrameContext) {
                editedFramePixels = resizedFrameContext.getImageData(0, 0, sourceFrameWidth, sourceFrameHeight);
              }
            }

            const bakedOperations = bakedEditOperationsByImageIdRef.current[item.id] || [];
            if (bakedOperations.length > 0) {
              editedFramePixels = cloneImageDataValue(rawFramePixels);
              for (const operation of bakedOperations) {
                editedFramePixels = await processImageDataWithAdjustments(
                  editedFramePixels,
                  operation.adjustments,
                  operation.selectionMask
                    ? scaleSelectionMaskToFrame(operation.selectionMask, sourceFrameWidth, sourceFrameHeight)
                    : undefined,
                  operation.selectionMaskIsFeathered,
                );
                if (isStaleLoad()) return;
              }
            }

            const frameAdjustments = getImageAdjustments(item.id);
            if (!areAdjustmentsDefault(frameAdjustments)) {
              const selectionMask = editScope === "selection"
                ? selectionMasksByImageIdRef.current[item.id]
                : undefined;
              editedFramePixels = await processImageDataWithAdjustments(
                editedFramePixels,
                frameAdjustments,
                selectionMask
                  ? scaleSelectionMaskToFrame(selectionMask, sourceFrameWidth, sourceFrameHeight)
                  : undefined,
              );
              if (isStaleLoad()) return;
            }

            frameCanvas.width = sourceFrameWidth;
            frameCanvas.height = sourceFrameHeight;
            const editedFrameContext = frameCanvas.getContext("2d");
            editedFrameContext?.putImageData(editedFramePixels, 0, 0);
            if (editedFrameContext) {
              await ensureTextLayerFontsReady(textLayers);
              await drawOrderedImageEditLayersToCanvas(
                editedFrameContext,
                textLayers,
                stickerLayers,
                layerOrder,
                item.id,
                sourceFrameWidth,
                sourceFrameHeight,
              );
              if (isStaleLoad()) return;
              strokeFrameSources.push({
                id: item.id,
                canvas: frameCanvas,
                naturalWidth: loaded.img.naturalWidth || loaded.img.width,
                naturalHeight: loaded.img.naturalHeight || loaded.img.height,
                sourceScale: frameScale,
              });
            }
          }

          if (isStaleLoad()) return;
          if (strokeFrameSources.length !== loadedImages.length) return;
          strokePreviewSourcesRef.current = strokeFrameSources;
          strokePreviewSourceSessionsRef.current.display = loadSession;
          basePreviewImageIdRef.current = previewSourceImage.id;
          setPreviewSourceVersion((version) => version + 1);
          return;
        }

        stackPreviewLayoutRef.current = null;
        const loaded = await loadImage(previewSourceImage.url);
        const img = loaded.img;
        cleanup = loaded.cleanup;
        if (isStaleLoad()) return;
        const scale =
          rightPanelTab === "stroke"
            ? getStrokeStackSourceScale(
                img.naturalWidth,
                img.naturalHeight,
                STROKE_PREVIEW_FRAME_DISPLAY_WIDTH,
              )
            : getPreviewScale(img.naturalWidth, img.naturalHeight);
        const previewWidth = Math.max(1, Math.round(img.naturalWidth * scale));
        const previewHeight = Math.max(1, Math.round(img.naturalHeight * scale));
        const previewBuffer = previewBufferCanvasRef.current || document.createElement("canvas");
        previewBufferCanvasRef.current = previewBuffer;
        previewBuffer.width = previewWidth;
        previewBuffer.height = previewHeight;
        const previewCtx = previewBuffer.getContext("2d", { willReadFrequently: true });
        if (!previewCtx) return;
        previewCtx.clearRect(0, 0, previewWidth, previewHeight);
        previewCtx.drawImage(img, 0, 0, previewWidth, previewHeight);
        const originalPreviewBuffer = originalPreviewBufferCanvasRef.current || document.createElement("canvas");
        originalPreviewBufferCanvasRef.current = originalPreviewBuffer;
        originalPreviewBuffer.width = previewWidth;
        originalPreviewBuffer.height = previewHeight;
        const originalPreviewCtx = originalPreviewBuffer.getContext("2d", { willReadFrequently: true });
        if (!originalPreviewCtx) return;
        originalPreviewCtx.clearRect(0, 0, previewWidth, previewHeight);
        originalPreviewCtx.drawImage(img, 0, 0, previewWidth, previewHeight);
        const imageData = previewCtx.getImageData(0, 0, previewWidth, previewHeight);
        const sourceImageData = new ImageData(
          new Uint8ClampedArray(imageData.data),
          imageData.width,
          imageData.height
        );
        sourcePreviewImageDataByImageIdRef.current[previewSourceImage.id] = cloneImageDataValue(sourceImageData);
        const bakedImageData = bakedPreviewImageDataByImageIdRef.current[previewSourceImage.id];
        if (
          bakedImageData &&
          (bakedImageData.width !== previewWidth || bakedImageData.height !== previewHeight)
        ) {
          // 描边 tab 与其他 tab 的预览分辨率不同(描边按 400px 列宽放宽到更高像素),
          // 跨 tab 复用烘焙数据必须重采样到当前预览尺寸,否则后续 putImageData 只
          // 覆盖左上角区域。
          const resizedBakedCanvas = document.createElement("canvas");
          paintPreviewImageDataToBuffer(
            bakedImageData,
            resizedBakedCanvas,
            previewWidth,
            previewHeight,
            document.createElement("canvas"),
          );
          const resizedBakedContext = resizedBakedCanvas.getContext("2d", { willReadFrequently: true });
          basePreviewImageDataRef.current = resizedBakedContext
            ? resizedBakedContext.getImageData(0, 0, previewWidth, previewHeight)
            : sourceImageData;
        } else {
          basePreviewImageDataRef.current = bakedImageData
            ? cloneImageDataValue(bakedImageData)
            : sourceImageData;
        }

        if (rightPanelTab === "stroke") {
          let styledPreviewPixels = cloneImageDataValue(basePreviewImageDataRef.current);
          const frameAdjustments = getImageAdjustments(previewSourceImage.id);
          if (!areAdjustmentsDefault(frameAdjustments)) {
            const frameSelectionMask = editScope === "selection"
              ? selectionMasksByImageIdRef.current[previewSourceImage.id]
              : undefined;
            styledPreviewPixels = await processImageDataWithAdjustments(
              styledPreviewPixels,
              frameAdjustments,
              frameSelectionMask
                ? scaleSelectionMaskToFrame(frameSelectionMask, previewWidth, previewHeight)
                : undefined,
            );
            if (isStaleLoad()) return;
          }

          const styledPreviewCanvas = document.createElement("canvas");
          styledPreviewCanvas.width = previewWidth;
          styledPreviewCanvas.height = previewHeight;
          const styledPreviewContext = styledPreviewCanvas.getContext("2d", { willReadFrequently: true });
          if (!styledPreviewContext) return;
          styledPreviewContext.putImageData(styledPreviewPixels, 0, 0);
          await ensureTextLayerFontsReady(textLayers);
          await drawOrderedImageEditLayersToCanvas(
            styledPreviewContext,
            textLayers,
            stickerLayers,
            layerOrder,
            previewSourceImage.id,
            previewWidth,
            previewHeight,
          );
          if (isStaleLoad()) return;
          const naturalWidth = img.naturalWidth || img.width;
          const naturalHeight = img.naturalHeight || img.height;
          strokePreviewSourcesRef.current = [{
            id: previewSourceImage.id,
            canvas: styledPreviewCanvas,
            naturalWidth,
            naturalHeight,
            sourceScale: scale,
          }];
          strokePreviewSourceSessionsRef.current.display = loadSession;
        }

        // Interactive and settled previews intentionally share the same pixel
        // dimensions. Keeping a separate low-resolution draft here caused the
        // worker result to be enlarged into the display buffer while dragging,
        // which was perceived as blur. A cloned reference is retained for the
        // existing request shape and history/bake transitions, but never
        // downscaled.
        draftPreviewImageDataRef.current = cloneImageDataValue(basePreviewImageDataRef.current);
        basePreviewImageIdRef.current = previewSourceImage.id;
        setPreviewSourceVersion((version) => version + 1);
      } catch {
        if (!isStaleLoad()) setMessage({ text: "E8101 编辑参数有误，请调整后重试", type: "error" });
      } finally {
        cleanup();
      }
    }

    loadPreviewSource();
    return () => {
      cancelled = true;
    };
  }, [
    editScope,
    ensureTextLayerFontsReady,
    getImageAdjustments,
    isStackPreview,
    open,
    processImageDataWithAdjustments,
    previewSourceImage?.id,
    previewSourceImage?.url,
    rightPanelTab,
    layerOrder,
    stickerLayers,
    stackPreviewStateRevision,
    textLayers,
    validImages,
  ]);

  useEffect(() => {
    if (!open || rightPanelTab !== "stroke") return;
    strokePreviewSessionRef.current += 1;
  }, [open, rightPanelTab]);

  useEffect(() => {
    if (!open || rightPanelTab !== "stroke") return;
    // Slider/Input changes collapse to one paint. Source canvases were prepared
    // by the async effect above; this frame only recomputes 400px geometry and
    // asks the display canvas to draw visible cached frames directly.
    const requestStrokePreviewFrame = () => {
      if (strokePreviewFrameRef.current !== null) return;
      strokePreviewFrameRef.current = requestAnimationFrame(() => {
        strokePreviewFrameRef.current = null;
        const activeStrokeSession = strokePreviewCacheSessionRef.current;
        const sources = strokePreviewSourceSessionsRef.current.display === activeStrokeSession
          ? strokePreviewSourcesRef.current
          : [];
        if (sources.length === 0) return;
        const sliderValues = sliderValuesRef.current;
        const layout = createStrokeStackPreviewLayout(
          sources.map((source) => ({
            id: source.id,
            width: source.naturalWidth,
            height: source.naturalHeight,
          })),
          sliderValues,
          STROKE_PREVIEW_FRAME_DISPLAY_WIDTH,
        );
        const renderedWidth = layout.width;
        const renderedHeight = layout.height;
        strokeStackDirectLayoutRef.current = layout;
        stackPreviewLayoutRef.current = isStackPreview ? layout : null;
        renderedPreviewImageDataRef.current = { width: renderedWidth, height: renderedHeight };
        setStrokePreviewBackend("canvas2d");
        setStrokePreviewMetrics({ width: renderedWidth, height: renderedHeight });

        const viewKey = `${strokePreviewSessionRef.current}:${isStackPreview ? "stack" : "single"}`;
        const previousTransform = canvasTransformRef.current;
        const previousDims = strokePreviewRenderedDimsRef.current;
        if (strokePreviewViewKeyRef.current !== viewKey || !previousDims) {
          strokePreviewViewKeyRef.current = viewKey;
          const transformed = getStrokePreviewFitTransform(
            renderedPreviewImageDataRef.current,
            canvasViewportSizeRef.current,
          );
          canvasTransformRef.current = transformed;
          setCanvasTransform(transformed);
        } else if (previousDims.width !== renderedWidth || previousDims.height !== renderedHeight) {
          const preserved = {
            scale: previousTransform.scale,
            offsetX: previousTransform.offsetX + ((previousDims.width - renderedWidth) / 2) * previousTransform.scale,
            offsetY: previousTransform.offsetY + ((previousDims.height - renderedHeight) / 2) * previousTransform.scale,
          };
          canvasTransformRef.current = preserved;
          setCanvasTransform(preserved);
        }
        strokePreviewRenderedDimsRef.current = { width: renderedWidth, height: renderedHeight };
        if (process.env.NODE_ENV !== "production") {
          (window as typeof window & { __FLOWX_STROKE_STACK_LAYOUT__?: typeof layout })
            .__FLOWX_STROKE_STACK_LAYOUT__ = layout;
        }
        drawCanvasPreview();
        drawSelectionOverlay();
      });
    };
    requestStrokePreviewFrameRef.current = requestStrokePreviewFrame;
    requestStrokePreviewFrame();

    return () => {
      requestStrokePreviewFrameRef.current = () => {};
      if (strokePreviewFrameRef.current !== null) {
        cancelAnimationFrame(strokePreviewFrameRef.current);
        strokePreviewFrameRef.current = null;
      }
    };
  }, [
    drawCanvasPreview,
    drawSelectionOverlay,
    isStackPreview,
    open,
    previewSourceVersion,
    rightPanelTab,
  ]);

  useEffect(() => {
    if (!open || !currentImage?.id) return;
    if (rightPanelTab === "stroke") return;
    if (basePreviewImageIdRef.current !== currentImage.id) return;

    const basePreview = basePreviewImageDataRef.current;
    const previewBuffer = previewBufferCanvasRef.current;
    if (!basePreview || !previewBuffer) return;
    const targetPreviewBuffer = previewBuffer;

    if (skipNextSelectionPreviewRef.current && previewSelectionMask) {
      skipNextSelectionPreviewRef.current = false;
      drawCanvasPreview();
      drawSelectionOverlay();
      return;
    }
    skipNextSelectionPreviewRef.current = false;

    previewProcessRequestRef.current = {
      imageId: currentImage.id,
      sourceImageData: basePreview,
      targetPreviewBuffer,
      adjustments,
      selectionMask: previewSelectionMask,
      viewport: canvasViewportSize,
      coordinateWidth: basePreview.width,
      coordinateHeight: basePreview.height,
      quality: "full",
    };
    scheduleLatestPreviewProcessing();
  }, [
    adjustments,
    canvasViewportSize,
    currentImage,
    currentImage?.id,
    drawCanvasPreview,
    drawSelectionOverlay,
    isStackPreview,
    open,
    previewSourceVersion,
    previewSelectionMask,
    rightPanelTab,
    scheduleLatestPreviewProcessing,
  ]);

  useEffect(() => {
    if (!open || editScope !== "selection" || !selectionOutlineVisible || !currentSelectionMask) {
      selectionDashOffsetRef.current = 0;
      drawSelectionOverlay(0);
      return;
    }

    let animationFrame = 0;
    let previousTime: number | null = null;

    const drawFrame = (time: number) => {
      const elapsed = previousTime === null ? 0 : Math.min(64, time - previousTime);
      previousTime = time;
      selectionDashOffsetRef.current =
        (selectionDashOffsetRef.current + elapsed * SELECTION_DASH_SPEED_PX_PER_MS) % SELECTION_DASH_PATTERN;
      drawSelectionOverlay(selectionDashOffsetRef.current);
      animationFrame = requestAnimationFrame(drawFrame);
      selectionAnimationFrameRef.current = animationFrame;
    };

    animationFrame = requestAnimationFrame(drawFrame);
    selectionAnimationFrameRef.current = animationFrame;
    return () => {
      cancelAnimationFrame(animationFrame);
      if (selectionAnimationFrameRef.current === animationFrame) {
        selectionAnimationFrameRef.current = null;
      }
    };
  }, [currentSelectionMask, drawSelectionOverlay, editScope, open, selectionOutlineVisible]);

  useEffect(() => {
    if (editScope === "selection" && selectionTool === "brush") return;
    clearCanvasCursorState({ wand: false });
  }, [clearCanvasCursorState, editScope, selectionTool]);

  useEffect(() => {
    if (editScope === "selection" && selectionTool === "wand") return;
    clearCanvasCursorState({ brush: false });
  }, [clearCanvasCursorState, editScope, selectionTool]);

  const showSelectionControls = mode === "batch";
  const canvasFitScale = rightPanelTab === "stroke"
    ? getStrokePreviewFitTransform(renderedPreviewImageDataRef.current, canvasViewportSize).scale
    : getFitCanvasTransform(renderedPreviewImageDataRef.current, canvasViewportSize).scale;
  const canvasZoomPercent = Math.round((canvasTransform.scale / Math.max(canvasFitScale, 0.01)) * 100);
  const effectiveCanvasPanMode = canvasPanMode || canvasShortcutPanMode;
  const showLayerEraserCursor =
    layerEraserActive &&
    !comparisonMode &&
    !effectiveCanvasPanMode &&
    layerEraserCursorPosition;
  const showBrushCursor =
    !comparisonMode &&
    !effectiveCanvasPanMode &&
    rightPanelTab === "color" &&
    editScope === "selection" &&
    selectionTool === "brush" &&
    brushCursorPosition;
  const activeBrushShortcutMode = editScope === "selection" && selectionTool === "brush" ? selectionShortcutMode : null;
  const showBrushModifierCursor =
    showBrushCursor && activeBrushShortcutMode
      ? { ...showBrushCursor, mode: activeBrushShortcutMode }
      : null;
  const brushModifierCursorOffset = Math.max(18, brushSize / 2 + 8);
  const showMagicWandCursor =
    !comparisonMode &&
    !canvasDragging &&
    !effectiveCanvasPanMode &&
    rightPanelTab === "color" &&
    editScope === "selection" &&
    selectionTool === "wand" &&
    wandCursorOverImage;
  const canvasCursorStyle = comparisonMode
    ? undefined
    : layerEraserActive
      ? ({ cursor: LAYER_ERASER_CURSOR } as CSSProperties)
    : eyeDropperActive && rightPanelTab === "font"
      ? ({ cursor: EYE_DROPPER_CURSOR } as CSSProperties)
      : showMagicWandCursor
        ? ({ cursor: MAGIC_WAND_CURSOR } as CSSProperties)
        : undefined;
  const activeWandShortcutMode = editScope === "selection" && selectionTool === "wand" ? selectionShortcutMode : null;
  const showWandModifierCursor =
    showMagicWandCursor && activeWandShortcutMode && wandCursorPosition
      ? { ...wandCursorPosition, mode: activeWandShortcutMode }
      : null;

  const getCanvasImagePoint = useCallback((clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    const sourceImageData = basePreviewImageDataRef.current;
    if (!canvas || !sourceImageData) return null;

    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    const transform = canvasTransformRef.current;
    if (transform.scale <= 0) return null;
    const x = (clientX - rect.left - transform.offsetX) / transform.scale;
    const y = (clientY - rect.top - transform.offsetY) / transform.scale;
    const stackLayout = stackPreviewLayoutRef.current;
    if (stackLayout) {
      const currentPixels = currentImage
        ? getImagePreviewPixels(
            currentImage,
            sourcePreviewImageDataByImageIdRef.current,
            bakedPreviewImageDataByImageIdRef.current,
          )
        : null;
      const mapped = currentPixels
        ? mapStackPointToFrame(stackLayout, x, y, currentPixels.width, currentPixels.height)
        : null;
      if (!mapped || mapped.frame.id !== currentImage?.id) return null;
      return { x: mapped.x, y: mapped.y };
    }
    if (x < 0 || y < 0 || x >= sourceImageData.width || y >= sourceImageData.height) return null;
    return {
      x: Math.min(sourceImageData.width - 1, Math.max(0, x)),
      y: Math.min(sourceImageData.height - 1, Math.max(0, y)),
    };
  }, [currentImage]);

  const handleStickerDragStart = useCallback((
    event: ReactDragEvent<HTMLButtonElement>,
    sticker: ImageEditSticker,
  ) => {
    event.dataTransfer.effectAllowed = "copy";
    event.dataTransfer.setData(STICKER_DRAG_MIME, sticker.uuid);
  }, []);

  const handleStickerCanvasDragOver = useCallback((event: ReactDragEvent<HTMLDivElement>) => {
    if (!Array.from(event.dataTransfer.types).includes(STICKER_DRAG_MIME)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  }, []);

  const addStickerLayerAtPoint = useCallback(async (
    sticker: ImageEditSticker,
    point?: { x: number; y: number } | null,
  ) => {
    if (!currentImage?.id) return;
    const targetImageId = currentImage.id;
    const coordinatePixels = getImagePreviewPixels(
      currentImage,
      sourcePreviewImageDataByImageIdRef.current,
      bakedPreviewImageDataByImageIdRef.current,
    );
    if (!coordinatePixels) return;

    let sourceWidth = Number(sticker.width);
    let sourceHeight = Number(sticker.height);
    if (!(sourceWidth > 0 && sourceHeight > 0)) {
      try {
        const { img, cleanup } = await loadImage(sticker.file_url);
        sourceWidth = img.naturalWidth || img.width;
        sourceHeight = img.naturalHeight || img.height;
        cleanup();
      } catch {
        sourceWidth = 1;
        sourceHeight = 1;
      }
    }
    const sourceAspect = sourceWidth > 0 && sourceHeight > 0 ? sourceWidth / sourceHeight : 1;
    // Fit the target image width; tall stickers retain their aspect ratio without shrinking to fit height.
    const width = coordinatePixels.width;
    const height = width / sourceAspect;
    // No drop point (panel click) targets the image center.
    const centerX = point?.x ?? coordinatePixels.width / 2;
    const centerY = point?.y ?? coordinatePixels.height / 2;
    const id = `sticker-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const layer: ImageEditStickerLayer = {
      id,
      imageId: targetImageId,
      stickerUuid: sticker.uuid,
      url: sticker.file_url,
      alt: sticker.file_name || "贴纸",
      x: Math.max(0, Math.min(coordinatePixels.width - width, centerX - width / 2)),
      y: Math.max(0, Math.min(coordinatePixels.height - height, centerY - height / 2)),
      width,
      height,
      coordinateWidth: coordinatePixels.width,
      coordinateHeight: coordinatePixels.height,
      rotation: 0,
    };
    setStickerLayers((layers) => [...layers, layer]);
    setLayerOrder((order) => [...order, { id, imageId: targetImageId, kind: "sticker" }]);
    setSelectedStickerLayerId(id);
    setSelectedTextLayerId(null);
  }, [currentImage]);

  const handleStickerCanvasDrop = useCallback((event: ReactDragEvent<HTMLDivElement>) => {
    const stickerUuid = event.dataTransfer.getData(STICKER_DRAG_MIME);
    if (!stickerUuid || !currentImage?.id) return;
    event.preventDefault();
    event.stopPropagation();
    const point = getCanvasImagePoint(event.clientX, event.clientY);
    const sticker = stickers.find((candidate) => candidate.uuid === stickerUuid);
    if (!point || !sticker) return;
    addStickerLayerAtPoint(sticker, point);
  }, [addStickerLayerAtPoint, currentImage, getCanvasImagePoint, stickers]);

  const handleStickerItemClick = useCallback((sticker: ImageEditSticker) => {
    addStickerLayerAtPoint(sticker);
  }, [addStickerLayerAtPoint]);

  const handleLayerOrderAction = useCallback((
    imageId: string,
    layerId: string,
    action: ImageEditLayerOrderAction,
  ) => {
    setLayerOrder((order) => moveImageEditLayer(order, imageId, layerId, action));
  }, []);

  const appendLayerErasePoint = useCallback((
    kind: "text" | "sticker",
    layerId: string,
    point: { x: number; y: number },
    startStroke: boolean,
  ) => {
    const updateLayer = <T extends {
      id: string;
      imageId: string;
      coordinateWidth: number;
      coordinateHeight: number;
      eraseStrokes?: LayerEraseStroke[];
    }>(layer: T): T => {
      if (layer.id !== layerId) return layer;
      const strokes = [...(layer.eraseStrokes ?? [])];
      const stackFrame = stackPreviewLayoutRef.current?.frames.find((frame) => frame.id === layer.imageId);
      const displayWidth = stackFrame?.width ?? renderedPreviewImageDataRef.current?.width ?? layer.coordinateWidth;
      const displayHeight = stackFrame?.height ?? renderedPreviewImageDataRef.current?.height ?? layer.coordinateHeight;
      const displayScale = Math.min(
        displayWidth / Math.max(1, layer.coordinateWidth),
        displayHeight / Math.max(1, layer.coordinateHeight),
      ) * Math.max(0.001, canvasTransformRef.current.scale);
      if (startStroke || strokes.length === 0) {
        strokes.push({ size: layerEraserSize / displayScale, points: [point] });
      } else {
        const lastIndex = strokes.length - 1;
        strokes[lastIndex] = { ...strokes[lastIndex], points: [...strokes[lastIndex].points, point] };
      }
      return { ...layer, eraseStrokes: strokes };
    };
    if (kind === "text") setTextLayers((layers) => layers.map(updateLayer));
    else setStickerLayers((layers) => layers.map(updateLayer));
  }, [layerEraserSize]);

  const beginLayerEraseStroke = useCallback((
    event: ReactPointerEvent<HTMLElement>,
    kind: "text" | "sticker",
    layerId: string,
  ) => {
    if (!layerEraserActive || event.button !== 0) return false;
    const point = getCanvasImagePoint(event.clientX, event.clientY);
    if (!point) return true;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    pushEditHistory();
    layerEraserStrokeOperationRef.current = { pointerId: event.pointerId, kind, layerId };
    appendLayerErasePoint(kind, layerId, point, true);
    return true;
  }, [appendLayerErasePoint, getCanvasImagePoint, layerEraserActive, pushEditHistory]);

  const continueLayerEraseStroke = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const operation = layerEraserStrokeOperationRef.current;
    if (!operation || operation.pointerId !== event.pointerId) return false;
    const point = getCanvasImagePoint(event.clientX, event.clientY);
    if (!point) return true;
    event.preventDefault();
    event.stopPropagation();
    appendLayerErasePoint(operation.kind, operation.layerId, point, false);
    return true;
  }, [appendLayerErasePoint, getCanvasImagePoint]);

  const finishLayerEraseStroke = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (layerEraserStrokeOperationRef.current?.pointerId !== event.pointerId) return false;
    layerEraserStrokeOperationRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    event.preventDefault();
    event.stopPropagation();
    return true;
  }, []);

  const handleStickerLayerPointerDown = useCallback((
    event: ReactPointerEvent<HTMLElement>,
    layer: ImageEditStickerLayer,
    mode: "move" | "resize" | "rotate",
    handle?: LayerResizeHandle,
  ) => {
    if (beginLayerEraseStroke(event, "sticker", layer.id)) return;
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const layerElement = event.currentTarget.closest<HTMLElement>("[data-image-edit-sticker-layer]")
      ?? event.currentTarget;
    // The resize handle moves with every frame. Capture on its stable layer
    // container so a continuous drag cannot lose pointer events mid-resize.
    layerElement.setPointerCapture(event.pointerId);
    const rect = layerElement.getBoundingClientRect();
    const centerClientX = rect.left + rect.width / 2;
    const centerClientY = rect.top + rect.height / 2;
    stickerLayerPointerOperationRef.current = {
      pointerId: event.pointerId,
      layerId: layer.id,
      mode,
      handle,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startX: layer.x,
      startY: layer.y,
      startWidth: layer.width,
      startHeight: layer.height,
      startRotation: layer.rotation,
      centerClientX,
      centerClientY,
      startPointerAngle: rotationFromPointer(centerClientX, centerClientY, event.clientX, event.clientY, 0),
    };
    setSelectedStickerLayerId(layer.id);
    setSelectedTextLayerId(null);
  }, [beginLayerEraseStroke]);

  const handleStickerLayerPointerMove = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (continueLayerEraseStroke(event)) return;
    const operation = stickerLayerPointerOperationRef.current;
    if (!operation || operation.pointerId !== event.pointerId) return;
    event.preventDefault();
    event.stopPropagation();
    const layer = stickerLayers.find((candidate) => candidate.id === operation.layerId);
    if (!layer) return;
    const transformScale = Math.max(0.001, canvasTransformRef.current.scale);
    const imageWidth = renderedPreviewImageDataRef.current?.width ?? layer.coordinateWidth;
    const imageHeight = renderedPreviewImageDataRef.current?.height ?? layer.coordinateHeight;
    const sourcePerClientX = layer.coordinateWidth / Math.max(1, imageWidth * transformScale);
    const sourcePerClientY = layer.coordinateHeight / Math.max(1, imageHeight * transformScale);
    const dx = (event.clientX - operation.startClientX) * sourcePerClientX;
    const dy = (event.clientY - operation.startClientY) * sourcePerClientY;
    setStickerLayers((layers) => layers.map((candidate) => {
      if (candidate.id !== operation.layerId) return candidate;
      if (operation.mode === "move") {
        const boundedPosition = clampLayerPositionToCanvasViewport({
          desiredX: operation.startX + dx,
          desiredY: operation.startY + dy,
          layerWidth: candidate.width,
          layerHeight: candidate.height,
          coordinateWidth: candidate.coordinateWidth,
          coordinateHeight: candidate.coordinateHeight,
          displayWidth: imageWidth,
          displayHeight: imageHeight,
          viewportWidth: canvasViewportSizeRef.current.width,
          viewportHeight: canvasViewportSizeRef.current.height,
          canvasScale: transformScale,
          offsetX: canvasTransformRef.current.offsetX,
          offsetY: canvasTransformRef.current.offsetY,
        });
        return { ...candidate, ...boundedPosition };
      }
      if (operation.mode === "rotate") {
        const pointerAngle = rotationFromPointer(
          operation.centerClientX,
          operation.centerClientY,
          event.clientX,
          event.clientY,
          0,
        );
        return { ...candidate, rotation: operation.startRotation + pointerAngle - operation.startPointerAngle };
      }
      if (!operation.handle) return candidate;
      const resized = resizeLayerFromHandle(
        {
          x: operation.startX,
          y: operation.startY,
          width: operation.startWidth,
          height: operation.startHeight,
          rotation: operation.startRotation,
        },
        operation.handle,
        dx,
        dy,
        MIN_STICKER_LAYER_SIZE,
        { proportional: isCornerResizeHandle(operation.handle) || event.shiftKey },
      );
      return { ...candidate, ...resized };
    }));
  }, [continueLayerEraseStroke, stickerLayers]);

  const handleStickerLayerPointerUp = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (finishLayerEraseStroke(event)) return;
    if (stickerLayerPointerOperationRef.current?.pointerId !== event.pointerId) return;
    stickerLayerPointerOperationRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, [finishLayerEraseStroke]);

  const handleTextLayerPointerDown = useCallback((
    event: ReactPointerEvent<HTMLElement>,
    layer: ImageEditTextLayer,
    mode: "move" | "resize" | "rotate",
    handle?: LayerResizeHandle,
  ) => {
    if (beginLayerEraseStroke(event, "text", layer.id)) return;
    if (event.button !== 0 || (rightPanelTab !== "color" && rightPanelTab !== "font" && rightPanelTab !== "sticker")) return;
    event.preventDefault();
    event.stopPropagation();
    const layerElement = event.currentTarget.closest<HTMLElement>("[data-image-edit-text-preview]")
      ?? event.currentTarget;
    // The resize handle moves with every frame. Capture on its stable layer
    // container so a continuous drag cannot lose pointer events mid-resize.
    layerElement.setPointerCapture(event.pointerId);
    const rect = layerElement.getBoundingClientRect();
    const centerClientX = rect.left + rect.width / 2;
    const centerClientY = rect.top + rect.height / 2;
    textLayerPointerOperationRef.current = {
      pointerId: event.pointerId,
      layerId: layer.id,
      mode,
      handle,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startX: layer.x,
      startY: layer.y,
      startWidth: layer.width,
      startHeight: layer.height,
      startRotation: layer.rotation,
      centerClientX,
      centerClientY,
      startPointerAngle: rotationFromPointer(centerClientX, centerClientY, event.clientX, event.clientY, 0),
      startStyle: layer.style,
      startCoordinateWidth: layer.coordinateWidth,
      startCoordinateHeight: layer.coordinateHeight,
    };
    selectTextLayer(layer);
  }, [beginLayerEraseStroke, rightPanelTab, selectTextLayer]);

  const handleTextLayerPointerMove = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (continueLayerEraseStroke(event)) return;
    const operation = textLayerPointerOperationRef.current;
    if (!operation || operation.pointerId !== event.pointerId) return;
    event.preventDefault();
    event.stopPropagation();
    if (operation.mode === "resize" && operation.handle) {
      const transformScale = Math.max(0.001, canvasTransformRef.current.scale);
      const imageWidth = renderedPreviewImageDataRef.current?.width ?? operation.startCoordinateWidth;
      const imageHeight = renderedPreviewImageDataRef.current?.height ?? operation.startCoordinateHeight;
      const dx = (event.clientX - operation.startClientX)
        * operation.startCoordinateWidth / Math.max(1, imageWidth * transformScale);
      const dy = (event.clientY - operation.startClientY)
        * operation.startCoordinateHeight / Math.max(1, imageHeight * transformScale);
      // 边中点默认单轴调整文字框；Shift 锁定比例，角点保留等比缩放。
      const proportional = isCornerResizeHandle(operation.handle) || event.shiftKey;
      const resized = resizeLayerFromHandle(
        {
          x: operation.startX,
          y: operation.startY,
          width: operation.startWidth,
          height: operation.startHeight,
          rotation: operation.startRotation,
        },
        operation.handle,
        dx,
        dy,
        MIN_TEXT_LAYER_SIZE,
        { proportional },
      );
      const fontScale = proportional ? resized.width / Math.max(1, operation.startWidth) : 1;
      // lineHeight 为 0 表示自动行高（fontSize*1.2），随 fontSize 缩放即可；显式行高按同比例缩放。
      // 字号与行高取整：每帧都从 startStyle 基准重算，取整不会累积漂移，右侧面板也不出现长小数。
      const scaledStyle: TextLayerStyle = proportional ? {
        ...operation.startStyle,
        fontSize: Math.max(1, Math.round(operation.startStyle.fontSize * fontScale)),
        lineHeight: operation.startStyle.lineHeight > 0
          ? Math.max(1, Math.round(operation.startStyle.lineHeight * fontScale))
          : 0,
      } : operation.startStyle;
      setTextLayers((layers) => layers.map((candidate) => (
        candidate.id === operation.layerId
          ? { ...candidate, ...resized, style: scaledStyle }
          : candidate
      )));
      setTextStyle(scaledStyle);
      return;
    }
    setTextLayers((layers) => layers.map((candidate) => {
      if (candidate.id !== operation.layerId) return candidate;
      if (operation.mode === "rotate") {
        const pointerAngle = rotationFromPointer(
          operation.centerClientX,
          operation.centerClientY,
          event.clientX,
          event.clientY,
          0,
        );
        return { ...candidate, rotation: operation.startRotation + pointerAngle - operation.startPointerAngle };
      }
      const transformScale = Math.max(0.001, canvasTransformRef.current.scale);
      const imageWidth = renderedPreviewImageDataRef.current?.width ?? candidate.coordinateWidth;
      const imageHeight = renderedPreviewImageDataRef.current?.height ?? candidate.coordinateHeight;
      const dx = (event.clientX - operation.startClientX)
        * candidate.coordinateWidth / Math.max(1, imageWidth * transformScale);
      const dy = (event.clientY - operation.startClientY)
        * candidate.coordinateHeight / Math.max(1, imageHeight * transformScale);
      const boundedPosition = clampLayerPositionToCanvasViewport({
        desiredX: operation.startX + dx,
        desiredY: operation.startY + dy,
        layerWidth: candidate.width,
        layerHeight: candidate.height,
        coordinateWidth: candidate.coordinateWidth,
        coordinateHeight: candidate.coordinateHeight,
        displayWidth: imageWidth,
        displayHeight: imageHeight,
        viewportWidth: canvasViewportSizeRef.current.width,
        viewportHeight: canvasViewportSizeRef.current.height,
        canvasScale: transformScale,
        offsetX: canvasTransformRef.current.offsetX,
        offsetY: canvasTransformRef.current.offsetY,
      });
      return { ...candidate, ...boundedPosition };
    }));
  }, [continueLayerEraseStroke]);

  const handleTextLayerPointerUp = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (finishLayerEraseStroke(event)) return;
    if (textLayerPointerOperationRef.current?.pointerId !== event.pointerId) return;
    textLayerPointerOperationRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, [finishLayerEraseStroke]);

  const updateFontCursorFromPointer = useCallback((event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (rightPanelTab !== "font" || layerEraserActive || eyeDropperActive || effectiveCanvasPanMode || comparisonModeRef.current) {
      setFontCursorPosition(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    setFontCursorPosition({ x: event.clientX - rect.left, y: event.clientY - rect.top });
  }, [eyeDropperActive, effectiveCanvasPanMode, layerEraserActive, rightPanelTab]);

  const getEyeDropperSample = useCallback((event: ReactPointerEvent<HTMLCanvasElement>): EyeDropperPreview | null => {
    if (!eyeDropperActive || rightPanelTab !== "font") return null;
    const point = getCanvasImagePoint(event.clientX, event.clientY);
    const previewPixels = previewBufferCanvasRef.current;
    if (!point || !previewPixels) return null;
    const ctx = previewPixels.getContext("2d", { willReadFrequently: true });
    if (!ctx) return null;
    const x = Math.min(previewPixels.width - 1, Math.max(0, Math.floor(point.x)));
    const y = Math.min(previewPixels.height - 1, Math.max(0, Math.floor(point.y)));
    try {
      const pixel = ctx.getImageData(x, y, 1, 1).data;
      const color = `#${[pixel[0], pixel[1], pixel[2]]
        .map((channel) => channel.toString(16).padStart(2, "0"))
        .join("")}`.toUpperCase();
      const rect = event.currentTarget.getBoundingClientRect();
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
        sampleX: x,
        sampleY: y,
        color,
        rgb: `RGB ${pixel[0]} ${pixel[1]} ${pixel[2]}`,
      };
    } catch {
      return null;
    }
  }, [eyeDropperActive, getCanvasImagePoint, rightPanelTab]);

  const updateEyeDropperFromPointer = useCallback((event: ReactPointerEvent<HTMLCanvasElement>) => {
    setEyeDropperPreview(getEyeDropperSample(event));
  }, [getEyeDropperSample]);

  useEffect(() => {
    const canvas = eyeDropperMagnifierCanvasRef.current;
    const source = previewBufferCanvasRef.current;
    if (!canvas || !source || !eyeDropperPreview) return;
    const context = canvas.getContext("2d");
    if (!context) return;
    const sampleSize = 9;
    const sampleHalf = Math.floor(sampleSize / 2);
    canvas.width = 40;
    canvas.height = 40;
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.imageSmoothingEnabled = false;
    context.drawImage(
      source,
      eyeDropperPreview.sampleX - sampleHalf,
      eyeDropperPreview.sampleY - sampleHalf,
      sampleSize,
      sampleSize,
      0,
      0,
      canvas.width,
      canvas.height,
    );
  }, [eyeDropperPreview]);

  const applyMagicWandAtPoint = useCallback((
    clientX: number,
    clientY: number,
    explicitShortcutMode?: SelectionShortcutMode | null
  ) => {
    if (editScope !== "selection" || selectionTool !== "wand" || !currentImage?.id) return;
    const point = getCanvasImagePoint(clientX, clientY);
    if (!point) return;

    const sourceImageData = currentImage
      ? getImagePreviewPixels(
          currentImage,
          sourcePreviewImageDataByImageIdRef.current,
          bakedPreviewImageDataByImageIdRef.current,
        ) ?? basePreviewImageDataRef.current
      : basePreviewImageDataRef.current;
    if (!sourceImageData) return;
    const nextMask = createMagicWandMask(sourceImageData, point.x, point.y, wandTolerance);
    const shortcutMode = explicitShortcutMode === undefined ? selectionShortcutMode : explicitShortcutMode;
    const combineMode = shortcutMode ?? normalizeSelectionCombineMode(selectionCombineMode);
    pushEditHistory();
    workerMaskCacheRef.current = null;
    setSmartSelectionActiveRegion(null);
    setSelectionMasksByImageId((prev) => {
      const combinedMask = combineSelectionMasks(prev[currentImage.id], nextMask, combineMode);
      currentSelectionMaskRef.current = combinedMask;
      const next = {
        ...prev,
        [currentImage.id]: combinedMask,
      };
      selectionMasksByImageIdRef.current = next;
      return next;
    });
    setMessage(null);
  }, [
    currentImage,
    editScope,
    getCanvasImagePoint,
    pushEditHistory,
    selectionCombineMode,
    selectionShortcutMode,
    selectionTool,
    wandTolerance,
  ]);

  const getRawCanvasImagePoint = useCallback((clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    const transform = canvasTransformRef.current;
    if (transform.scale <= 0) return null;
    const x = (clientX - rect.left - transform.offsetX) / transform.scale;
    const y = (clientY - rect.top - transform.offsetY) / transform.scale;
    const stackLayout = stackPreviewLayoutRef.current;
    if (!stackLayout || !currentImage) return { x, y };
    const currentPixels = getImagePreviewPixels(
      currentImage,
      sourcePreviewImageDataByImageIdRef.current,
      bakedPreviewImageDataByImageIdRef.current,
    );
    if (!currentPixels) return null;
    const mapped = mapStackPointToFrame(stackLayout, x, y, currentPixels.width, currentPixels.height);
    if (!mapped || mapped.frame.id !== currentImage.id) return null;
    return { x: mapped.x, y: mapped.y };
  }, [currentImage]);

  const updateComparisonSplitFromPointer = useCallback((clientX: number, clientY: number) => {
    const renderedPreview = renderedPreviewImageDataRef.current;
    if (!renderedPreview || renderedPreview.width <= 0) return false;

    const point = getRawCanvasImagePoint(clientX, clientY);
    if (!point) return false;

    const nextSplit = Math.min(1, Math.max(0, point.x / renderedPreview.width));
    comparisonSplitRef.current = nextSplit;
    setComparisonSplit((current) => (
      Math.abs(current - nextSplit) < 0.001 ? current : nextSplit
    ));
    drawCanvasPreview();
    drawSelectionOverlay();
    return true;
  }, [drawCanvasPreview, drawSelectionOverlay, getRawCanvasImagePoint]);

  const handleComparisonModeToggle = useCallback(() => {
    const nextMode = !comparisonModeRef.current;
    comparisonModeRef.current = nextMode;
    comparisonDragPointerRef.current = null;
    setComparisonMode(nextMode);
    setComparisonDragging(false);
    if (nextMode) {
      comparisonSplitRef.current = 0.5;
      setComparisonSplit(0.5);
      clearCanvasCursorState();
    }
    requestAnimationFrame(() => {
      drawCanvasPreview();
      drawSelectionOverlay();
    });
  }, [clearCanvasCursorState, drawCanvasPreview, drawSelectionOverlay]);

  const getBrushRadius = useCallback(() => {
    const scale = canvasTransformRef.current.scale || 1;
    return Math.max(0.5, brushSize / 2 / scale);
  }, [brushSize]);

  const updateBrushCursorPosition = useCallback((event: ReactPointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    pendingBrushCursorRef.current = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
    scheduleCursorStateUpdate();
  }, [scheduleCursorStateUpdate]);

  const updateWandCursorPosition = useCallback((event: ReactPointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    pendingWandCursorRef.current = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
      overImage: Boolean(getCanvasImagePoint(event.clientX, event.clientY)),
    };
    scheduleCursorStateUpdate();
  }, [getCanvasImagePoint, scheduleCursorStateUpdate]);

  const beginBrushStroke = useCallback((
    clientX: number,
    clientY: number,
    explicitShortcutMode?: SelectionShortcutMode | null
  ) => {
    if (!currentImage?.id) return;
    const base = currentImage
      ? getImagePreviewPixels(
          currentImage,
          sourcePreviewImageDataByImageIdRef.current,
          bakedPreviewImageDataByImageIdRef.current,
        ) ?? basePreviewImageDataRef.current
      : basePreviewImageDataRef.current;
    if (!base) return;
    const point = getRawCanvasImagePoint(clientX, clientY);
    if (!point) return;

    const shortcutMode = explicitShortcutMode === undefined ? selectionShortcutMode : explicitShortcutMode;
    const combineMode = shortcutMode ?? normalizeSelectionCombineMode(selectionCombineMode);
    const existing = selectionMasksByImageIdRef.current[currentImage.id];
    const strokeMask =
      existing && existing.width === base.width && existing.height === base.height
        ? cloneSelectionMask(existing)
        : createEmptySelectionMask(base.width, base.height);

    const erase = combineMode === "subtract";
    const radius = getBrushRadius();
    const previewLineWidth = Math.max(1, radius * 2);
    const previewPathData = createSvgLinePath(point.x, point.y, point.x + 0.01, point.y);
    pushEditHistory();
    paintExactSelectionBrushDab(strokeMask, point.x, point.y, radius, erase);
    brushStrokeRef.current = {
      active: true,
      lastX: point.x,
      lastY: point.y,
      mask: strokeMask,
      previewPathData,
      previewLineWidth,
      erase,
    };
    setMessage(null);
    scheduleBrushPreview();
  }, [
    currentImage,
    getBrushRadius,
    getRawCanvasImagePoint,
    pushEditHistory,
    scheduleBrushPreview,
    selectionCombineMode,
    selectionShortcutMode,
  ]);

  const continueBrushStroke = useCallback((clientX: number, clientY: number) => {
    const stroke = brushStrokeRef.current;
    if (!stroke?.active) return;
    const point = getRawCanvasImagePoint(clientX, clientY);
    if (!point) return;

    const radius = getBrushRadius();
    const dx = point.x - stroke.lastX;
    const dy = point.y - stroke.lastY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const step = Math.max(1, radius / 2);
    const steps = Math.max(1, Math.ceil(distance / step));
    for (let i = 1; i <= steps; i += 1) {
      const t = i / steps;
      paintExactSelectionBrushDab(
        stroke.mask,
        stroke.lastX + dx * t,
        stroke.lastY + dy * t,
        radius,
        stroke.erase
      );
    }
    stroke.lastX = point.x;
    stroke.lastY = point.y;
    stroke.previewPathData += ` L ${formatPathNumber(point.x)} ${formatPathNumber(point.y)}`;
    scheduleBrushPreview();
  }, [getBrushRadius, getRawCanvasImagePoint, scheduleBrushPreview]);

  const finishBrushStroke = useCallback(() => {
    const stroke = brushStrokeRef.current;
    if (!stroke?.active) return false;
    const committedMask = stroke.mask;
    brushStrokeRef.current = null;
    if (brushPreviewFrameRef.current !== null) {
      cancelAnimationFrame(brushPreviewFrameRef.current);
      brushPreviewFrameRef.current = null;
    }
    if (!currentImage?.id) return true;
    currentSelectionMaskRef.current = committedMask;
    selectionOutlinePathCacheRef.current = null;
    workerMaskCacheRef.current = null;
    setSmartSelectionActiveRegion(null);
    setSelectionMasksByImageId((prev) => {
      const next = {
        ...prev,
        [currentImage.id]: committedMask,
      };
      selectionMasksByImageIdRef.current = next;
      return next;
    });
    drawSelectionOverlay();
    return true;
  }, [currentImage?.id, drawSelectionOverlay]);

  const handleCanvasWheel = useCallback((event: WheelEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    event.preventDefault();
    const rect = canvas.getBoundingClientRect();
    const zoomFactor = event.deltaY > 0 ? 0.92 : 1.08;
    updateCanvasZoom(canvasTransformRef.current.scale * zoomFactor, {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    });
  }, [updateCanvasZoom]);

  useEffect(() => {
    if (!open) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.addEventListener("wheel", handleCanvasWheel, { passive: false });
    return () => canvas.removeEventListener("wheel", handleCanvasWheel);
  }, [handleCanvasWheel, open]);

  const handleCanvasPointerDown = useCallback((event: ReactPointerEvent<HTMLCanvasElement>) => {
    const isLeftButton = event.button === 0;
    const isMiddleButton = event.button === 1;
    if (!isLeftButton && !isMiddleButton) return;
    event.preventDefault();

    if (isLeftButton && canvasShortcutPanMode) {
      event.currentTarget.setPointerCapture(event.pointerId);
      clearCanvasCursorState();
      dragStartRef.current = {
        clientX: event.clientX,
        clientY: event.clientY,
        offsetX: canvasTransformRef.current.offsetX,
        offsetY: canvasTransformRef.current.offsetY,
        allowWand: false,
        wandShortcutMode: null,
      };
      setCanvasDragging(true);
      return;
    }
    if (isLeftButton && rightPanelTab === "font" && eyeDropperActive && !effectiveCanvasPanMode) {
      const sample = getEyeDropperSample(event);
      if (sample) {
        updateTextStyle({ color: sample.color });
        setEyeDropperPreview(sample);
      }
      return;
    }
    if (isLeftButton && layerEraserActive) {
      clearCanvasCursorState();
      return;
    }
    // 图层外的画布点击是同一个 dismiss 动作：输入态和选中态必须一次清空。
    // 文字/贴纸层、控制柄及图层工具栏会阻止事件冒泡，右侧属性控件也不经过此 handler。
    if (isLeftButton && (editingTextLayerId || selectedTextLayerId || selectedStickerLayerId)) {
      finishTextLayerEditing(editingTextLayerId);
      setSelectedTextLayerId(null);
      setSelectedStickerLayerId(null);
      return;
    }
    if (isLeftButton && rightPanelTab === "font" && !effectiveCanvasPanMode && currentImage?.id) {
      const point = getCanvasImagePoint(event.clientX, event.clientY);
      const coordinatePixels = getImagePreviewPixels(
        currentImage,
        sourcePreviewImageDataByImageIdRef.current,
        bakedPreviewImageDataByImageIdRef.current,
      );
      if (point && coordinatePixels) {
        const id = `text-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const layer: ImageEditTextLayer = {
          id,
          imageId: currentImage.id,
          text: "",
          x: point.x,
          y: point.y,
          coordinateWidth: coordinatePixels.width,
          coordinateHeight: coordinatePixels.height,
          width: Math.max(80, Math.min(260, coordinatePixels.width - point.x)),
          height: Math.max(MIN_TEXT_LAYER_SIZE, resolveTextLineHeight(textStyle) + 8),
          rotation: 0,
          style: { ...textStyle },
        };
        setTextLayers((layers) => [...layers, layer]);
        setLayerOrder((order) => [...order, { id, imageId: currentImage.id, kind: "text" }]);
        setSelectedTextLayerId(id);
        setEditingTextLayerId(id);
      }
      return;
    }
    if (isLeftButton && comparisonModeRef.current) {
      event.currentTarget.setPointerCapture(event.pointerId);
      comparisonDragPointerRef.current = event.pointerId;
      setComparisonDragging(true);
      clearCanvasCursorState();
      updateComparisonSplitFromPointer(event.clientX, event.clientY);
      return;
    }
    if (isLeftButton && stackPreviewLayoutRef.current) {
      const canvas = canvasRef.current;
      const rect = canvas?.getBoundingClientRect();
      const transform = canvasTransformRef.current;
      if (canvas && rect && transform.scale > 0) {
        const frame = hitTestStackPreviewFrame(
          stackPreviewLayoutRef.current,
          (event.clientX - rect.left - transform.offsetX) / transform.scale,
          (event.clientY - rect.top - transform.offsetY) / transform.scale,
        );
        if (frame && frame.id !== currentImage?.id) {
          const item = validImages.find((candidate) => candidate.id === frame.id);
          if (item) {
            setCurrentId(item.id);
            setSelectedIds(new Set([item.id]));
            setSmartSelectionActiveRegion(null);
            applyAdjustmentsToControls(getImageAdjustments(item.id));
          }
          return;
        }
      }
    }
    if (isLeftButton && (rightPanelTab === "sticker" || rightPanelTab === "stroke")) {
      clearCanvasCursorState();
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    updateBrushCursorPosition(event);
    if (editScope === "selection" && selectionTool === "wand") {
      updateWandCursorPosition(event);
    }

    const isBrushStroke = isLeftButton && !effectiveCanvasPanMode && editScope === "selection" && selectionTool === "brush";
    if (isBrushStroke) {
      beginBrushStroke(event.clientX, event.clientY, getSelectionShortcutMode(selectionShortcutKeysRef.current));
      return;
    }

    dragStartRef.current = {
      clientX: event.clientX,
      clientY: event.clientY,
      offsetX: canvasTransformRef.current.offsetX,
      offsetY: canvasTransformRef.current.offsetY,
      allowWand: isLeftButton && !effectiveCanvasPanMode,
      wandShortcutMode: getSelectionShortcutMode(selectionShortcutKeysRef.current),
    };
    setCanvasDragging(true);
  }, [
    applyAdjustmentsToControls,
    beginBrushStroke,
    canvasShortcutPanMode,
    currentImage,
    editScope,
    editingTextLayerId,
    effectiveCanvasPanMode,
    eyeDropperActive,
    clearCanvasCursorState,
    finishTextLayerEditing,
    getEyeDropperSample,
    getImageAdjustments,
    getCanvasImagePoint,
    layerEraserActive,
    rightPanelTab,
    selectedStickerLayerId,
    selectedTextLayerId,
    selectionTool,
    textStyle,
    updateBrushCursorPosition,
    updateComparisonSplitFromPointer,
    updateTextStyle,
    updateWandCursorPosition,
    validImages,
  ]);

  const handleCanvasPointerMove = useCallback((event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (layerEraserActive) {
      const rect = event.currentTarget.getBoundingClientRect();
      setLayerEraserCursorPosition({ x: event.clientX - rect.left, y: event.clientY - rect.top });
    }
    updateFontCursorFromPointer(event);
    if (eyeDropperActive && rightPanelTab === "font") {
      updateEyeDropperFromPointer(event);
      return;
    }

    if (comparisonDragPointerRef.current === event.pointerId) {
      event.preventDefault();
      updateComparisonSplitFromPointer(event.clientX, event.clientY);
      return;
    }

    if (rightPanelTab === "color" && editScope === "selection" && selectionTool === "brush") {
      updateBrushCursorPosition(event);
    }
    if (rightPanelTab === "color" && editScope === "selection" && selectionTool === "wand") {
      updateWandCursorPosition(event);
    }

    if (brushStrokeRef.current?.active) {
      event.preventDefault();
      continueBrushStroke(event.clientX, event.clientY);
      return;
    }

    const dragStart = dragStartRef.current;
    if (!dragStart) return;
    event.preventDefault();
    const dx = event.clientX - dragStart.clientX;
    const dy = event.clientY - dragStart.clientY;
    canvasTransformRef.current = {
      ...canvasTransformRef.current,
      offsetX: dragStart.offsetX + dx,
      offsetY: dragStart.offsetY + dy,
    };
    scheduleCanvasRedraw();
  }, [
    continueBrushStroke,
    editScope,
    eyeDropperActive,
    layerEraserActive,
    rightPanelTab,
    scheduleCanvasRedraw,
    selectionTool,
    updateBrushCursorPosition,
    updateComparisonSplitFromPointer,
    updateEyeDropperFromPointer,
    updateWandCursorPosition,
    updateFontCursorFromPointer,
  ]);

  const handleCanvasPointerEnter = useCallback((event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (layerEraserActive) {
      const rect = event.currentTarget.getBoundingClientRect();
      setLayerEraserCursorPosition({ x: event.clientX - rect.left, y: event.clientY - rect.top });
    }
    updateFontCursorFromPointer(event);
    if (eyeDropperActive && rightPanelTab === "font") {
      updateEyeDropperFromPointer(event);
      return;
    }
    if (rightPanelTab === "color" && editScope === "selection" && selectionTool === "brush") {
      updateBrushCursorPosition(event);
    }
    if (rightPanelTab === "color" && editScope === "selection" && selectionTool === "wand") {
      updateWandCursorPosition(event);
    }
  }, [editScope, eyeDropperActive, layerEraserActive, rightPanelTab, selectionTool, updateBrushCursorPosition, updateEyeDropperFromPointer, updateFontCursorFromPointer, updateWandCursorPosition]);

  const handleCanvasPointerLeave = useCallback(() => {
    setFontCursorPosition(null);
    setLayerEraserCursorPosition(null);
    setEyeDropperPreview(null);
    if (!brushStrokeRef.current?.active) {
      pendingBrushCursorRef.current = null;
    }
    pendingWandCursorRef.current = null;
    scheduleCursorStateUpdate();
  }, [scheduleCursorStateUpdate]);

  const handleCanvasPointerUp = useCallback((event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (comparisonDragPointerRef.current === event.pointerId) {
      event.preventDefault();
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      comparisonDragPointerRef.current = null;
      setComparisonDragging(false);
      updateComparisonSplitFromPointer(event.clientX, event.clientY);
      return;
    }

    if (brushStrokeRef.current?.active) {
      event.preventDefault();
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      finishBrushStroke();
      return;
    }

    const dragStart = dragStartRef.current;
    if (!dragStart) return;
    dragStartRef.current = null;
    setCanvasDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    const dx = event.clientX - dragStart.clientX;
    const dy = event.clientY - dragStart.clientY;
    const movedDistance = Math.sqrt(dx * dx + dy * dy);
    if (movedDistance <= CANVAS_DRAG_THRESHOLD) {
      const restoredTransform = {
        ...canvasTransformRef.current,
        offsetX: dragStart.offsetX,
        offsetY: dragStart.offsetY,
      };
      canvasTransformRef.current = restoredTransform;
      setCanvasTransform(restoredTransform);
      if (dragStart.allowWand) {
        applyMagicWandAtPoint(event.clientX, event.clientY, dragStart.wandShortcutMode);
      }
    } else {
      setCanvasTransform(canvasTransformRef.current);
    }
  }, [applyMagicWandAtPoint, finishBrushStroke, updateComparisonSplitFromPointer]);

  const handleCanvasPointerCancel = useCallback((event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (comparisonDragPointerRef.current === event.pointerId) {
      comparisonDragPointerRef.current = null;
      setComparisonDragging(false);
      return;
    }
    if (brushStrokeRef.current?.active) {
      finishBrushStroke();
      return;
    }
    dragStartRef.current = null;
    setCanvasDragging(false);
    setCanvasTransform(canvasTransformRef.current);
  }, [finishBrushStroke]);

  // Capture at the viewport instead of the base canvas. This keeps Ctrl/Cmd
  // guide dragging available when a text or sticker layer is under the pointer.
  const handleGuidePointerDownCapture = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (layerEraserActive) {
      const rect = event.currentTarget.getBoundingClientRect();
      setLayerEraserCursorPosition({ x: event.clientX - rect.left, y: event.clientY - rect.top });
    }
    if (event.button !== 0 || !(event.ctrlKey || event.metaKey) || layerEraserActive) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const screenX = event.clientX - rect.left;
    const screenY = event.clientY - rect.top;
    const transform = canvasTransformRef.current;
    const nearbyGuide = findNearbyGuide(guides, "horizontal", screenY, transform)
      ?? findNearbyGuide(guides, "vertical", screenX, transform);
    if (!nearbyGuide) return;

    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    activeGuideDragRef.current = {
      id: nearbyGuide.id,
      type: nearbyGuide.type,
      pointerId: event.pointerId,
      startPointerClient: nearbyGuide.type === "horizontal" ? event.clientY : event.clientX,
      startPosition: nearbyGuide.position,
    };
    setGuideMoveCursorActive(true);
  }, [guides, layerEraserActive]);

  const handleGuidePointerMoveCapture = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (layerEraserActive) {
      setLayerEraserCursorPosition({
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      });
    }
    guideModifierPressedRef.current = event.ctrlKey || event.metaKey;
    updateGuideMoveCursorAtPointer(
      event.clientX - rect.left,
      event.clientY - rect.top,
      guideModifierPressedRef.current,
    );
    const activeGuideDrag = activeGuideDragRef.current;
    if (!activeGuideDrag || activeGuideDrag.pointerId !== event.pointerId) return;

    event.preventDefault();
    event.stopPropagation();
    const currentPointerClient = activeGuideDrag.type === "horizontal" ? event.clientY : event.clientX;
    const newCanvasPos = activeGuideDrag.startPosition + guidePointerDeltaToCanvasDelta(
      currentPointerClient - activeGuideDrag.startPointerClient,
      canvasTransformRef.current.scale,
    );
    setGuides((previous) => previous.map((guide) => (
      guide.id === activeGuideDrag.id ? { ...guide, position: newCanvasPos } : guide
    )));
  }, [layerEraserActive, updateGuideMoveCursorAtPointer]);

  const handleGuidePointerLeave = useCallback(() => {
    guidePointerPositionRef.current = null;
    setLayerEraserCursorPosition(null);
    if (!activeGuideDragRef.current) setGuideMoveCursorActive(false);
  }, []);

  const finishGuidePointerCapture = useCallback((event: ReactPointerEvent<HTMLDivElement>, removeWhenOutside: boolean) => {
    const activeGuideDrag = activeGuideDragRef.current;
    if (!activeGuideDrag || activeGuideDrag.pointerId !== event.pointerId) return;

    event.preventDefault();
    event.stopPropagation();
    let removedGuide = false;
    if (removeWhenOutside) {
      const rect = event.currentTarget.getBoundingClientRect();
      const screenPos = activeGuideDrag.type === "horizontal"
        ? event.clientY - rect.top
        : event.clientX - rect.left;
      if (screenPos < 0) {
        removedGuide = true;
        setGuides((previous) => previous.filter((guide) => guide.id !== activeGuideDrag.id));
      }
    }
    activeGuideDragRef.current = null;
    guideModifierPressedRef.current = event.ctrlKey || event.metaKey;
    setGuideMoveCursorActive(
      removeWhenOutside && !removedGuide && guideModifierPressedRef.current,
    );
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const handleGuidePointerUpCapture = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    finishGuidePointerCapture(event, true);
  }, [finishGuidePointerCapture]);

  const handleGuidePointerCancelCapture = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    finishGuidePointerCapture(event, false);
  }, [finishGuidePointerCapture]);

  const addGuideAtViewportCenter = useCallback((type: Guide["type"]) => {
    const transform = canvasTransformRef.current;
    const viewport = canvasViewportSizeRef.current;
    const screenPosition = type === "horizontal" ? viewport.height / 2 : viewport.width / 2;
    const offset = type === "horizontal" ? transform.offsetY : transform.offsetX;
    setGuides((previous) => [
      ...previous,
      {
        id: `keyboard-${type}-${Date.now()}`,
        type,
        position: screenToCanvasCoordinate(screenPosition, offset, transform.scale),
      },
    ]);
  }, []);

  const handleGuideKeyDown = useCallback((
    event: ReactKeyboardEvent<HTMLButtonElement>,
    guide: Guide,
  ) => {
    if (event.key === "Delete" || event.key === "Backspace") {
      event.preventDefault();
      event.stopPropagation();
      setGuides((previous) => previous.filter((item) => item.id !== guide.id));
      return;
    }

    const decreasing = event.key === "ArrowLeft" || event.key === "ArrowUp";
    const increasing = event.key === "ArrowRight" || event.key === "ArrowDown";
    if (!decreasing && !increasing) return;

    event.preventDefault();
    event.stopPropagation();
    const step = event.shiftKey ? 10 : 1;
    const delta = decreasing ? -step : step;
    setGuides((previous) => previous.map((item) => (
      item.id === guide.id ? { ...item, position: item.position + delta } : item
    )));
  }, []);

  // 标尺拖动事件处理
  const handleHorizontalRulerPointerDown = useCallback((event: React.PointerEvent<HTMLCanvasElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    activeRulerDragRef.current = "horizontal";
  }, []);

  const handleVerticalRulerPointerDown = useCallback((event: React.PointerEvent<HTMLCanvasElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    activeRulerDragRef.current = "vertical";
  }, []);

  const handleRulerPointerMove = useCallback((event: React.PointerEvent<HTMLCanvasElement>) => {
    const rulerDragType = activeRulerDragRef.current;
    const canvas = canvasRef.current;
    if (!rulerDragType || !canvas) return;
    event.preventDefault();
    const rect = canvas.getBoundingClientRect();
    const screenPos = rulerDragType === "horizontal"
      ? event.clientY - rect.top
      : event.clientX - rect.left;
    const offset = rulerDragType === "horizontal"
      ? canvasTransformRef.current.offsetY
      : canvasTransformRef.current.offsetX;
    const nextPreview: Guide = {
      id: "ruler-guide-preview",
      type: rulerDragType,
      position: screenToCanvasCoordinate(screenPos, offset, canvasTransformRef.current.scale),
    };
    rulerGuidePreviewRef.current = nextPreview;
    setRulerGuidePreview({
      ...nextPreview,
      position: screenToCanvasCoordinate(screenPos, offset, canvasTransformRef.current.scale),
    });
  }, []);

  const handleRulerPointerUp = useCallback((event: React.PointerEvent<HTMLCanvasElement>) => {
    const rulerDragType = activeRulerDragRef.current;
    if (!rulerDragType) return;
    event.preventDefault();
    const canvas = canvasRef.current;
    const preview = rulerGuidePreviewRef.current;
    if (canvas && preview) {
      const rect = canvas.getBoundingClientRect();
      const screenPos = rulerDragType === "horizontal"
        ? event.clientY - rect.top
        : event.clientX - rect.left;
      if (screenPos >= 0) {
        setGuides(prev => [...prev, { ...preview, id: `${rulerDragType}-${Date.now()}` }]);
      }
    }

    activeRulerDragRef.current = null;
    rulerGuidePreviewRef.current = null;
    setRulerGuidePreview(null);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const handleRulerPointerCancel = useCallback((event: React.PointerEvent<HTMLCanvasElement>) => {
    activeRulerDragRef.current = null;
    rulerGuidePreviewRef.current = null;
    setRulerGuidePreview(null);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const handleWandToleranceChange = useCallback((nextValue: number) => {
    const nextTolerance = clampTolerance(nextValue);
    if (wandTolerance === nextTolerance) return;
    setWandTolerance(nextTolerance);
  }, [wandTolerance]);

  const handleSelectionCombineModeChange = useCallback((nextMode: SelectionShortcutMode) => {
    if (selectionCombineMode === nextMode) return;
    setSelectionCombineMode(nextMode);
  }, [selectionCombineMode]);

  const handleSelectionToolEntryClick = useCallback((nextTool: SelectionTool, panelTop: number) => {
    setLayerEraserActive(false);
    setLayerEraserPanelOpen(false);
    setLayerEraserCursorPosition(null);
    setSelectionPanelTop(panelTop);
    setSmartSelectionMenuOpen(false);
    setFeatherPanelOpen(false);
    const scopeChanged = editScope !== "selection";
    const toolAlreadyActive = !scopeChanged && selectionTool === nextTool && selectionPanelOpen;
    if (toolAlreadyActive) {
      setSelectionPanelOpen(false);
      setSelectionTool(null);
      clearCanvasCursorState();
      return;
    }
    setSelectionPanelOpen(true);
    skipNextSelectionPreviewRef.current = false;
    if (scopeChanged) {
      editScopeRef.current = "selection";
      setEditScope("selection");
    }
    if (selectionCombineMode === "replace") setSelectionCombineMode("add");
    setSelectionTool(nextTool);
  }, [clearCanvasCursorState, editScope, selectionCombineMode, selectionPanelOpen, selectionTool]);

  const handleSmartSelectionEntryClick = useCallback((panelTop: number) => {
    setSelectionTool(null);
    setLayerEraserActive(false);
    setLayerEraserCursorPosition(null);
    setSelectionPanelTop(panelTop);
    clearCanvasCursorState();
    setFeatherPanelOpen(false);
    setSelectionPanelOpen(false);
    setSmartSelectionMenuOpen((openMenu) => !openMenu);
  }, [clearCanvasCursorState]);

  const handleBrushSizeChange = useCallback((nextValue: number) => {
    const nextBrushSize = clampBrushSize(nextValue);
    if (brushSize === nextBrushSize) return;
    setBrushSize(nextBrushSize);
  }, [brushSize]);

  const handleFeatherEntryClick = useCallback((panelTop: number) => {
    setSelectionTool(null);
    setLayerEraserActive(false);
    setLayerEraserCursorPosition(null);
    setSelectionPanelTop(panelTop);
    setSmartSelectionMenuOpen(false);
    clearCanvasCursorState();
    setSelectionPanelOpen(false);
    setFeatherPanelOpen((openPanel) => !openPanel);
  }, [clearCanvasCursorState]);

  const handleSelectionFeatherRadiusChange = useCallback((nextValue: number) => {
    const nextRadius = clampSelectionFeatherRadius(nextValue);
    if (selectionFeatherRadiusRef.current === nextRadius) return;
    selectionFeatherRadiusRef.current = nextRadius;
    setSelectionFeatherRadius(nextRadius);
    workerMaskCacheRef.current = null;
    requestPreviewProcessingRef.current(
      liveAdjustmentsRef.current,
      editScopeRef.current === "selection" ? currentSelectionMaskRef.current : undefined,
      "full"
    );
  }, []);

  const handleResetCurrentAdjustments = useCallback(() => {
    if (!currentImage?.id) return;
    const currentAdjustments = getImageAdjustments(currentImage.id);
    if (areAdjustmentsDefault(currentAdjustments)) return;
    pushEditHistory();
    const freshDefaultAdjustments = cloneColorAdjustments(DEFAULT_ADJUSTMENTS);
    applyAdjustmentsToControls(freshDefaultAdjustments);
    setAdjustmentsByImageId((prev) => {
      const next = {
        ...prev,
        [currentImage.id]: freshDefaultAdjustments,
      };
      adjustmentsByImageIdRef.current = next;
      return next;
    });
    requestPreviewProcessingRef.current(
      freshDefaultAdjustments,
      editScopeRef.current === "selection" ? currentSelectionMaskRef.current : undefined,
      "full"
    );
  }, [applyAdjustmentsToControls, currentImage?.id, getImageAdjustments, pushEditHistory]);

  const importLocalImageFiles = useCallback((files: File[], source: LocalImageImportSource) => {
    const imageFiles = sortLocalImageFiles(dedupeLocalImageFiles(files.filter(isLocalImageFile)));

    if (imageFiles.length === 0) {
      setMessage({ text: "E8101 请选择有效的图片文件", type: "error" });
      return;
    }

    const remainingSlots = Math.max(0, MAX_BATCH_COUNT - validImages.length);
    if (remainingSlots === 0) {
      setMessage({ text: "E8105 选择图片过多，请减少后重试", type: "error" });
      return;
    }

    const acceptedFiles = imageFiles.slice(0, remainingSlots);
    const importStartedAt = Date.now();
    const nextLocalImages = acceptedFiles.map((file) => {
      const url = URL.createObjectURL(file);
      localImageUrlsRef.current.add(url);
      localImageSequenceRef.current += 1;
      return {
        id: `local-${importStartedAt}-${localImageSequenceRef.current}`,
        url,
        index: 0,
        isLocal: true,
        sourceFile: file,
      };
    });
    const firstAddedImage = nextLocalImages[0];

    setLocalImages((prev) => [...prev, ...nextLocalImages]);
    setCurrentId(firstAddedImage.id);
    setSmartSelectionMenuOpen(false);
    setSmartSelectionLoadingRegion(null);
    setSmartSelectionActiveRegion(null);
    clearCanvasCursorState();
    applyAdjustmentsToControls(cloneColorAdjustments(DEFAULT_ADJUSTMENTS));
    setSelectedIds((prev) => {
      if (mode === "single") return new Set([firstAddedImage.id]);
      const next = new Set(prev);
      nextLocalImages.forEach((item) => next.add(item.id));
      return next;
    });
    const sourceText = source === "folder" ? "从文件夹添加" : "已添加";
    setMessage({
      text: acceptedFiles.length < imageFiles.length
        ? `${sourceText} ${acceptedFiles.length} 张本地图片，最多支持 ${MAX_BATCH_COUNT} 张`
        : `${sourceText} ${acceptedFiles.length} 张本地图片`,
      type: "success",
    });
  }, [applyAdjustmentsToControls, clearCanvasCursorState, mode, validImages.length]);

  const handleLocalImageFilesChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    importLocalImageFiles(files, "files");
  }, [importLocalImageFiles]);

  const handleLocalImageFolderChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    importLocalImageFiles(files, "folder");
  }, [importLocalImageFiles]);

  const handleLocalImageDragEnter = useCallback((event: ReactDragEvent<HTMLDivElement>) => {
    if (!Array.from(event.dataTransfer.types).includes("Files")) return;
    event.preventDefault();
    event.stopPropagation();
    localUploadDragDepthRef.current += 1;
    setLocalDropActive(true);
  }, []);

  const handleLocalImageDragOver = useCallback((event: ReactDragEvent<HTMLDivElement>) => {
    if (!Array.from(event.dataTransfer.types).includes("Files")) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "copy";
    setLocalDropActive(true);
  }, []);

  const handleLocalImageDragLeave = useCallback((event: ReactDragEvent<HTMLDivElement>) => {
    if (!Array.from(event.dataTransfer.types).includes("Files")) return;
    event.preventDefault();
    event.stopPropagation();
    localUploadDragDepthRef.current = Math.max(0, localUploadDragDepthRef.current - 1);
    if (localUploadDragDepthRef.current === 0) {
      setLocalDropActive(false);
    }
  }, []);

  const handleLocalImageDrop = useCallback(async (event: ReactDragEvent<HTMLDivElement>) => {
    if (!Array.from(event.dataTransfer.types).includes("Files")) return;
    event.preventDefault();
    event.stopPropagation();
    localUploadDragDepthRef.current = 0;
    setLocalDropActive(false);
    if (saving) return;

    try {
      const droppedFiles = await getFilesFromDataTransfer(event.dataTransfer);
      importLocalImageFiles(droppedFiles, "drop");
    } catch {
      setMessage({ text: "E8101 本地图片读取失败，请重新选择", type: "error" });
    }
  }, [importLocalImageFiles, saving]);

  const toggleImage = (item: ImageEditItem) => {
    setCurrentId(item.id);
    setSmartSelectionActiveRegion(null);
    const nextAdjustments = getImageAdjustments(item.id);
    applyAdjustmentsToControls(nextAdjustments);
    if (!showSelectionControls) {
      setSelectedIds(new Set([item.id]));
      return;
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(item.id)) {
        next.delete(item.id);
      } else if (next.size >= MAX_BATCH_COUNT) {
        setMessage({ text: "E8105 选择图片过多，请减少后重试", type: "error" });
      } else {
        next.add(item.id);
      }
      return next;
    });
  };

  const createExportProcessor = useCallback((item: ImageEditItem) => async (imageData: ImageData) => {
    let processed = imageData;
    const bakedOperations = bakedEditOperationsByImageIdRef.current[item.id] || [];
    for (const operation of bakedOperations) {
      processed = await processImageDataWithAdjustments(
        processed,
        operation.adjustments,
        operation.selectionMask,
        operation.selectionMaskIsFeathered
      );
    }
    return processImageDataWithAdjustments(
      processed,
      getImageAdjustments(item.id),
      editScope === "selection" ? selectionMasksByImageIdRef.current[item.id] : undefined
    );
  }, [editScope, getImageAdjustments, processImageDataWithAdjustments]);

  const downloadSingleItem = useCallback(async (item: ImageEditItem | undefined) => {
    if (!item) {
      setMessage({ text: "E8101 编辑参数有误，请调整后重试", type: "error" });
      return;
    }

    setSaving(true);
    setSavingAction("download");
    setExportProgress({ current: 1, total: 1 });
    setMessage(null);
    try {
      const blob = await renderEditedBlob(item.url, createExportProcessor(item), async (ctx, width, height) => {
        await ensureTextLayerFontsReady(textLayers);
        await drawOrderedImageEditLayersToCanvas(
          ctx, textLayers, stickerLayers, layerOrder, item.id, width, height,
        );
      }, { topBottom: strokeTopBottom, leftRight: strokeLeftRight, cornerRadius }, STROKE_PREVIEW_FRAME_DISPLAY_WIDTH);
      downloadBlob(blob, `flowx_edit_${item.index + 1}_${Date.now()}.png`);
      setMessage({ text: "已下载 1 张图片", type: "success" });
    } catch (err) {
      setMessage({ text: getSafeErrorMessage(err, "E8102 图片下载失败，请稍后重试"), type: "error" });
    } finally {
      setSaving(false);
      setSavingAction(null);
      setExportProgress(null);
    }
  }, [cornerRadius, createExportProcessor, ensureTextLayerFontsReady, layerOrder, stickerLayers, strokeLeftRight, strokeTopBottom, textLayers]);

  const handleDownload = useCallback(async () => {
    if (!currentImage || saving) return;
    if (!isStackPreview) {
      await downloadSingleItem(currentImage);
      return;
    }

    setSaving(true);
    setSavingAction("download");
    setMessage(null);
    try {
      // 统一打成 ZIP 压缩包一次性下载，避免多张时逐张弹出保存框。
      const JSZip = (await import("jszip")).default;
      const zip = new JSZip();
      let added = 0;
      for (let index = 0; index < validImages.length; index += 1) {
        const item = validImages[index];
        setExportProgress({ current: index + 1, total: validImages.length });
        const blob = await renderEditedBlob(item.url, createExportProcessor(item), async (ctx, width, height) => {
          await ensureTextLayerFontsReady(textLayers);
          await drawOrderedImageEditLayersToCanvas(
            ctx, textLayers, stickerLayers, layerOrder, item.id, width, height,
          );
        }, { topBottom: strokeTopBottom, leftRight: strokeLeftRight, cornerRadius }, STROKE_PREVIEW_FRAME_DISPLAY_WIDTH);
        // renderEditedBlob 已产出 image/png；压缩包统一 .png 命名。
        zip.file(`flowx_edit_${item.index + 1}.png`, blob);
        added += 1;
      }
      if (added === 0) {
        throw new Error("E8102 没有可下载的图片");
      }
      const content = await zip.generateAsync({ type: "blob" });
      downloadBlob(content, `flowx_edit_batch_${Date.now()}.zip`);
      setMessage({ text: `已下载 ${added} 张图片（压缩包）`, type: "success" });
    } catch (err) {
      setMessage({ text: getSafeErrorMessage(err, "E8102 图片下载失败，请稍后重试"), type: "error" });
    } finally {
      setSaving(false);
      setSavingAction(null);
      setExportProgress(null);
    }
  }, [cornerRadius, createExportProcessor, currentImage, downloadSingleItem, ensureTextLayerFontsReady, isStackPreview, layerOrder, saving, stickerLayers, strokeLeftRight, strokeTopBottom, textLayers, validImages]);

  const handleSave = useCallback(async () => {
    if (!currentImage || saving) return;

    setSaving(true);
    setSavingAction("save");
    setMessage(null);
    try {
      const exportItems = isStackPreview ? validImages : [currentImage];
      const results = await Promise.all(exportItems.map(async (item) => {
        const blob = await renderEditedBlob(item.url, createExportProcessor(item), async (ctx, width, height) => {
          await ensureTextLayerFontsReady(textLayers);
          await drawOrderedImageEditLayersToCanvas(
            ctx, textLayers, stickerLayers, layerOrder, item.id, width, height,
          );
        }, { topBottom: strokeTopBottom, leftRight: strokeLeftRight, cornerRadius }, STROKE_PREVIEW_FRAME_DISPLAY_WIDTH);
        const uploaded = await imageEditApi.uploadEditedImage(blob, taskId || "", item.index);
        return {
          sourceIndex: item.index,
          sourceUrl: item.url,
          editedUrl: uploaded.url,
        };
      }));
      await onSave(results);
      onClose();
    } catch (err) {
      setMessage({ text: getSafeErrorMessage(err, "E8102 图片保存失败，请稍后重试"), type: "error" });
    } finally {
      setSaving(false);
      setSavingAction(null);
    }
  }, [cornerRadius, createExportProcessor, currentImage, ensureTextLayerFontsReady, isStackPreview, layerOrder, onClose, onSave, saving, stickerLayers, strokeLeftRight, strokeTopBottom, taskId, textLayers, validImages]);
  const smartSelectionDisabled =
    smartSelectionLoadingRegion !== null ||
    !currentImage ||
    (currentImage.isLocal && !currentImage.sourceFile && !currentImage.remoteUrl) ||
    basePreviewImageIdRef.current !== currentImage.id ||
    !basePreviewImageDataRef.current;
  const colorToolsEnabled = rightPanelTab === "color";
  const wandToolActive = colorToolsEnabled && selectionPanelOpen && !layerEraserActive && !smartSelectionMenuOpen && !featherPanelOpen && editScope === "selection" && selectionTool === "wand";
  const brushToolActive = colorToolsEnabled && selectionPanelOpen && !layerEraserActive && !smartSelectionMenuOpen && !featherPanelOpen && editScope === "selection" && selectionTool === "brush";
  const smartSelectionToolActive =
    colorToolsEnabled && !layerEraserActive && smartSelectionMenuOpen && smartSelectionLoadingRegion === null && smartSelectionActiveRegion === null;

  const renderAdjustmentControl = useCallback((control: AdjustmentControlConfig) => (
    <AdjustmentControl
      key={control.key}
      label={control.label}
      value={adjustments[control.key]}
      gradient={control.gradient}
      displayScale={control.displayScale}
      displayPrecision={control.displayPrecision}
      onChange={adjustmentChangeHandlers[control.key]}
      onInteractionEnd={endAdjustmentInteraction}
    />
  ), [adjustmentChangeHandlers, adjustments, endAdjustmentInteraction]);

  const ensureSmartSelectionImageUrl = useCallback(async (item: ImageEditItem) => {
    if (!item.isLocal) return item.url;
    if (item.remoteUrl) return item.remoteUrl;
    if (!item.sourceFile) {
      throw new Error("E8106 本地图片上传失败，请重新选择图片");
    }

    const existingUpload = localSmartSelectionUploadPromisesRef.current[item.id];
    if (existingUpload) return existingUpload;

    const uploadPromise = imageEditApi
      .uploadLocalSourceImage(item.sourceFile, taskId, item.index)
      .then((uploaded) => {
        setLocalImages((prev) => prev.map((localItem) => (
          localItem.id === item.id
            ? { ...localItem, remoteUrl: uploaded.url }
            : localItem
        )));
        return uploaded.url;
      })
      .finally(() => {
        delete localSmartSelectionUploadPromisesRef.current[item.id];
      });

    localSmartSelectionUploadPromisesRef.current[item.id] = uploadPromise;
    return uploadPromise;
  }, [taskId]);

  const handleSmartSelection = useCallback(async (region: SegmentSelectionRegion) => {
    if (!currentImage?.id || !currentImage.url) {
      setMessage({ text: "E8101 编辑参数有误，请调整后重试", type: "error" });
      return;
    }

    if (editScopeRef.current !== "selection") {
      editScopeRef.current = "selection";
      setEditScope("selection");
    }

    const basePreview = basePreviewImageDataRef.current;
    if (!basePreview || basePreviewImageIdRef.current !== currentImage.id) {
      setMessage({ text: "E8106 智能选区失败，请手动选择区域", type: "error" });
      return;
    }

    const cacheKey = `${currentImage.id}:${region}`;
    const cached = smartSelectionCacheRef.current[cacheKey];
    if (
      cached &&
      cached.imageId === currentImage.id &&
      cached.imageUrl === currentImage.url &&
      cached.region === region &&
      cached.width === basePreview.width &&
      cached.height === basePreview.height
    ) {
      const nextMask = cloneSelectionMask(cached.mask);
      pushEditHistory();
      currentSelectionMaskRef.current = nextMask;
      selectionOutlinePathCacheRef.current = null;
      workerMaskCacheRef.current = null;
      setSelectionMasksByImageId((prev) => {
        const next = {
          ...prev,
          [currentImage.id]: nextMask,
        };
        selectionMasksByImageIdRef.current = next;
        return next;
      });
      setSelectionOutlineVisible(true);
      setSmartSelectionActiveRegion(region);
      setMessage(null);
      drawSelectionOverlay();
      return;
    }

    setSmartSelectionLoadingRegion(region);
    setMessage(null);
    try {
      const segmentImageUrl = await ensureSmartSelectionImageUrl(currentImage);
      const result = await imageEditApi.segmentSelection({
        image_url: segmentImageUrl,
        region,
        target_width: basePreview.width,
        target_height: basePreview.height,
      });
      const nextMask = await selectionMaskFromPngBase64(
        result.mask_png_base64,
        result.width || basePreview.width,
        result.height || basePreview.height
      );
      smartSelectionCacheRef.current[cacheKey] = {
        imageId: currentImage.id,
        imageUrl: currentImage.url,
        region,
        width: nextMask.width,
        height: nextMask.height,
        mask: cloneSelectionMask(nextMask),
      };
      pushEditHistory();
      currentSelectionMaskRef.current = nextMask;
      selectionOutlinePathCacheRef.current = null;
      workerMaskCacheRef.current = null;
      setSelectionMasksByImageId((prev) => {
        const next = {
          ...prev,
          [currentImage.id]: nextMask,
        };
        selectionMasksByImageIdRef.current = next;
        return next;
      });
      setSelectionOutlineVisible(true);
      setSmartSelectionActiveRegion(region);
      drawSelectionOverlay();
    } catch (err) {
      setMessage({ text: safeImageEditErrorText(err, "E8106 智能选区失败，请手动选择区域"), type: "error" });
    } finally {
      setSmartSelectionLoadingRegion(null);
    }
  }, [currentImage, drawSelectionOverlay, ensureSmartSelectionImageUrl, pushEditHistory]);

  const getTextLayerViewportStyle = (layer: ImageEditTextLayer): CSSProperties | null => {
    const transform = canvasTransform;
    let imageX = 0;
    let imageY = 0;
    let imageWidth = renderedPreviewImageDataRef.current?.width ?? layer.coordinateWidth;
    let imageHeight = renderedPreviewImageDataRef.current?.height ?? layer.coordinateHeight;
    const stackFrame = stackPreviewLayoutRef.current?.frames.find((frame) => frame.id === layer.imageId);
    if (stackPreviewLayoutRef.current) {
      if (!stackFrame) return null;
      imageX = stackFrame.x;
      imageY = stackFrame.y;
      imageWidth = stackFrame.width;
      imageHeight = stackFrame.height;
    } else if (layer.imageId !== currentImage?.id) {
      return null;
    }
    const scaleX = imageWidth / Math.max(1, layer.coordinateWidth);
    const scaleY = imageHeight / Math.max(1, layer.coordinateHeight);
    const displayScale = Math.min(scaleX, scaleY) * transform.scale;
    const baseStyle = getTextLayerCssStyle(layer.style);
    // 图层浮层（选框、手柄、图层顺序工具栏）必须落在整数像素上：
    // GPU 光栅的 Chrome 会在小数坐标上重采样合成层，导致工具栏文字/图标发糊。
    // 高度取偶保证工具栏 top-1/2 -translate-y-1/2 的垂直中心也是整数像素。
    return {
      ...baseStyle,
      left: Math.round(transform.offsetX + (imageX + layer.x * scaleX) * transform.scale),
      top: Math.round(transform.offsetY + (imageY + layer.y * scaleY) * transform.scale),
      width: Math.round(Math.max(48, layer.width * scaleX * transform.scale) + 2),
      height: 2 * Math.round((Math.max(MIN_TEXT_LAYER_SIZE, layer.height * scaleY * transform.scale) + 2) / 2),
      fontSize: layer.style.fontSize * displayScale,
      lineHeight: `${resolveTextLineHeight(layer.style) * displayScale}px`,
      letterSpacing: resolveTextLetterSpacing(layer.style) * displayScale,
      transform: `rotate(${layer.rotation}deg)`,
      transformOrigin: "center top",
      wordBreak: "break-all",
      overflowWrap: "anywhere",
      zIndex: getImageEditLayerZIndex(layerOrder, layer.imageId, layer.id),
    };
  };

  const getTextLayerContentViewportStyle = (layer: ImageEditTextLayer): CSSProperties => {
    let imageWidth = renderedPreviewImageDataRef.current?.width ?? layer.coordinateWidth;
    let imageHeight = renderedPreviewImageDataRef.current?.height ?? layer.coordinateHeight;
    const stackFrame = stackPreviewLayoutRef.current?.frames.find((frame) => frame.id === layer.imageId);
    if (stackFrame) {
      imageWidth = stackFrame.width;
      imageHeight = stackFrame.height;
    }
    const displayScale = Math.min(
      imageWidth / Math.max(1, layer.coordinateWidth),
      imageHeight / Math.max(1, layer.coordinateHeight),
    ) * canvasTransform.scale;
    return {
      boxSizing: "border-box",
      padding: TEXT_LAYER_CONTENT_PADDING * displayScale,
      fontFamily: "inherit",
      fontSize: "inherit",
      fontStyle: "inherit",
      fontWeight: "inherit",
      lineHeight: "inherit",
      letterSpacing: "inherit",
      textAlign: "inherit",
      color: "inherit",
    };
  };

  const getLayerEraseMaskStyle = (
    layer: ImageEditTextLayer | ImageEditStickerLayer,
    kind: "text" | "sticker",
  ): CSSProperties | undefined => {
    if (!layer.eraseStrokes?.length) return undefined;
    const originX = layer.x + layer.width / 2;
    const originY = kind === "text" ? layer.y : layer.y + layer.height / 2;
    const localOffsetY = kind === "text" ? 0 : layer.height / 2;
    const radians = -layer.rotation * Math.PI / 180;
    const paths = layer.eraseStrokes.map((stroke) => {
      const points = stroke.points.map((point) => {
        const dx = point.x - originX;
        const dy = point.y - originY;
        return {
          x: dx * Math.cos(radians) - dy * Math.sin(radians) + layer.width / 2,
          y: dx * Math.sin(radians) + dy * Math.cos(radians) + localOffsetY,
        };
      });
      if (!points.length) return "";
      const path = points.length === 1
        ? `M ${points[0].x} ${points[0].y} L ${points[0].x + 0.01} ${points[0].y}`
        : points.map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`).join(" ");
      return `<path d='${path}' fill='none' stroke='black' stroke-width='${stroke.size}' stroke-linecap='round' stroke-linejoin='round'/>`;
    }).join("");
    const maskWidth = Math.max(1, layer.width);
    const maskHeight = Math.max(1, layer.height);
    const svg = [
      `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${maskWidth} ${maskHeight}'>`,
      `<mask id='erase-mask' maskUnits='userSpaceOnUse' maskContentUnits='userSpaceOnUse' mask-type='luminance' x='0' y='0' width='${maskWidth}' height='${maskHeight}'>`,
      `<rect width='${maskWidth}' height='${maskHeight}' fill='white'/>`,
      paths,
      "</mask>",
      `<rect width='${maskWidth}' height='${maskHeight}' fill='white' mask='url(#erase-mask)'/>`,
      "</svg>",
    ].join("");
    const maskImage = `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
    return {
      maskImage,
      WebkitMaskImage: maskImage,
      maskSize: "100% 100%",
      WebkitMaskSize: "100% 100%",
      maskRepeat: "no-repeat",
      WebkitMaskRepeat: "no-repeat",
    };
  };

  const getStickerLayerViewportStyle = (layer: ImageEditStickerLayer): CSSProperties | null => {
    const transform = canvasTransform;
    let imageX = 0;
    let imageY = 0;
    let imageWidth = renderedPreviewImageDataRef.current?.width ?? layer.coordinateWidth;
    let imageHeight = renderedPreviewImageDataRef.current?.height ?? layer.coordinateHeight;
    const stackFrame = stackPreviewLayoutRef.current?.frames.find((frame) => frame.id === layer.imageId);
    if (stackPreviewLayoutRef.current) {
      if (!stackFrame) return null;
      imageX = stackFrame.x;
      imageY = stackFrame.y;
      imageWidth = stackFrame.width;
      imageHeight = stackFrame.height;
    } else if (layer.imageId !== currentImage?.id) {
      return null;
    }
    const scaleX = imageWidth / Math.max(1, layer.coordinateWidth);
    const scaleY = imageHeight / Math.max(1, layer.coordinateHeight);
    // 同 getTextLayerViewportStyle：整数像素摆放浮层，高度取偶对齐工具栏垂直中心。
    return {
      left: Math.round(transform.offsetX + (imageX + layer.x * scaleX) * transform.scale),
      top: Math.round(transform.offsetY + (imageY + layer.y * scaleY) * transform.scale),
      width: Math.round(layer.width * scaleX * transform.scale),
      height: 2 * Math.round((layer.height * scaleY * transform.scale) / 2),
      transform: `rotate(${layer.rotation}deg)`,
      zIndex: getImageEditLayerZIndex(layerOrder, layer.imageId, layer.id),
    };
  };

  // 绘制水平标尺
  useEffect(() => {
    const canvas = horizontalRulerRef.current;
    const viewport = canvasViewportRef.current;
    if (!canvas || !viewport) return;

    const rect = canvas.getBoundingClientRect();
    const width = rect.width;
    const dpr = window.devicePixelRatio || 1;

    canvas.width = width * dpr;
    canvas.height = RULER_THICKNESS * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${RULER_THICKNESS}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.scale(dpr, dpr);

    const ticks = calculateRulerTicks(
      width,
      canvasTransformRef.current.offsetX,
      canvasTransformRef.current.scale,
    );

    drawRuler(ctx, ticks, 'horizontal', { width, height: RULER_THICKNESS });
  }, [canvasTransform, canvasViewportSize]);

  // 绘制垂直标尺
  useEffect(() => {
    const canvas = verticalRulerRef.current;
    const viewport = canvasViewportRef.current;
    if (!canvas || !viewport) return;

    const rect = canvas.getBoundingClientRect();
    const height = rect.height;
    const dpr = window.devicePixelRatio || 1;

    canvas.width = RULER_THICKNESS * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${RULER_THICKNESS}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.scale(dpr, dpr);

    const ticks = calculateRulerTicks(
      height,
      canvasTransformRef.current.offsetY,
      canvasTransformRef.current.scale,
    );

    drawRuler(ctx, ticks, 'vertical', { width: RULER_THICKNESS, height });
  }, [canvasTransform, canvasViewportSize]);

  // 绘制辅助线
  useEffect(() => {
    const canvas = guidesCanvasRef.current;
    const viewport = canvasViewportRef.current;
    if (!canvas || !viewport) return;

    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.scale(dpr, dpr);

    const visibleGuides = rulerGuidePreview
      ? [...guides, rulerGuidePreview]
      : guides;
    drawGuides(canvas, visibleGuides, canvasTransformRef.current, { width: rect.width, height: rect.height });
  }, [canvasTransform, canvasViewportSize, guides, rulerGuidePreview]);

  const imageEditorHelpTip = normalizeHelpTipConfig(imageEditorTutorial ? {
    ...imageEditorTutorial.config,
    enabled: true,
    display_mode: "icon",
    red_dot_position: "icon",
    revision: imageEditorTutorial.revision,
  } : null);
  const imageEditorTutorialScopeKey = `u${user?.id || 0}:t${user?.current_team_id || 0}:d${imageEditorTutorialDeviceId}`;
  const imageEditorHelpTipRedDotStorageKey = imageEditorHelpTip && imageEditorTutorial
    ? getEcomHelpTipRedDotStorageKey({
        scopeKey: imageEditorTutorialScopeKey,
        workflowKey: "scene-tutorial",
        componentParamKey: IMAGE_EDITOR_TUTORIAL_SCENE_KEY,
        helpTip: {
          ...imageEditorHelpTip,
          revision: imageEditorTutorial.revision,
        },
      })
    : undefined;

  if (!open) return null;

  return createPortal(
    <EcomPortalProvider controller={editorPortal}>
    <div
      data-image-edit-modal="true"
      className={`flowx-ecom-image ${getEcomOverlayClassName("canvasModal")} fixed inset-0 flex items-center justify-center bg-black/45 p-3 backdrop-blur-[5px]`}
      style={{ zIndex: ECOM_LAYERS.canvasModal }}
    >
      <div
        data-image-edit-preview-layout={previewLayout}
        className="flex h-[min(920px,calc(100dvh-24px))] min-h-0 w-[min(1560px,calc(100vw-24px))] flex-col overflow-hidden rounded-[8px] border border-white/10 bg-[#151515] shadow-[0_20px_80px_rgba(0,0,0,0.58)]"
      >
        <div className="relative z-[90] min-h-[80px] shrink-0 overflow-visible border-b border-white/10 bg-[#151515] px-5 py-[9px]">
          <div className="flex min-w-0 flex-wrap items-center gap-3 lg:flex-nowrap">
            <div className="flex min-w-[190px] shrink-0 items-center gap-2 text-white">
              <div>
                <div data-image-edit-title="true" className="flex items-center gap-1.5">
                  <span className="text-l font-semibold">图像编辑</span>
                  <HelpTipTrigger
                    helpTip={imageEditorHelpTip}
                    fallbackTitle="图像编辑教程"
                    ariaLabel="查看图像编辑教程"
                    redDotStorageKey={imageEditorHelpTipRedDotStorageKey}
                  />
                </div>
                <div className="mt-1 text-[12px] text-white/40">{saveAsCopy ? "保存副本后替换当前显示，原交付图片保留" : "下载到本地，或保存并替换当前效果图"}</div>
              </div>
            </div>

            {!isStackPreview && (
            <div
              data-image-edit-thumbnail-strip="true"
              onDragEnter={handleLocalImageDragEnter}
              onDragOver={handleLocalImageDragOver}
              onDragLeave={handleLocalImageDragLeave}
              onDrop={handleLocalImageDrop}
              className={`relative flex min-w-[220px] flex-1 items-center gap-2 overflow-visible rounded-[8px] transition ${
                localDropActive
                  ? "bg-white/[0.05] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.45)]"
                  : ""
              }`}
            >
              <input
                ref={localFileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleLocalImageFilesChange}
              />
              <input
                ref={localFolderInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleLocalImageFolderChange}
              />
              {showThumbnailScrollControls && canScrollThumbnailPrevious && (
                <button
                  type="button"
                  data-image-edit-thumbnail-previous
                  aria-label="向左查看更多效果图"
                  title="向左查看更多效果图"
                  onClick={() => scrollThumbnailViewport(-1)}
                  className="absolute left-0 z-10 flex size-5 cursor-pointer items-center justify-center rounded-sm bg-[#151515]/92 text-white/70 shadow-[1px_0_8px_rgba(0,0,0,0.45)]"
                >
                  <ChevronLeft className="size-3.5" strokeWidth={2} />
                </button>
              )}
              <div
                ref={thumbnailViewportRef}
                data-image-edit-thumbnail-viewport
                className={`flex min-w-0 max-w-full items-center gap-2 overflow-x-auto py-1 pr-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${
                  showThumbnailScrollControls ? "px-5" : ""
                }`}
              >
                {validImages.map((item) => {
                  const checked = selectedIds.has(item.id);
                  const active = currentImage?.id === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => toggleImage(item)}
                      aria-pressed={active}
                      className="relative h-[60px] w-[60px] shrink-0 overflow-hidden rounded-[6px] border-2 border-white/10 bg-transparent transition-all hover:border-white/35"
                    >
                      <img src={toThumbnailUrl(item.url)} alt={`效果图 ${item.index + 1}`} className="h-full w-full rounded-[3px] object-cover" />
                      {!active && (
                        <span
                          data-image-edit-thumbnail-dim-overlay
                          aria-hidden="true"
                          className="pointer-events-none absolute inset-0 bg-black/45"
                        />
                      )}
                      <span className={`absolute bottom-1 left-1 rounded px-1 py-0.5 text-[9px] ${active ? "bg-white font-semibold text-black" : "bg-black/65 text-white/80"}`}>图 {item.index + 1}</span>
                      {showSelectionControls && (
                        <span className={`absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-sm border ${checked ? "border-white bg-white shadow-[0_0_8px_rgba(255,255,255,0.45)]" : "border-white/60 bg-black/45"}`}>
                          {checked && (
                            <span className="block h-2 w-1.5 rotate-45 border-b-2 border-r-2 border-black" />
                          )}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              {showThumbnailScrollControls && canScrollThumbnailNext && (
                <button
                  type="button"
                  data-image-edit-thumbnail-next
                  aria-label="向右查看更多效果图"
                  title="向右查看更多效果图"
                  onClick={() => scrollThumbnailViewport(1)}
                  className="absolute right-0 z-10 flex size-5 cursor-pointer items-center justify-center rounded-sm bg-[#151515]/92 text-white/70 shadow-[-1px_0_8px_rgba(0,0,0,0.45)]"
                >
                  <ChevronRight className="size-3.5" strokeWidth={2} />
                </button>
              )}
            </div>
            )}

            <div className="ml-auto flex flex-wrap items-center justify-end gap-3">
              <div className="flex items-center gap-3">
                <EcomButton type="button" variant="transparent" size="medium" onClick={onClose}>
                  取消
                </EcomButton>
                <EcomButton
                  type="button"
                  variant="transparent"
                  size="medium"
                  onClick={handleSave}
                  disabled={saving || !currentImage || currentImage.isLocal}
                  title={saveAsCopy ? "保存编辑副本并替换当前显示图片" : currentImage?.isLocal ? "本地添加的图片不能替换任务效果图" : "保存并替换当前效果图"}
                >
                  {saving && savingAction === "save" ? "保存中" : saveAsCopy ? "保存副本" : "保存"}
                </EcomButton>
                <EcomButton
                  type="button"
                  variant="default"
                  size="medium"
                  onClick={handleDownload}
                  disabled={saving || !currentImage}
                >
                  {saving && savingAction === "download" && exportProgress ? "处理中..." : "下载到本地"}
                </EcomButton>
              </div>
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-hidden md:overflow-x-auto">
          <div
            data-image-edit-left-toolbar-visible={rightPanelTab !== "stroke"}
            className="grid h-full min-h-0 grid-cols-[0_0_minmax(0,1fr)] grid-rows-[minmax(0,1fr)] md:min-w-[740px] md:grid-cols-[60px_minmax(320px,1fr)_360px] md:data-[image-edit-left-toolbar-visible=false]:grid-cols-[0_minmax(320px,1fr)_360px]"
          >
          {rightPanelTab !== "stroke" && (
          <aside className="invisible pointer-events-none relative z-30 min-h-0 overflow-hidden border-r border-white/5 bg-[#151515] md:visible md:pointer-events-auto md:overflow-visible">
            <div data-image-edit-left-toolbar="true" className="flex w-[60px] shrink-0 flex-col items-center gap-1 bg-[#151515] px-0.5 py-3">
              <SidebarToolButton
                active={brushToolActive}
                disabled={rightPanelTab === "font" || rightPanelTab === "sticker"}
                isPanelTrigger
                label="涂抹"
                title="涂抹"
                onClick={(event) => handleSelectionToolEntryClick("brush", event.currentTarget.offsetTop)}
              >
                <SmudgeToolIcon className="h-[20px] w-[20px]" />
              </SidebarToolButton>
              <SidebarToolButton
                active={wandToolActive}
                disabled={rightPanelTab === "font" || rightPanelTab === "sticker"}
                isPanelTrigger
                label="魔棒"
                title="魔棒"
                onClick={(event) => handleSelectionToolEntryClick("wand", event.currentTarget.offsetTop)}
              >
                <MagicWandIcon className="h-[20px] w-[20px]" />
              </SidebarToolButton>
              <SidebarToolButton
                active={rightPanelTab === "color" && featherPanelOpen}
                disabled={rightPanelTab === "font" || rightPanelTab === "sticker"}
                isPanelTrigger
                label="羽化"
                title="羽化"
                onClick={(event) => handleFeatherEntryClick(event.currentTarget.offsetTop)}
              >
                <FeatherSelectionIcon className="h-[20px] w-[20px]" />
              </SidebarToolButton>
              <SidebarToolButton
                active={layerEraserActive}
                label="橡皮擦"
                isPanelTrigger
                title="橡皮擦（仅擦除字体和贴纸）"
                onClick={(event) => handleLayerEraserToggle(event.currentTarget.offsetTop)}
              >
                <Eraser size={20} strokeWidth={1.85} />
              </SidebarToolButton>
              <SidebarToolButton
                active={smartSelectionToolActive}
                disabled={rightPanelTab === "font" || rightPanelTab === "sticker"}
                isPanelTrigger
                label={"智能\n选区"}
                title="AI智能选区"
                onClick={(event) => handleSmartSelectionEntryClick(event.currentTarget.offsetTop)}
              >
                <AiSmartSelectionIcon className={`h-[22px] w-[22px] ${smartSelectionLoadingRegion ? "animate-pulse" : ""}`} />
              </SidebarToolButton>
              <SidebarToolButton
                disabled={rightPanelTab === "font" || rightPanelTab === "sticker" || !currentSelectionMask}
                label="清空选区"
                shortcut="Ctrl+D"
                title="清空选区（Ctrl+D）"
                onClick={() => {
                  void clearSelectionAndResetAdjustments();
                }}
              >
                <SelectionClearIcon />
              </SidebarToolButton>
              <SidebarToolButton
                disabled={!canUndoEdit}
                label="上一步"
                title="上一步"
                onClick={undoLastEdit}
              >
                <PreviousStepIcon className="h-[20px] w-[20px]" />
              </SidebarToolButton>
              <SidebarToolButton
                disabled={!canRedoEdit}
                label="下一步"
                title="下一步"
                onClick={redoLastEdit}
              >
                <NextStepIcon className="h-[20px] w-[20px]" />
              </SidebarToolButton>
            </div>

            <style>{`
              input.image-edit-selection-range::-webkit-slider-runnable-track {
                height: 5px;
                appearance: none;
                border-radius: 9999px;
                background: transparent;
              }

              input.image-edit-selection-range::-webkit-slider-thumb {
                height: 16px;
                width: 16px;
                appearance: none;
                border-radius: 9999px;
                border: 0;
                background: #f4f4f4;
                box-shadow: 0 1px 4px rgba(0, 0, 0, 0.45);
                transform: translateY(-5.5px);
              }

              input.image-edit-selection-range::-moz-range-track {
                height: 5px;
                border-radius: 9999px;
                background: #5d5d5d;
              }

              input.image-edit-selection-range::-moz-range-progress {
                height: 5px;
                border-radius: 9999px;
                background: #2eb6c6;
              }

              input.image-edit-selection-range::-moz-range-thumb {
                height: 16px;
                width: 16px;
                border: 0;
                border-radius: 9999px;
                background: #f4f4f4;
                box-shadow: 0 1px 4px rgba(0, 0, 0, 0.45);
              }
            `}</style>

            {layerEraserActive && layerEraserPanelOpen && (
              <section
                ref={selectionPanelRef}
                data-image-edit-panel="layer-eraser"
                style={{ top: layerEraserPanelTop }}
                className="absolute left-[60px] z-50 ml-2 w-[196px] rounded-[12px] border border-l-0 border-white/10 bg-[#242424]/96 p-2 shadow-[12px_18px_36px_rgba(0,0,0,0.38)] backdrop-blur-md"
              >
                <div className="rounded-[8px] border border-white/12 bg-[#242424] px-3 py-2.5 text-white/70">
                  <div className="flex items-center justify-between gap-3 text-[11px] font-medium">
                    <span className="w-10 shrink-0">粗细</span>
                    <div className="flex min-w-0 flex-1 items-center">
                      <input
                        type="range"
                        min={MIN_BRUSH_SIZE}
                        max={MAX_BRUSH_SIZE}
                        value={layerEraserSize}
                        onChange={(event) => setLayerEraserSize(clampBrushSize(Number(event.target.value)))}
                        aria-label="橡皮擦粗细"
                        className="image-edit-selection-range h-4 min-w-0 flex-1 cursor-pointer appearance-none rounded-full bg-[#666]"
                        style={{
                          background: `linear-gradient(90deg,#2eb6c6 0%,#2eb6c6 ${((layerEraserSize - MIN_BRUSH_SIZE) / (MAX_BRUSH_SIZE - MIN_BRUSH_SIZE)) * 100}%,#666 ${((layerEraserSize - MIN_BRUSH_SIZE) / (MAX_BRUSH_SIZE - MIN_BRUSH_SIZE)) * 100}%,#666 100%)`,
                        }}
                      />
                      <SelectionValueInput
                        aria-label="橡皮擦粗细数值"
                        value={layerEraserSize}
                        min={MIN_BRUSH_SIZE}
                        max={MAX_BRUSH_SIZE}
                        onChange={(value) => setLayerEraserSize(clampBrushSize(value))}
                      />
                    </div>
                  </div>
                </div>
              </section>
            )}

            {featherPanelOpen && (
              <section ref={selectionPanelRef} data-image-edit-panel="feather" style={{ top: selectionPanelTop }} className="absolute left-[60px] z-50 ml-2 w-[196px] rounded-[12px] border border-l-0 border-white/10 bg-[#242424]/96 p-2 shadow-[12px_18px_36px_rgba(0,0,0,0.38)] backdrop-blur-md">
                <div className="rounded-[8px] border border-white/12 bg-[#242424] px-3 py-2.5 text-white/70">
                  <div className="flex items-center justify-between gap-3 text-[11px] font-medium">
                    <span className="w-10 shrink-0">羽化</span>
                    <div className="flex min-w-0 flex-1 items-center">
                      <input
                        type="range"
                        min={MIN_SELECTION_FEATHER_RADIUS}
                        max={MAX_SELECTION_FEATHER_RADIUS}
                        step={1}
                        value={selectionFeatherRadius}
                        onChange={(event) => handleSelectionFeatherRadiusChange(Number(event.target.value))}
                        aria-label="羽化"
                        className="image-edit-selection-range h-3 min-w-0 flex-1 cursor-pointer appearance-none rounded-full outline-none"
                        style={{
                          background: `linear-gradient(90deg,#2eb6c6 0%,#2eb6c6 ${selectionFeatherRadius}%,#666 ${selectionFeatherRadius}%,#666 100%)`,
                        }}
                      />
                      <SelectionValueInput
                        aria-label="羽化数值"
                        value={selectionFeatherRadius}
                        min={MIN_SELECTION_FEATHER_RADIUS}
                        max={MAX_SELECTION_FEATHER_RADIUS}
                        onChange={handleSelectionFeatherRadiusChange}
                      />
                    </div>
                  </div>
                </div>
              </section>
            )}

            {smartSelectionMenuOpen && (
              <section ref={selectionPanelRef} data-image-edit-panel="smart-selection" style={{ top: selectionPanelTop }} className="absolute left-[60px] z-50 ml-2 w-[196px] rounded-[12px] border border-l-0 border-white/10 bg-[#242424]/96 p-2 shadow-[12px_18px_36px_rgba(0,0,0,0.38)] backdrop-blur-md">
                <div className="grid grid-cols-3 gap-2">
                  {SMART_SELECTION_BUTTONS.map(({ region, label, Icon }) => {
                    const loading = smartSelectionLoadingRegion === region;
                    const active = loading || smartSelectionActiveRegion === region;
                    return (
                      <button
                        key={region}
                        type="button"
                        onClick={() => handleSmartSelection(region)}
                        disabled={smartSelectionDisabled}
                        className={`flex h-[58px] flex-col items-center justify-center gap-1 rounded-[8px] border text-[11px] font-medium transition outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-45 ${
                          active
                            ? "border-white/24 bg-[#333] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.14)]"
                            : "border-white/12 bg-[#242424] text-white/70 hover:border-white/24 hover:bg-[#2f2f2f] hover:text-white"
                        }`}
                      >
                        <Icon size={18} strokeWidth={1.65} className={loading ? "animate-pulse" : ""} />
                        <span>{loading ? "识别中" : label}</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {editScope === "selection" && selectionPanelOpen && !smartSelectionMenuOpen && !featherPanelOpen && (
              <section ref={selectionPanelRef} data-image-edit-panel="selection" style={{ top: selectionPanelTop }} className="absolute left-[60px] z-40 ml-2 max-h-[calc(100%-32px)] w-[196px] overflow-y-auto rounded-[12px] border border-l-0 border-white/10 bg-[#242424]/96 p-2 shadow-[12px_18px_36px_rgba(0,0,0,0.38)] backdrop-blur-md">
                <div className="space-y-2">
                  <div className="rounded-[8px] border border-white/12 bg-[#242424] px-3 py-2.5 text-white/70">
                    {selectionTool === "wand" ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between gap-3 text-[11px] font-medium">
                          <span style={{ width: "40px" }}>容差</span>
                          <div className="flex min-w-0 flex-1 items-center">
                            <input
                              type="range"
                              min={1}
                              max={100}
                              value={wandTolerance}
                              onChange={(e) => handleWandToleranceChange(Number(e.target.value))}
                              className="image-edit-selection-range h-2.5 min-w-0 flex-1 cursor-pointer appearance-none rounded-full outline-none"
                              style={{
                                background: `linear-gradient(90deg,#2eb6c6 0%,#2eb6c6 ${((wandTolerance - 1) / 99) * 100}%,#666 ${((wandTolerance - 1) / 99) * 100}%,#666 100%)`,
                              }}
                            />
                            <SelectionValueInput
                              aria-label="魔棒容差数值"
                              value={wandTolerance}
                              min={1}
                              max={100}
                              onChange={handleWandToleranceChange}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between gap-3 text-[11px] font-medium">
                          <span style={{ width: "40px" }}>粗细</span>
                          <div className="flex min-w-0 flex-1 items-center">
                            <input
                              type="range"
                              min={MIN_BRUSH_SIZE}
                              max={MAX_BRUSH_SIZE}
                              step={1}
                              value={brushSize}
                              onChange={(e) => handleBrushSizeChange(Number(e.target.value))}
                              className="image-edit-selection-range h-3 min-w-0 flex-1 cursor-pointer appearance-none rounded-full outline-none"
                              style={{
                                background: `linear-gradient(90deg,#2eb6c6 0%,#2eb6c6 ${((brushSize - MIN_BRUSH_SIZE) / (MAX_BRUSH_SIZE - MIN_BRUSH_SIZE)) * 100}%,#666 ${((brushSize - MIN_BRUSH_SIZE) / (MAX_BRUSH_SIZE - MIN_BRUSH_SIZE)) * 100}%,#666 100%)`,
                              }}
                            />
                            <SelectionValueInput
                              aria-label="涂抹粗细数值"
                              value={brushSize}
                              min={MIN_BRUSH_SIZE}
                              max={MAX_BRUSH_SIZE}
                              onChange={handleBrushSizeChange}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    {([
                      ["add", "添加（shift）", SelectionAddIcon],
                      ["subtract", "减去（alt）", SelectionSubtractIcon],
                    ] as Array<[SelectionShortcutMode, string, () => ReactElement]>).map(([modeValue, label, Icon]) => (
                      <button
                        key={modeValue}
                        type="button"
                        onClick={() => handleSelectionCombineModeChange(modeValue)}
                        className={`flex h-[58px] flex-col items-center justify-center gap-1 rounded-[8px] border text-[11px] font-medium transition outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 ${
                          selectionCombineMode === modeValue
                            ? "border-white/24 bg-[#333] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.14)]"
                            : "border-white/12 bg-[#242424] text-white/70 hover:border-white/24 hover:bg-[#2f2f2f] hover:text-white"
                        }`}
                      >
                        <Icon />
                        <span>{label}</span>
                      </button>
                    ))}
                  </div>

                  <div className="px-1 text-[10px] leading-4 text-white/45">
                    点击画布生成选区，拖动画布查看细节
                  </div>
                </div>
              </section>
            )}
          </aside>
          )}

          <main className="invisible pointer-events-none col-start-2 box-border min-h-0 min-w-0 bg-[#07070A] p-[10px] md:visible md:pointer-events-auto">
            <div
              data-image-edit-canvas-workspace="true"
              className="relative h-full min-h-0 overflow-hidden bg-[#07070A]"
            >
              {/* 标尺角块 */}
              <div className="absolute left-0 top-0 z-20 h-[20px] w-[20px] bg-[#1a1a1a]" />

              {/* 垂直标尺 */}
              <canvas
                ref={verticalRulerRef}
                data-image-edit-ruler="vertical"
                onPointerDown={handleVerticalRulerPointerDown}
                onPointerMove={handleRulerPointerMove}
                onPointerUp={handleRulerPointerUp}
                onPointerCancel={handleRulerPointerCancel}
                className="absolute left-0 top-[20px] z-20 w-[20px] touch-none cursor-crosshair"
                style={{ height: "calc(100% - 20px)" }}
              />

              {/* 水平标尺 */}
              <canvas
                ref={horizontalRulerRef}
                data-image-edit-ruler="horizontal"
                onPointerDown={handleHorizontalRulerPointerDown}
                onPointerMove={handleRulerPointerMove}
                onPointerUp={handleRulerPointerUp}
                onPointerCancel={handleRulerPointerCancel}
                className="absolute left-[20px] top-0 z-20 h-[20px] touch-none cursor-crosshair"
                style={{ width: "calc(100% - 20px)" }}
              />

              <div className="absolute right-0 top-0 z-30 flex h-[20px]">
                <button
                  type="button"
                  aria-label="添加水平辅助线"
                  title="在视口中心添加水平辅助线"
                  onClick={() => addGuideAtViewportCenter("horizontal")}
                  className="flex h-[20px] w-[20px] items-center justify-center border-l border-white/15 bg-[#242424] text-[10px] font-semibold text-cyan-300 outline-none hover:bg-[#343434] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cyan-300"
                >
                  H
                </button>
                <button
                  type="button"
                  aria-label="添加垂直辅助线"
                  title="在视口中心添加垂直辅助线"
                  onClick={() => addGuideAtViewportCenter("vertical")}
                  className="flex h-[20px] w-[20px] items-center justify-center border-l border-white/15 bg-[#242424] text-[10px] font-semibold text-cyan-300 outline-none hover:bg-[#343434] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cyan-300"
                >
                  V
                </button>
              </div>

              <div
                ref={canvasViewportRef}
                data-image-edit-canvas-viewport="true"
                data-guide-move-cursor={guideMoveCursorActive ? "true" : undefined}
                data-image-edit-stroke-backend={rightPanelTab === "stroke" ? strokePreviewBackend : undefined}
                data-image-edit-stroke-width={rightPanelTab === "stroke" ? strokePreviewMetrics.width : undefined}
                data-image-edit-stroke-height={rightPanelTab === "stroke" ? strokePreviewMetrics.height : undefined}
                data-layer-eraser-active={layerEraserActive ? "true" : undefined}
                onPointerDownCapture={handleGuidePointerDownCapture}
                onPointerMoveCapture={handleGuidePointerMoveCapture}
                onPointerUpCapture={handleGuidePointerUpCapture}
                onPointerCancelCapture={handleGuidePointerCancelCapture}
                onLostPointerCapture={handleGuidePointerCancelCapture}
                onPointerLeave={handleGuidePointerLeave}
                onDragOver={handleStickerCanvasDragOver}
                onDrop={handleStickerCanvasDrop}
                className="absolute left-[20px] top-[20px] h-[calc(100%-20px)] min-h-0 w-[calc(100%-20px)] overflow-hidden bg-[#07070A]"
              >
                <style>{`
                  [data-image-edit-canvas-viewport][data-guide-move-cursor="true"],
                  [data-image-edit-canvas-viewport][data-guide-move-cursor="true"] * {
                    cursor: ${GUIDE_MOVE_CURSOR} !important;
                  }
                  [data-image-edit-canvas-viewport][data-layer-eraser-active="true"],
                  [data-image-edit-canvas-viewport][data-layer-eraser-active="true"] * {
                    cursor: none !important;
                  }
                `}</style>

              <canvas
                ref={canvasRef}
                onPointerEnter={handleCanvasPointerEnter}
                onPointerLeave={handleCanvasPointerLeave}
                onPointerDown={handleCanvasPointerDown}
                onPointerMove={handleCanvasPointerMove}
                onPointerUp={handleCanvasPointerUp}
                onPointerCancel={handleCanvasPointerCancel}
                style={canvasCursorStyle}
                className={`block h-full w-full touch-none ${
                  comparisonMode || comparisonDragging
                    ? "cursor-col-resize"
                    : canvasDragging
                    ? "cursor-grabbing"
                    : effectiveCanvasPanMode
                      ? "cursor-grab"
                    : layerEraserActive
                      ? "cursor-none"
                    : rightPanelTab !== "color" && rightPanelTab !== "font"
                      ? "cursor-default"
                    : rightPanelTab === "font" && eyeDropperActive
                      ? ""
                    : rightPanelTab === "font"
                      ? "cursor-none"
                    : editScope === "selection" && selectionTool === "brush"
                      ? "cursor-none"
                    : editScope === "selection"
                      ? "cursor-crosshair"
                      : "cursor-grab"
                }`}
              />
              <canvas
                ref={selectionOverlayCanvasRef}
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 z-10 h-full w-full"
              />
              {/* 辅助线画布 */}
              <canvas
                ref={guidesCanvasRef}
                data-image-edit-guides="true"
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 z-10 h-full w-full"
              />
              <div data-image-edit-guide-keyboard-controls="true" className="pointer-events-none absolute inset-0 z-20">
                {guides.map((guide) => {
                  const screenPosition = canvasToScreenCoordinate(
                    guide.position,
                    guide.type === "horizontal" ? canvasTransform.offsetY : canvasTransform.offsetX,
                    canvasTransform.scale,
                  );
                  const visible = guide.type === "horizontal"
                    ? screenPosition >= 0 && screenPosition <= canvasViewportSize.height
                    : screenPosition >= 0 && screenPosition <= canvasViewportSize.width;
                  if (!visible) return null;
                  return (
                    <button
                      key={guide.id}
                      type="button"
                      tabIndex={0}
                      aria-label={`${guide.type === "horizontal" ? "水平" : "垂直"}辅助线 ${Math.round(guide.position)} 像素；方向键移动，Shift 加速，Delete 删除`}
                      onKeyDown={(event) => handleGuideKeyDown(event, guide)}
                      className={`absolute flex h-5 min-w-5 items-center justify-center rounded-sm border border-cyan-300/80 bg-[#15343a]/90 px-1 text-[9px] font-semibold text-cyan-100 outline-none hover:bg-[#20515a] focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1 focus-visible:ring-offset-[#07070A] ${layerEraserActive ? "pointer-events-none" : "pointer-events-auto"}`}
                      style={guide.type === "horizontal"
                        ? { left: 0, top: screenPosition, transform: "translateY(-50%)" }
                        : { left: screenPosition, top: 0, transform: "translateX(-50%)" }}
                    >
                      {guide.type === "horizontal" ? "H" : "V"}
                    </button>
                  );
                })}
              </div>
              <div className="pointer-events-none absolute inset-0 z-0">
              {rightPanelTab !== "stroke" && stickerLayers.map((layer) => {
                const viewportStyle = getStickerLayerViewportStyle(layer);
                if (!viewportStyle) return null;
                const selected = selectedStickerLayerId === layer.id;
                return (
                  <div
                    key={layer.id}
                    data-image-edit-sticker-layer={layer.id}
                    data-image-edit-sticker-image-id={layer.imageId}
                    role="img"
                    aria-label={layer.alt}
                    tabIndex={0}
                    onFocus={() => {
                      setSelectedStickerLayerId(layer.id);
                      setSelectedTextLayerId(null);
                    }}
                    onPointerDown={(event) => handleStickerLayerPointerDown(event, layer, "move")}
                    onPointerMove={handleStickerLayerPointerMove}
                    onPointerUp={handleStickerLayerPointerUp}
                    onPointerCancel={handleStickerLayerPointerUp}
                    onLostPointerCapture={handleStickerLayerPointerUp}
                    className={`absolute touch-none select-none ${
                      canvasShortcutPanMode || eyeDropperActive
                        ? "pointer-events-none cursor-grab"
                        : layerEraserActive
                          ? "pointer-events-auto cursor-none"
                          : "pointer-events-auto cursor-move"
                    }`}
                    style={{
                      ...viewportStyle,
                      boxShadow: selected && !layerEraserActive ? `0 0 0 1px ${LAYER_SELECTION_BORDER_COLOR}` : undefined,
                    }}
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={layer.url}
                      alt=""
                      draggable={false}
                      className="pointer-events-none block h-full w-full object-fill"
                      style={getLayerEraseMaskStyle(layer, "sticker")}
                    />
                    {selected && !layerEraserActive && <>
                      {LAYER_RESIZE_HANDLES.map(({ handle, className, cursor }) => (
                        <span
                          key={handle}
                          data-resize-handle={handle}
                          aria-label={`${handle}缩放贴纸`}
                          role="button"
                          tabIndex={-1}
                          onPointerDown={(event) => handleStickerLayerPointerDown(event, layer, "resize", handle)}
                          onPointerMove={handleStickerLayerPointerMove}
                          onPointerUp={handleStickerLayerPointerUp}
                          onPointerCancel={handleStickerLayerPointerUp}
                          className={`absolute h-3 w-3 rounded-full border border-black bg-white shadow ${className} ${cursor}`}
                        />
                      ))}
                      <span className="pointer-events-none absolute -top-7 left-1/2 h-6 border-l border-white/90" />
                      <span
                        aria-label="旋转贴纸"
                        role="button"
                        tabIndex={-1}
                        onPointerDown={(event) => handleStickerLayerPointerDown(event, layer, "rotate")}
                        onPointerMove={handleStickerLayerPointerMove}
                        onPointerUp={handleStickerLayerPointerUp}
                        onPointerCancel={handleStickerLayerPointerUp}
                        className="absolute -top-9 left-1/2 h-4 w-4 -translate-x-1/2 cursor-grab rounded-full border border-black bg-white shadow active:cursor-grabbing"
                      />
                      <LayerOrderToolbar
                        rotation={layer.rotation}
                        onAction={(action) => handleLayerOrderAction(layer.imageId, layer.id, action)}
                      />
                    </>}
                  </div>
                );
              })}
              {rightPanelTab !== "stroke" && textLayers.map((layer) => {
                const viewportStyle = getTextLayerViewportStyle(layer);
                if (!viewportStyle) return null;
                const selected = selectedTextLayerId === layer.id;
                const editing = editingTextLayerId === layer.id;
                const textContentStyle = getTextLayerContentViewportStyle(layer);
                // 调色、字体与贴纸标签页共享文字层选中/拖拽；双击才切到字体编辑。
                const textLayerInteractive = rightPanelTab === "color" || rightPanelTab === "font" || rightPanelTab === "sticker";
                return editing && rightPanelTab === "font" && !layerEraserActive ? (
                  <div
                    key={layer.id}
                    data-image-edit-text-preview={layer.id}
                    data-image-edit-text-image-id={layer.imageId}
                    onPointerMove={handleTextLayerPointerMove}
                    onPointerUp={handleTextLayerPointerUp}
                    onPointerCancel={handleTextLayerPointerUp}
                    onLostPointerCapture={handleTextLayerPointerUp}
                    className={`absolute box-border overflow-visible border bg-transparent ${eyeDropperActive ? "pointer-events-none" : "pointer-events-auto"}`}
                    style={{
                      ...viewportStyle,
                      borderColor: LAYER_SELECTION_BORDER_COLOR,
                      boxShadow: `0 0 0 1px ${LAYER_SELECTION_BORDER_COLOR}40`,
                    }}
                  >
                    <textarea
                      ref={activeTextAreaRef}
                      data-image-edit-text-layer={layer.id}
                      aria-label="图片文字"
                      value={layer.text}
                      placeholder="请输入文字"
                      spellCheck={false}
                      onPointerDown={(event) => {
                        event.stopPropagation();
                        selectTextLayer(layer, true);
                      }}
                      onChange={(event) => {
                        const value = event.target.value;
                        setTextLayers((layers) => layers.map((candidate) => (
                          candidate.id === layer.id
                            ? {
                                ...candidate,
                                text: value,
                                height: Math.max(
                                  candidate.height,
                                  MIN_TEXT_LAYER_SIZE,
                                  measureTextLayerFittedHeight(candidate.style, value, candidate.width),
                                ),
                              }
                            : candidate
                        )));
                      }}
                      onBlur={() => finishTextLayerEditing(layer.id)}
                      className="absolute inset-0 h-full w-full box-border resize-none overflow-hidden bg-transparent p-0 border-0 outline-none"
                      style={textContentStyle}
                    />
                    {LAYER_RESIZE_HANDLES.map(({ handle, className, cursor }) => (
                      <span
                        key={handle}
                        data-resize-handle={handle}
                        aria-label={`${handle}缩放文字`}
                        role="button"
                        tabIndex={-1}
                        onPointerDown={(event) => handleTextLayerPointerDown(event, layer, "resize", handle)}
                        onPointerMove={handleTextLayerPointerMove}
                        onPointerUp={handleTextLayerPointerUp}
                        onPointerCancel={handleTextLayerPointerUp}
                        className={`absolute h-3 w-3 rounded-full border border-white bg-white shadow ${className} ${cursor}`}
                      />
                    ))}
                    <span className="pointer-events-none absolute -top-7 left-1/2 h-6 border-l border-white" />
                    <span
                      aria-label="旋转文字"
                      role="button"
                      tabIndex={-1}
                      onPointerDown={(event) => handleTextLayerPointerDown(event, layer, "rotate")}
                      onPointerMove={handleTextLayerPointerMove}
                      onPointerUp={handleTextLayerPointerUp}
                      onPointerCancel={handleTextLayerPointerUp}
                      className="absolute -top-9 left-1/2 h-4 w-4 -translate-x-1/2 cursor-grab rounded-full border border-white bg-white shadow active:cursor-grabbing"
                    />
                    <LayerOrderToolbar
                      rotation={layer.rotation}
                      onAction={(action) => handleLayerOrderAction(layer.imageId, layer.id, action)}
                    />
                  </div>
                ) : (
                  <div
                    key={layer.id}
                    data-image-edit-text-preview={layer.id}
                    data-image-edit-text-image-id={layer.imageId}
                    role="button"
                    tabIndex={textLayerInteractive && !eyeDropperActive ? 0 : -1}
                    aria-label={layerEraserActive ? "点击擦除图片文字" : selected ? "已选图片文字" : "图片文字"}
                    onFocus={() => selectTextLayer(layer)}
                    onPointerDown={(event) => handleTextLayerPointerDown(event, layer, "move")}
                    onPointerMove={handleTextLayerPointerMove}
                    onPointerUp={handleTextLayerPointerUp}
                    onPointerCancel={handleTextLayerPointerUp}
                    onLostPointerCapture={handleTextLayerPointerUp}
                    onDoubleClick={() => selectTextLayer(layer, true)}
                    className={`absolute box-border overflow-visible whitespace-pre-wrap border bg-transparent p-0 outline-none ${
                      selected ? "" : "border-transparent"
                    } ${
                      textLayerInteractive && !eyeDropperActive
                        ? layerEraserActive
                          ? "pointer-events-auto cursor-crosshair"
                          : "pointer-events-auto cursor-move"
                        : "pointer-events-none"
                    }`}
                    style={{
                      ...viewportStyle,
                      borderColor: selected && !layerEraserActive ? LAYER_SELECTION_BORDER_COLOR : undefined,
                      boxShadow: selected && !layerEraserActive ? `0 0 0 1px ${LAYER_SELECTION_BORDER_COLOR}40` : undefined,
                    }}
                  >
                    <span
                      className="block h-full w-full overflow-hidden"
                      style={{ ...textContentStyle, ...getLayerEraseMaskStyle(layer, "text") }}
                    >
                      {layer.text}
                    </span>
                    {selected && textLayerInteractive && !layerEraserActive && <>
                      {LAYER_RESIZE_HANDLES.map(({ handle, className, cursor }) => (
                        <span
                          key={handle}
                          data-resize-handle={handle}
                          aria-label={`${handle}缩放文字`}
                          role="button"
                          tabIndex={-1}
                          onPointerDown={(event) => handleTextLayerPointerDown(event, layer, "resize", handle)}
                          onPointerMove={handleTextLayerPointerMove}
                          onPointerUp={handleTextLayerPointerUp}
                          onPointerCancel={handleTextLayerPointerUp}
                          className={`absolute h-3 w-3 rounded-full border border-white bg-white shadow ${className} ${cursor}`}
                        />
                      ))}
                      <span className="pointer-events-none absolute -top-7 left-1/2 h-6 border-l border-white" />
                      <span
                        aria-label="旋转文字"
                        role="button"
                        tabIndex={-1}
                        onPointerDown={(event) => handleTextLayerPointerDown(event, layer, "rotate")}
                        onPointerMove={handleTextLayerPointerMove}
                        onPointerUp={handleTextLayerPointerUp}
                        onPointerCancel={handleTextLayerPointerUp}
                        className="absolute -top-9 left-1/2 h-4 w-4 -translate-x-1/2 cursor-grab rounded-full border border-white bg-white shadow active:cursor-grabbing"
                      />
                      <LayerOrderToolbar
                        rotation={layer.rotation}
                        onAction={(action) => handleLayerOrderAction(layer.imageId, layer.id, action)}
                      />
                    </>}
                  </div>
                );
              })}
              </div>
              {eyeDropperPreview && eyeDropperActive && rightPanelTab === "font" && (
                <>
                <div
                  data-image-edit-eyedropper-preview="true"
                  role="status"
                  aria-label={`当前颜色 ${eyeDropperPreview.color}，${eyeDropperPreview.rgb}`}
                  className="pointer-events-none absolute z-40 flex w-[196px] items-center gap-3 rounded-[8px] border border-black/10 bg-[#F7F7F7] px-3 py-2.5 text-[#1E1E1E] shadow-[0_5px_16px_rgba(0,0,0,0.28)]"
                  style={{
                    left: Math.min(
                      Math.max(8, eyeDropperPreview.x + 18),
                      Math.max(8, canvasViewportSize.width - 204),
                    ),
                    top: eyeDropperPreview.y < 82
                      ? eyeDropperPreview.y + 20
                      : eyeDropperPreview.y - 76,
                  }}
                >
                  <span
                    aria-hidden="true"
                    className="relative h-10 w-10 shrink-0 overflow-hidden rounded-[5px] border-2 border-white bg-[#202020] shadow-[0_0_0_1px_#6B6B6B,0_1px_3px_rgba(0,0,0,0.22)]"
                  >
                    <canvas
                      ref={eyeDropperMagnifierCanvasRef}
                      className="absolute inset-0 h-full w-full [image-rendering:pixelated]"
                    />
                    <span className="absolute left-1/2 top-1/2 h-[6px] w-[6px] -translate-x-1/2 -translate-y-1/2 border border-white shadow-[0_0_0_1px_#111111]" />
                    <span className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-white/50 mix-blend-difference" />
                    <span className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-white/50 mix-blend-difference" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="text-[10px] font-medium uppercase tracking-[0.04em] text-[#777]">HEX</span>
                      <span className="font-mono text-[12px] font-semibold tabular-nums">{eyeDropperPreview.color}</span>
                    </span>
                    <span className="mt-0.5 flex items-center justify-between gap-2">
                      <span className="text-[10px] font-medium uppercase tracking-[0.04em] text-[#777]">RGB</span>
                      <span className="font-mono text-[10px] tabular-nums text-[#555]">{eyeDropperPreview.rgb.replace("RGB ", "")}</span>
                    </span>
                    <span className="mt-1 block border-t border-black/10 pt-1 text-[9px] leading-none text-[#777]">移动取色，点击应用</span>
                  </span>
                </div>
                </>
              )}
              {fontCursorPosition && rightPanelTab === "font" && !layerEraserActive && !eyeDropperActive && (
                <div
                  data-image-edit-font-cursor="true"
                  className="pointer-events-none absolute z-30 text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]"
                  style={{
                    left: fontCursorPosition.x,
                    top: fontCursorPosition.y,
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <TextInsertionIcon />
                </div>
              )}
              {selectionHintVisible && (
                <div
                  aria-live="polite"
                  data-image-edit-selection-hint="true"
                  className="pointer-events-none absolute left-1/2 top-0 z-30 -translate-x-1/2 rounded-full border border-white/15 bg-[#0b0b0d]/70 px-3 py-1.5 text-xs font-medium text-white/85 shadow-lg backdrop-blur-md"
                >
                  已建立选区 · Ctrl+D 取消蚂蚁线
                </div>
              )}
              {showLayerEraserCursor && (
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute z-[90] rounded-full border border-white/90 bg-black/10 shadow-[0_0_0_1px_rgba(0,0,0,0.7)]"
                  style={{
                    left: showLayerEraserCursor.x,
                    top: showLayerEraserCursor.y,
                    width: layerEraserSize,
                    height: layerEraserSize,
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <Eraser className="absolute -right-4 -top-4 text-white drop-shadow-[0_1px_2px_#000]" size={16} strokeWidth={2} />
                </div>
              )}
              {showBrushCursor && (
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute z-20 rounded-full border border-white/85 bg-[#ef4444]/15 shadow-[0_0_0_1px_rgba(0,0,0,0.45),0_0_10px_rgba(239,68,68,0.35)]"
                  style={{
                    left: showBrushCursor.x,
                    top: showBrushCursor.y,
                    width: brushSize,
                    height: brushSize,
                    transform: "translate(-50%, -50%)",
                  }}
                />
              )}
              {showBrushModifierCursor && (
                <div
                  aria-hidden="true"
                  data-selection-modifier={showBrushModifierCursor.mode === "add" ? "shift-add" : "alt-subtract"}
                  className="pointer-events-none absolute z-20 flex h-3.5 w-3.5 items-center justify-center rounded-full border border-black bg-white text-black shadow-[0_3px_8px_rgba(0,0,0,0.45)]"
                  style={{
                    left: showBrushModifierCursor.x,
                    top: showBrushModifierCursor.y,
                    transform: `translate(${brushModifierCursorOffset}px, -${brushModifierCursorOffset}px)`,
                  }}
                >
                  {showBrushModifierCursor.mode === "add" ? (
                    <Plus size={9} strokeWidth={2.8} />
                  ) : (
                    <Minus size={9} strokeWidth={2.8} />
                  )}
                </div>
              )}
              {showWandModifierCursor && (
                <div
                  aria-hidden="true"
                  data-selection-modifier={showWandModifierCursor.mode === "add" ? "shift-add" : "alt-subtract"}
                  className="pointer-events-none absolute z-20 flex h-3.5 w-3.5 items-center justify-center rounded-full border border-black bg-white text-black shadow-[0_3px_8px_rgba(0,0,0,0.45)]"
                  style={{
                    left: showWandModifierCursor.x,
                    top: showWandModifierCursor.y,
                    transform: "translate(24px, -24px)",
                  }}
                >
                  {showWandModifierCursor.mode === "add" ? (
                    <Plus size={9} strokeWidth={2.8} />
                  ) : (
                    <Minus size={9} strokeWidth={2.8} />
                  )}
                </div>
              )}
              <div className="absolute right-3 top-3 z-30 flex h-9 items-center gap-1 rounded-lg border border-white/10 bg-[#0b0b0d]/90 px-1.5 shadow-xl backdrop-blur-md">
                <button
                  type="button"
                  onClick={() => {
                    setCanvasPanMode((enabled) => !enabled);
                    clearCanvasCursorState({ wand: false });
                  }}
                  aria-pressed={effectiveCanvasPanMode}
                  className={`flex h-7 w-7 items-center justify-center rounded-md transition ${
                    effectiveCanvasPanMode
                      ? "bg-white text-black shadow-[0_0_0_1px_rgba(255,255,255,0.22),0_0_12px_rgba(255,255,255,0.22)]"
                      : "text-white/55 hover:bg-white/10 hover:text-white"
                  }`}
                  aria-label="拖拽，按住空格可临时启用"
                  title={canvasPanMode ? "关闭拖拽" : canvasShortcutPanMode ? "空格拖拽中" : "拖拽（按住空格临时启用）"}
                >
                  <Hand size={13} />
                </button>
                <button
                  type="button"
                  onClick={() => updateCanvasZoom(canvasTransform.scale / CANVAS_ZOOM_STEP)}
                  className="flex h-7 w-7 items-center justify-center rounded-md text-white/55 transition hover:bg-white/10 hover:text-white"
                  title="缩小"
                >
                  <ZoomOut size={13} />
                </button>
                <span className="w-9 select-none text-center text-[10px] tabular-nums text-white/50">
                  {canvasZoomPercent}%
                </span>
                <button
                  type="button"
                  onClick={() => updateCanvasZoom(canvasTransform.scale * CANVAS_ZOOM_STEP)}
                  className="flex h-7 w-7 items-center justify-center rounded-md text-white/55 transition hover:bg-white/10 hover:text-white"
                  title="放大"
                >
                  <ZoomIn size={13} />
                </button>
                <button
                  type="button"
                  onClick={resetCanvasToFit}
                  className="flex h-7 w-7 items-center justify-center rounded-md text-white/55 transition hover:bg-white/10 hover:text-white"
                  title="适应窗口"
                >
                  <Maximize size={13} />
                </button>
              </div>
              <div className="absolute bottom-3 right-3 z-30">
                <button
                  type="button"
                  onClick={handleComparisonModeToggle}
                  aria-pressed={comparisonMode}
                  className={`flex h-8 w-8 items-center justify-center rounded-[8px] bg-[url('/button_bg3.png')] bg-cover bg-center bg-no-repeat shadow-xl transition-[filter,color] hover:brightness-125 ${
                    comparisonMode
                      ? "text-white brightness-125"
                      : "text-white/70 hover:text-white"
                  }`}
                  title={comparisonMode ? `关闭前后对比（${Math.round(comparisonSplit * 100)}%）` : "前后对比"}
                  aria-label={comparisonMode ? "关闭前后对比" : "开启前后对比"}
                >
                  <svg
                    aria-hidden="true"
                    className="h-[15px] w-[15px]"
                    viewBox="0 0 1024 1024"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M177.2544 787.712h236.288v78.7456H98.4576V157.5424h315.0848v78.7456H177.2544v551.424z m433.2032 0h78.7968v78.7456h-78.7968v-78.7456z m118.1696 0h78.7456v78.7456h-78.7456v-78.7456z m118.1184 78.7456v-78.7456h78.7968v78.7456h-78.7968z m0-275.6608V512h78.7968v78.7968h-78.7968z m0 118.1184v-78.7456h78.7968v78.7456h-78.7968z m0-236.288V393.8304h78.7968v78.7968h-78.7968z m0-118.1696V275.712h78.7968v78.7456h-78.7968z m0-118.1696V157.5424h78.7968v78.7456h-78.7968z m-39.3728 0h-78.7456V157.5424h78.7456v78.7456z m-118.1184 0h-78.7968V157.5424h78.7968v78.7456zM492.288 78.848h78.7968v866.4576H492.288V78.7968z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              </div>
              </div>
            </div>
          </main>

          <aside className="flex min-h-0 min-w-0 flex-col overflow-hidden border-l border-[#353537] bg-[#181818]" style={{ gridColumn: 3 }}>
            {/* Tab 切换栏 */}
            <div className="shrink-0 px-[20px] pt-3">
              <div className="flex flex-wrap items-center gap-1 rounded-[8px] bg-[#242424] p-1.5" role="tablist" aria-label="右侧功能面板">
                {(
                  [
                    { key: "color", label: "调色" },
                    { key: "font", label: "字体" },
                    { key: "sticker", label: "贴纸" },
                    { key: "stroke", label: "描边/圆角" },
                  ] as const
                ).map((tab) => {
                  const active = rightPanelTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => {
                        void switchRightPanelTab(tab.key);
                      }}
                      className={`flex h-7 items-center justify-center gap-1.5 rounded-[6px] px-2 text-[14px] font-normal transition ${
                        active ? "bg-[#363636] text-[#FFFFFF]" : "bg-transparent text-[#B2B2B2] hover:text-white"
                      }`}
                    >
                      <span className="flex h-3.5 w-3.5 items-center justify-center" aria-hidden="true">
                        {tab.key === "color" && (
                          <svg viewBox="0 0 1024 1024" className="h-3.5 w-3.5" fill="currentColor">
                            <path d="M765.659429 439.588571c-4.754286-1.462857-6.290286-4.681143-6.290286-6.217142-1.609143-1.609143-1.609143-4.754286 0-7.899429 14.116571-36.205714 20.48-75.629714 17.261714-116.589714C765.659429 170.276571 645.924571 66.267429 508.854857 66.267429c-105.545143 0-201.654857 64.585143-237.860571 163.84-23.698286 67.730286-23.698286 133.924571 0 195.364571 1.536 3.145143 0 6.290286 0 7.899429 0 1.536-1.609143 3.145143-6.363429 4.681142a265.947429 265.947429 0 0 0-195.291428 278.893715c10.971429 121.270857 105.545143 220.525714 225.28 236.251428 12.580571 1.609143 25.161143 3.145143 37.814857 3.145143 59.830857 0 118.125714-20.48 165.376-59.830857 9.508571-7.899429 20.48-7.899429 28.379428 0a267.410286 267.410286 0 0 0 166.985143 59.904 263.533714 263.533714 0 0 0 263.094857-263.094857c0-116.589714-78.774857-220.598857-190.610285-253.659429z m-237.933715 365.568a188.196571 188.196571 0 0 1-34.669714-111.908571c0-37.741714 11.044571-75.556571 31.524571-108.690286l3.145143-4.681143c3.145143-4.754286 6.290286-7.899429 9.508572-12.653714l4.681143-6.290286 6.290285-6.290285a195.291429 195.291429 0 0 1 132.388572-62.976h28.306285c55.149714 4.681143 105.545143 31.451429 140.214858 74.020571 34.669714 42.496 50.468571 97.645714 42.569142 152.868572a202.166857 202.166857 0 0 1-173.348571 170.057142 202.752 202.752 0 0 1-176.420571-67.730285c-4.681143-1.536-9.435429-7.826286-14.189715-15.725715z m-222.134857 86.601143a200.557714 200.557714 0 0 1-171.666286-171.666285 192.365714 192.365714 0 0 1 44.105143-152.868572 198.729143 198.729143 0 0 1 141.750857-72.411428h29.988572c34.596571 3.145143 66.121143 14.189714 94.500571 31.451428 15.725714 11.044571 23.625143 34.669714 15.725715 52.004572a267.483429 267.483429 0 0 0-26.770286 115.053714c0 42.496 9.508571 83.456 29.988571 121.270857a31.963429 31.963429 0 0 1-9.508571 40.96c-45.714286 29.988571-97.645714 42.569143-148.114286 36.205714zM655.36 433.371429a264.192 264.192 0 0 0-127.634286 55.149714 23.990857 23.990857 0 0 1-31.451428 0 271.872 271.872 0 0 0-116.589715-53.613714 75.337143 75.337143 0 0 1-59.904-51.931429 195.730286 195.730286 0 0 1-6.290285-52.004571c0-53.613714 20.48-104.009143 58.294857-141.824 37.814857-37.814857 88.210286-59.830857 141.750857-58.294858a200.411429 200.411429 0 0 1 141.824 59.904c37.814857 39.350857 58.294857 89.746286 58.294857 143.36 0 18.870857-3.145143 37.814857-9.508571 56.685715a61.44 61.44 0 0 1-48.786286 42.569143z" />
                          </svg>
                        )}
                        {tab.key === "font" && (
                          <svg viewBox="0 0 1024 1024" className="h-3.5 w-3.5" fill="currentColor">
                            <path d="M512 950.857143c-24.137143 0-43.885714-19.748571-43.885714-43.885714V117.028571c0-24.137143 19.748571-43.885714 43.885714-43.885714 24.137143 0 43.885714 19.748571 43.885714 43.885714v789.942858c0 24.137143-19.748571 43.885714-43.885714 43.885714z" />
                            <path d="M73.142857 117.028571c0-24.137143 19.748571-43.885714 43.885714-43.885714h789.942858c24.137143 0 43.885714 19.748571 43.885714 43.885714 0 24.137143-19.748571 43.885714-43.885714 43.885715H117.028571c-24.137143 0-43.885714-19.748571-43.885714-43.885715z" />
                          </svg>
                        )}
                        {tab.key === "sticker" && (
                          <svg viewBox="0 0 1024 1024" className="h-3.5 w-3.5" fill="currentColor">
                            <path d="M512 51.2A460.8 460.8 0 0 1 972.8 512a42.130286 42.130286 0 0 1-8.630857 25.453714l-3.657143 4.169143-418.889143 418.889143a41.837714 41.837714 0 0 1-29.622857 12.214857A460.8 460.8 0 1 1 512 51.2z m0 83.675429a377.051429 377.051429 0 0 0-86.308571 744.155428 335.067429 335.067429 0 0 1 453.412571-453.485714 377.197714 377.197714 0 0 0-367.177143-290.669714z m209.408 335.213714a251.318857 251.318857 0 0 0-204.8 396.946286l350.500571-350.500572a250.148571 250.148571 0 0 0-145.627428-46.445714h-0.073143z" />
                          </svg>
                        )}
                        {tab.key === "stroke" && (
                          <svg viewBox="0 0 1024 1024" className="h-3.5 w-3.5" fill="currentColor">
                            <path d="M352.036571 85.430857a42.642286 42.642286 0 0 1 0 85.357714H191.926857a21.357714 21.357714 0 0 0-21.211428 21.284572l0.731428 120.685714-0.219428 4.388572a42.642286 42.642286 0 0 1-84.845715 0.512l-0.219428-4.388572-0.804572-120.905143v-0.292571c0-58.88 47.762286-106.642286 106.642286-106.642286h160.036571z m479.963429 0c58.806857 0 106.496 47.616 106.642286 106.349714l0.804571 120.905143-0.219428 4.388572a42.642286 42.642286 0 0 1-84.845715 0.585143l-0.219428-4.388572-0.804572-120.905143v-0.292571a21.357714 21.357714 0 0 0-21.357714-21.284572H671.963429a42.642286 42.642286 0 0 1 0-85.357714h160.036571zM352.036571 924.525714a42.642286 42.642286 0 0 0 0-85.357714H191.926857a21.357714 21.357714 0 0 1-21.211428-21.284571l0.731428-120.612572-0.219428-4.388571a42.642286 42.642286 0 0 0-84.845715-0.585143l-0.219428 4.388571-0.804572 120.905143v0.292572c0 58.953143 47.762286 106.642286 106.642286 106.642285h160.036571z m479.963429 0a106.642286 106.642286 0 0 0 106.642286-106.349714l0.804571-120.905143-0.219428-4.388571a42.642286 42.642286 0 0 0-84.845715-0.585143l-0.219428 4.388571-0.804572 120.905143v0.292572a21.357714 21.357714 0 0 1-21.357714 21.284571H671.963429a42.642286 42.642286 0 0 0 0 85.357714h160.036571z" />
                          </svg>
                        )}
                      </span>
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 调色 tab（原内容原样保留） */}
            {rightPanelTab === "color" && (
            <div className="flex min-h-0 flex-1 select-none flex-col overflow-y-auto px-[20px] py-3">
              <div className="space-y-[22px] pb-3">
                <section aria-labelledby="image-edit-hue-title" className="space-y-2">
                  <h3 id="image-edit-hue-title" className="text-[15px] font-medium leading-[18px] text-[#F6F6F6]">
                    色相/饱和度
                  </h3>
                  <div className="flex items-center gap-1.5" role="radiogroup" aria-label="选择调色颜色">
                    {HUE_CHANNEL_UI.map(({ channel, label, color }) => {
                      const active = activeHueChannel === channel;
                      return (
                        <button
                          key={channel}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          aria-label={label}
                          title={label}
                          onClick={() => setActiveHueChannel(channel)}
                          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition ${active ? "bg-[#3075F4]" : "bg-transparent hover:bg-white/10"}`}
                        >
                          <span
                            aria-hidden="true"
                            className="h-6 w-6 rounded-full border border-white"
                            style={{
                              background: color || "conic-gradient(#FF0000,#FFFF05,#03FF00,#03FFFF,#0000FF,#FF00FF,#FF0000)",
                            }}
                          />
                        </button>
                      );
                    })}
                  </div>
                  <div className="space-y-1.5">
                    <AdjustmentControl
                      label="色相"
                      value={adjustments.hueSaturation[activeHueChannel].hue}
                      gradient={HUE_TRACK_GRADIENTS[activeHueChannel]}
                      onChange={(value) => updateHueChannelAdjustment(activeHueChannel, "hue", value)}
                      onInteractionEnd={endAdjustmentInteraction}
                    />
                    <AdjustmentControl
                      label="饱和度"
                      value={adjustments.hueSaturation[activeHueChannel].saturation}
                      gradient={SATURATION_TRACK_GRADIENTS[activeHueChannel]}
                      onChange={(value) => updateHueChannelAdjustment(activeHueChannel, "saturation", value)}
                      onInteractionEnd={endAdjustmentInteraction}
                    />
                    <AdjustmentControl
                      label="明度"
                      value={adjustments.hueSaturation[activeHueChannel].lightness}
                      gradient={LIGHTNESS_TRACK_GRADIENT}
                      onChange={(value) => updateHueChannelAdjustment(activeHueChannel, "lightness", value)}
                      onInteractionEnd={endAdjustmentInteraction}
                    />
                  </div>
                </section>

                <section aria-labelledby="image-edit-balance-title" className="space-y-2">
                  <h3 id="image-edit-balance-title" className="text-[15px] font-medium leading-[18px] text-[#F6F6F6]">
                    色彩平衡
                  </h3>
                  <label className="flex items-center gap-1.5 text-[13px] text-[#A0A0A0]">
                    <span className="shrink-0">色调：</span>
                    <ColorBalanceToneSelect
                      value={activeColorBalanceTone}
                      onChange={(tone) => setActiveColorBalanceTone(tone)}
                    />
                  </label>
                  <div className="space-y-1.5">
                    {COLOR_BALANCE_AXIS_UI.map(({ axis, leftLabel, rightLabel, gradient }) => (
                      <AdjustmentControl
                        key={`${activeColorBalanceTone}:${axis}`}
                        label={`${leftLabel} - ${rightLabel}`}
                        value={adjustments.colorBalance[activeColorBalanceTone][axis]}
                        gradient={gradient}
                        endLabels={{ left: leftLabel, right: rightLabel }}
                        thumbColor={activeColorBalanceTone === "shadows" ? "#181818" : "#D2D2D2"}
                        onChange={(value) => updateColorBalanceAdjustment(activeColorBalanceTone, axis, value)}
                        onInteractionEnd={endAdjustmentInteraction}
                      />
                    ))}
                  </div>
                </section>

                <section aria-labelledby="image-edit-other-title" className="space-y-2">
                  <h3 id="image-edit-other-title" className="text-[15px] font-medium leading-[18px] text-[#F6F6F6]">
                    其它
                  </h3>
                  <div className="space-y-1.5">
                    {OTHER_ADJUSTMENT_CONTROLS.map(renderAdjustmentControl)}
                  </div>
                </section>
              </div>

              <div className="mt-auto space-y-2 border-t border-[#3F3F3F] pt-3">
                {message && (
                  <div className={`rounded-lg px-3 py-2 text-xs ${message.type === "success" ? "bg-green-500/12 text-green-300" : "bg-red-500/12 text-red-300"}`}>
                    {message.text}
                  </div>
                )}

                <EcomButton
                  type="button"
                  variant="transparent"
                  size="large"
                  onClick={handleResetCurrentAdjustments}
                >
                  全部重置
                </EcomButton>

              </div>
            </div>
            )}

            {/* 字体 tab */}
            {rightPanelTab === "font" && (
              <ConfigProvider
                theme={{
                  algorithm: theme.darkAlgorithm,
                  token: {
                    colorBgContainer: "#1F1F21",
                    colorBgElevated: "#2B2B2D",
                    colorBorder: LAYER_SELECTION_BORDER_COLOR,
                    colorPrimary: LAYER_SELECTION_BORDER_COLOR,
                    borderRadius: 6,
                    controlHeight: 32,
                    fontSize: 13,
                  },
                  components: {
                    Segmented: { itemSelectedBg: "#3A3A3C", trackBg: "#242426" },
                  },
                }}
              >
                <div data-image-edit-font-panel="antd-dark" className="image-edit-font-panel flex min-h-0 min-w-0 flex-1 select-none flex-col overflow-y-auto px-5 py-3">
                  <div className="space-y-4 pb-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[13px] font-medium text-[#F6F6F6]">字体</span>
                      <EcomButton
                        variant="outline"
                        size="mini"
                        disabled={fontUploading}
                        aria-busy={fontUploading}
                        onClick={() => fontFileInputRef.current?.click()}
                      >
                        {fontUploading ? `上传中 ${fontUploadProgress.completed}/${fontUploadProgress.total}` : "上传字体"}
                      </EcomButton>
                      <input
                        ref={fontFileInputRef}
                        type="file"
                        multiple
                        accept=".ttf,.otf,.woff,.woff2"
                        className="hidden"
                        aria-label="上传字体文件"
                        disabled={fontUploading}
                        onChange={(event) => {
                          const files = Array.from(event.target.files ?? []);
                          event.target.value = "";
                          if (files.length > 0) void handleImportCustomFonts(files);
                        }}
                      />
                    </div>

                    {message?.type === "error" && message.text.startsWith("E8108") && (
                      <div role="alert" className="rounded-[6px] bg-red-500/10 px-2.5 py-2 text-[12px] text-red-300">
                        {message.text}
                      </div>
                    )}

                    <div className="block space-y-1.5">
                      <span className="block text-[12px] text-[#A0A0A0]">字体</span>
                      <EcomSelect
                        aria-label="字体"
                        value={textStyle.fontFamily}
                        labelRender={() => <span className="block truncate">{selectedFontLabel}</span>}
                        showSearch
                        filterOption={(input, option) => {
                          const query = input.trim().toLowerCase();
                          if (!query) return true;
                          return `${option?.label ?? ""} ${option?.family ?? ""}`
                            .toLowerCase()
                            .includes(query);
                        }}
                        options={sortFontsForPicker(platformFonts).map((font) => ({ label: font.display_name, value: font.css_family, family: font.family }))}
                        optionRender={(option) => {
                          const font = platformFonts.find((candidate) => candidate.css_family === option.value);
                          if (!font?.personalFontUuids?.length) return <span>{font?.display_name ?? option.label}</span>;
                          return (
                            <div className="group flex min-w-0 items-center gap-2">
                              <span className="min-w-0 flex-1 truncate">{font.display_name}</span>
                              <button
                                type="button"
                                aria-label={`删除个人字体 ${font.display_name}`}
                                onMouseDown={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                }}
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  setPendingPersonalFontDelete(font);
                                }}
                                className="invisible flex h-5 w-5 shrink-0 items-center justify-center rounded text-[#8C8C8C] transition hover:bg-red-500/15 hover:text-red-300 focus-visible:visible focus-visible:outline-none group-hover:visible group-focus-within:visible"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          );
                        }}
                        onChange={(value) => {
                          const nextFont = platformFonts.find((font) => font.css_family === value);
                          const nextWeights = nextFont?.weights.length ? nextFont.weights : [400];
                          const nextWeight = nextWeights.includes(textStyle.fontWeight)
                            ? textStyle.fontWeight
                            : nextWeights.reduce((nearest, weight) => (
                                Math.abs(weight - textStyle.fontWeight) < Math.abs(nearest - textStyle.fontWeight)
                                  ? weight
                                  : nearest
                              ), nextWeights[0]);
                          updateTextStyle({ fontFamily: value, fontWeight: nextWeight });
                        }}
                        className="w-full"
                        overlayLayer="modalDropdown"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="min-w-0 space-y-1.5">
                        <span className="block text-[12px] text-[#A0A0A0]">字体粗细</span>
                        <EcomSelect
                          aria-label="字体粗细"
                          value={textStyle.fontWeight}
                          options={fontWeightOptions.map((weight) => ({
                            label: activePlatformFont?.weightLabels?.[weight]
                              ?? FONT_WEIGHT_LABELS[weight]
                              ?? `Weight ${weight}`,
                            value: weight,
                          }))}
                          onChange={(value) => updateTextStyle({ fontWeight: value })}
                          className="w-full"
                          overlayLayer="modalDropdown"
                        />
                      </div>
                      <div className="min-w-0 space-y-1.5">
                        <span className="block text-[12px] text-[#A0A0A0]">字体大小</span>
                        <EcomInputNumber
                          aria-label="字体大小"
                          value={textStyle.fontSize}
                          min={1}
                          max={400}
                          onChange={(value) => typeof value === "number" && updateTextStyle({ fontSize: value })}
                          className="w-full"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="min-w-0 space-y-1.5">
                        <span className="block text-[12px] text-[#A0A0A0]">行高</span>
                        <EcomInputNumber
                          aria-label="行高"
                          value={textStyle.lineHeight}
                          min={0}
                          max={600}
                          formatter={(value) => Number(value) === 0 ? "自动" : String(value ?? "")}
                          parser={(value) => value === "自动" ? 0 : Number(value || 0)}
                          onChange={(value) => typeof value === "number" && updateTextStyle({ lineHeight: value })}
                          className="w-full"
                        />
                      </div>
                      <div className="min-w-0 space-y-1.5">
                        <span className="block text-[12px] text-[#A0A0A0]">字距</span>
                        <EcomInputNumber
                          aria-label="字距"
                          value={textStyle.letterSpacing}
                          min={-100}
                          max={500}
                          formatter={(value) => `${value ?? 0}%`}
                          parser={(value) => Number(value?.replace("%", "") || 0)}
                          onChange={(value) => typeof value === "number" && updateTextStyle({ letterSpacing: value })}
                          className="w-full"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <span className="block text-[12px] text-[#A0A0A0]">文本修饰</span>
                      <div className="ecom-text-style-toggle-group">
                        {([
                          ["bold", "B", boldFontWeight !== regularFontWeight && textStyle.fontWeight === boldFontWeight],
                          ["italic", "I", textStyle.italic],
                          ["underline", "U", textStyle.underline],
                          ["strike", "S", textStyle.strikeThrough],
                        ] as const).map(([key, label, active]) => (
                          <EcomTextStyleToggle
                            key={key}
                            decoration={key}
                            pressed={active}
                            aria-label={{ bold: "粗体", italic: "斜体", underline: "下划线", strike: "删除线" }[key]}
                            onClick={() => {
                              if (key === "bold") {
                                updateTextStyle({ fontWeight: active ? regularFontWeight : boldFontWeight });
                              }
                              if (key === "italic") updateTextStyle({ italic: !active });
                              if (key === "underline") updateTextStyle({ underline: !active });
                              if (key === "strike") updateTextStyle({ strikeThrough: !active });
                            }}
                          >
                            {label}
                          </EcomTextStyleToggle>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <span className="block text-[12px] text-[#A0A0A0]">对齐</span>
                      <EcomSegmented<TextLayerAlign>
                        variant="fontPanel"
                        aria-label="水平对齐"
                        block
                        value={textStyle.align}
                        onChange={(value) => updateTextStyle({ align: value })}
                        options={[
                          { value: "left", label: <AlignLeft size={15} aria-label="左对齐" /> },
                          { value: "center", label: <AlignCenter size={15} aria-label="居中对齐" /> },
                          { value: "right", label: <AlignRight size={15} aria-label="右对齐" /> },
                        ]}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <span className="block text-[12px] text-[#A0A0A0]">填充颜色</span>
                      <EcomColorInput
                        colorLabel="填充颜色"
                        opacityLabel="不透明度"
                        value={textStyle.color}
                        opacity={Math.round(textStyle.opacity * 100)}
                        eyedropper
                        eyedropperLabel="从左侧图片吸取填充颜色"
                        eyedropperActive={eyeDropperActive}
                        onEyedropperChange={(active) => {
                          setEyeDropperActive(active);
                          setEyeDropperPreview(null);
                          setFontCursorPosition(null);
                        }}
                        onChange={(value) => updateTextStyle({ color: value })}
                        onOpacityChange={(value) => updateTextStyle({ opacity: value / 100 })}
                      />
                    </div>
                  </div>
                </div>
              </ConfigProvider>
            )}

            {/* 贴纸 tab */}
            {rightPanelTab === "sticker" && (
              <div className="flex min-h-0 flex-1 select-none flex-col px-[20px] py-3">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-[13px] font-medium text-[#F6F6F6]">我的贴纸</span>
                  <button
                    type="button"
                    onClick={() => stickerFileInputRef.current?.click()}
                    disabled={stickerUploading || stickerListLoading}
                    className="flex h-[26px] min-w-[82px] items-center justify-center gap-1.5 rounded-[6px] bg-[#2C2C2C] px-3 text-[13px] font-normal text-[#E6E6E6] transition hover:bg-[#383838] disabled:cursor-not-allowed disabled:opacity-55"
                    aria-label={stickerUploading ? "正在上传贴纸" : "上传贴纸"}
                  >
                    {stickerUploading && <Loader2 size={12} className="animate-spin" />}
                    {stickerUploading ? "上传中" : "上传贴纸"}
                  </button>
                  <input
                    ref={stickerFileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    className="hidden"
                    onChange={handleStickerUpload}
                  />
                </div>

                {(stickerUploading || stickerError) && (
                  <div
                    className={`mb-3 rounded-[6px] px-2.5 py-2 text-[12px] ${
                      stickerError ? "bg-red-500/10 text-red-300" : "bg-white/5 text-[#BDBDBD]"
                    }`}
                    role={stickerError ? "status" : undefined}
                  >
                    {stickerError ?? `正在上传 ${stickerUploadProgress.completed}/${stickerUploadProgress.total}`}
                  </div>
                )}

                <div className="flex min-h-0 flex-1 flex-col overflow-y-auto pb-3">
                  {stickerListLoading ? (
                    <div className="flex flex-1 items-center justify-center gap-2 text-[13px] text-[#A0A0A0]" role="status">
                      <Loader2 size={14} className="animate-spin" />
                      正在加载贴纸
                    </div>
                  ) : stickers.length === 0 ? (
                    <div className="flex flex-1 items-center justify-center text-[13px] text-[#A0A0A0]">
                      暂无贴纸
                    </div>
                  ) : (
                    <div className="columns-2 gap-3" data-sticker-masonry="true">
                      {stickers.map((sticker) => {
                        const aspectRatio = sticker.width && sticker.height
                          ? `${sticker.width} / ${sticker.height}`
                          : undefined;
                        return (
                          <div
                            key={sticker.uuid}
                            className="group relative mb-3 w-full break-inside-avoid overflow-hidden rounded-[4px] bg-[#151515]"
                          >
                            <button
                              type="button"
                              onClick={() => handleStickerItemClick(sticker)}
                              draggable
                              onDragStart={(event) => handleStickerDragStart(event, sticker)}
                              className="relative block w-full cursor-grab overflow-hidden rounded-[4px] outline-none transition active:cursor-grabbing"
                              style={{ aspectRatio }}
                              aria-label={`添加贴纸 ${sticker.file_name}`}
                              title="点击添加到图片中心，或拖拽到指定位置"
                            >
                              <span
                                className="pointer-events-none absolute inset-0 opacity-0 transition-opacity group-hover:opacity-100"
                                aria-hidden="true"
                                style={{
                                  backgroundColor: "#E8E8E8",
                                  backgroundImage: "linear-gradient(45deg,#C8C8C8 25%,transparent 25%),linear-gradient(-45deg,#C8C8C8 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#C8C8C8 75%),linear-gradient(-45deg,transparent 75%,#C8C8C8 75%)",
                                  backgroundPosition: "0 0,0 6px,6px -6px,-6px 0",
                                  backgroundSize: "12px 12px",
                                }}
                              />
                              {/* eslint-disable-next-line @next/next/no-img-element */}
                              <img
                                src={sticker.file_url}
                                alt={sticker.file_name || "贴纸"}
                                className={`relative z-[1] block w-full ${aspectRatio ? "h-full object-contain" : "h-auto"}`}
                              />
                            </button>
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                setPendingStickerDelete(sticker);
                              }}
                              disabled={deletingStickerId !== null}
                              className="absolute right-1.5 top-1.5 z-[2] flex h-4 w-4 items-center justify-center rounded-full bg-black/70 text-white opacity-0 shadow-sm transition hover:bg-black focus:opacity-100 group-hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-40"
                              aria-label={`删除贴纸 ${sticker.file_name}`}
                            >
                              <X size={10} strokeWidth={1.8} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 描边/圆角 tab */}
            {rightPanelTab === "stroke" && (
              <ConfigProvider
                theme={{
                  algorithm: theme.darkAlgorithm,
                  token: {
                    colorBgContainer: "#1F1F21",
                    colorBgElevated: "#2B2B2D",
                    colorBorder: "#444446",
                    colorPrimary: "#FFFFFF",
                    borderRadius: 6,
                    controlHeight: 32,
                    fontSize: 13,
                  },
                  components: {
                    Slider: { railBg: "#2D2D2F", railHoverBg: "#353537" },
                  },
                }}
              >
                <div data-image-edit-stroke-panel="antd-dark" className="flex min-h-0 flex-1 select-none flex-col overflow-y-auto px-5 py-3">
                  <div className="space-y-6 pb-3">
                    <div className="flex items-baseline gap-1">
                      <span className="text-[13px] font-medium text-[#F6F6F6]">描边/圆角</span>
                      <span className="text-[13px] text-[#C7C7C7]">（作用于全局）</span>
                    </div>

                    {([
                      ["上下描边", "topBottom", strokeTopBottom, setStrokeTopBottom],
                      ["左右描边", "leftRight", strokeLeftRight, setStrokeLeftRight],
                      ["圆角半径", "cornerRadius", cornerRadius, setCornerRadius],
                    ] as const).map(([label, key, value, setValue]) => (
                      <div key={label} className="grid min-h-8 grid-cols-[64px_minmax(0,1fr)_90px] items-center gap-3">
                        <span className="text-[12px] text-[#A0A0A0]">{label}</span>
                        <Slider
                          ariaLabelForHandle={label}
                          min={0}
                          max={100}
                          value={value}
                          tooltip={{ open: false }}
                          onChange={(nextValue) => updateStrokeFrameValue(key, setValue, nextValue)}
                          className="m-0 min-w-0"
                        />
                        <EcomInputNumber
                          aria-label={`${label}数值`}
                          min={0}
                          max={100}
                          value={value}
                          onChange={(nextValue) => {
                            updateStrokeFrameValue(
                              key,
                              setValue,
                              nextValue === null ? null : Number(nextValue),
                            );
                          }}
                          className="w-[90px]"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </ConfigProvider>
            )}
          </aside>
          </div>
        </div>
      </div>

      {pendingStickerDelete && (
        <div
          className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/35 px-4 backdrop-blur-[4px]"
          onPointerDown={(event) => {
            if (event.target === event.currentTarget && !deletingStickerId) {
              setPendingStickerDelete(null);
            }
          }}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="image-edit-sticker-delete-title"
            aria-describedby="image-edit-sticker-delete-description"
            className="w-[min(400px,calc(100vw-32px))] overflow-hidden rounded-[8px] border border-white/10 bg-[#202020] shadow-[0_20px_70px_rgba(0,0,0,0.6)]"
          >
            <div className="flex h-14 items-center justify-between border-b border-white/10 px-5">
              <h3 id="image-edit-sticker-delete-title" className="text-[14px] font-medium text-[#F6F6F6]">
                删除贴纸
              </h3>
              <button
                type="button"
                onClick={() => setPendingStickerDelete(null)}
                disabled={deletingStickerId !== null}
                className="flex h-8 w-8 items-center justify-center rounded-[6px] text-[#9B9B9B] transition hover:bg-white/8 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="关闭删除贴纸确认"
              >
                <X size={16} />
              </button>
            </div>
            <div className="px-5 pb-5 pt-4">
              <p id="image-edit-sticker-delete-description" className="text-[14px] leading-6 text-[#E6E6E6]">
                是否删除贴纸「{pendingStickerDelete.file_name}」？删除后无法恢复。
              </p>
              {stickerError?.startsWith("E9003") && (
                <p className="mt-2 text-[12px] text-red-300" role="status">{stickerError}</p>
              )}
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setPendingStickerDelete(null)}
                  disabled={deletingStickerId !== null}
                  className="h-9 min-w-[72px] rounded-[6px] border border-[#444] px-4 text-[13px] text-[#BDBDBD] transition hover:border-[#5A5A5A] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={handleConfirmStickerDelete}
                  disabled={deletingStickerId !== null}
                  className="flex h-9 min-w-[96px] items-center justify-center gap-2 rounded-[6px] bg-[#F6F6F6] px-4 text-[13px] font-medium text-[#202020] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {deletingStickerId && <Loader2 size={13} className="animate-spin" />}
                  {deletingStickerId ? "删除中" : "确认删除"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {pendingPersonalFontDelete && (
        <div
          className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/35 px-4 backdrop-blur-[4px]"
          onPointerDown={(event) => {
            if (event.target === event.currentTarget && !deletingPersonalFontFamily) {
              setPendingPersonalFontDelete(null);
            }
          }}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="image-edit-personal-font-delete-title"
            aria-describedby="image-edit-personal-font-delete-description"
            className="w-[min(400px,calc(100vw-32px))] overflow-hidden rounded-[8px] border border-white/10 bg-[#202020] shadow-[0_20px_70px_rgba(0,0,0,0.6)]"
          >
            <div className="flex h-14 items-center justify-between border-b border-white/10 px-5">
              <h3 id="image-edit-personal-font-delete-title" className="text-[14px] font-medium text-[#F6F6F6]">
                删除个人字体
              </h3>
              <button
                type="button"
                onClick={() => setPendingPersonalFontDelete(null)}
                disabled={deletingPersonalFontFamily !== null}
                className="flex h-8 w-8 items-center justify-center rounded-[6px] text-[#9B9B9B] transition hover:bg-white/8 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="关闭删除个人字体确认"
              >
                <X size={16} />
              </button>
            </div>
            <div className="px-5 pb-5 pt-4">
              <p id="image-edit-personal-font-delete-description" className="text-[14px] leading-6 text-[#E6E6E6]">
                是否删除个人字体「{pendingPersonalFontDelete.display_name}」及其 {pendingPersonalFontDelete.personalFontUuids?.length ?? 0} 个字体文件？删除后无法恢复。
              </p>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  ref={personalFontDeleteCancelRef}
                  onClick={() => setPendingPersonalFontDelete(null)}
                  disabled={deletingPersonalFontFamily !== null}
                  className="h-9 min-w-[72px] rounded-[6px] border border-[#444] px-4 text-[13px] text-[#BDBDBD] transition hover:border-[#5A5A5A] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={handleConfirmPersonalFontDelete}
                  disabled={deletingPersonalFontFamily !== null}
                  className="flex h-9 min-w-[96px] items-center justify-center gap-2 rounded-[6px] bg-[#F6F6F6] px-4 text-[13px] font-medium text-[#202020] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {deletingPersonalFontFamily && <Loader2 size={13} className="animate-spin" />}
                  {deletingPersonalFontFamily ? "删除中" : "确认删除"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <EcomPortalRoot id="flowx-ecom-image-edit-portal-root" portalRef={editorPortal.portalRootRef} />
    </div>
    </EcomPortalProvider>,
    editorPortalContainer,
  );
}
